import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../api/api';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Upload, Download, Plus, Search, Filter, 
  DollarSign, TrendingUp, TrendingDown, Calendar,
  Building2, Receipt, CreditCard, X, Check, AlertCircle,
  ChevronDown, ChevronRight, Eye, Trash2, Edit, Link2, Unlink,
  FolderSync, Cloud, RefreshCw, User, Clock, CheckCircle2,
  Folder, FolderOpen, ExternalLink, Play, Settings, Database, Calculator,
  FileWarning, Send, Printer, Repeat, Sparkles, AlertTriangle,
  History, Zap, FolderInput, Link, CheckCircle, List, Target, RotateCcw,
  Mail, Users, Banknote
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';

// Componentes extraídos del módulo Finanzas
import PrestamosTab from '../components/finanzas/PrestamosTab';
import FiscalTab from '../components/finanzas/FiscalTab';
import A3docTab from '../components/finanzas/A3docTab';
import NominasTab from '../components/finanzas/NominasTab';
import ConexionBancaria from '../components/finanzas/ConexionBancaria';
import RegistroAutomatizacion from '../components/finanzas/RegistroAutomatizacion';
import MigracionesPanel from '../components/MigracionesPanel';
import { limpiarConcepto } from '../components/finanzas/finanzasUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Extraer solo el número de factura (sin prefijo DC2025)
const extraerNumeroFactura = (numeroCompleto) => {
  if (!numeroCompleto) return '';
  const str = String(numeroCompleto);
  // Si tiene formato DC2025XXXX, extraer solo el número
  const match = str.match(/DC\d{4}(\d+)$/);
  if (match) return match[1];
  // Si es solo número, devolverlo tal cual
  return str;
};

// Función para formatear fecha a DD/MM/AAAA
const formatFecha = (fecha) => {
  if (!fecha) return '';
  // Si ya está en formato DD/MM/AAAA, devolverlo
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) return fecha;
  // Si está en formato YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(fecha)) {
    const [año, mes, dia] = fecha.split('-');
    return `${dia}/${mes}/${año}`;
  }
  return fecha;
};

// ISO (con hora, UTC) -> "DD/MM/AAAA HH:MM" en hora local. Para tooltips de a3doc.
const formatFechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Función helper para fetch seguro que evita el error "clone" causado por rrweb-recorder
// Usa XMLHttpRequest para evitar la interceptación del recorder
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const method = options.method || 'GET';
    
    xhr.open(method, url, true);
    
    // Configurar headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    
    xhr.timeout = 120000; // 120 segundos para facturas largas
    
    xhr.onload = function() {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        console.error('Error parsing JSON:', xhr.responseText?.substring(0, 200));
        data = { error: true, detail: xhr.responseText || 'Respuesta inválida' };
      }
      resolve({ 
        ok: xhr.status >= 200 && xhr.status < 300, 
        status: xhr.status, 
        data 
      });
    };
    
    xhr.onerror = function() {
      console.error('XHR error for:', url);
      resolve({ ok: false, status: 0, data: { error: true, detail: 'Error de conexión' } });
    };
    
    xhr.ontimeout = function() {
      resolve({ ok: false, status: 0, data: { error: true, detail: 'Tiempo de espera agotado' } });
    };
    
    // Enviar
    if (options.body) {
      xhr.send(options.body);
    } else {
      xhr.send();
    }
  });
};

// Versión simplificada que devuelve directamente los datos (para usar con Promise.all)
const fetchData = async (url, token) => {
  try {
    const { ok, data } = await safeFetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (ok) return data;
    console.error('Error fetching:', url, data);
    return Array.isArray(data) ? [] : {};
  } catch (e) {
    console.error('Exception fetching:', url, e);
    return {};
  }
};


// Categorías de gastos (personalizadas) - ordenadas alfabéticamente
const CATEGORIAS_GASTO_DEFAULT = [
  { value: 'alimentacion', label: 'Alimentación', color: 'bg-orange-100 text-orange-800' },
  { value: 'alquileres', label: 'Alquileres', color: 'bg-fuchsia-100 text-fuchsia-800' },
  { value: 'arrendamiento', label: 'Arrendamiento', color: 'bg-violet-100 text-violet-800' },
  { value: 'combustible', label: 'Combustible', color: 'bg-amber-100 text-amber-800' },
  { value: 'comisiones_bancarias', label: 'Comisiones bancarias', color: 'bg-orange-100 text-orange-800' },
  { value: 'compras', label: 'Compras', color: 'bg-blue-100 text-blue-800' },
  { value: 'consumibles', label: 'Consumibles', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'devolucion', label: 'Devolución', color: 'bg-green-100 text-green-800' },
  { value: 'dietas', label: 'Dietas', color: 'bg-lime-100 text-lime-800' },
  { value: 'extras', label: 'Extras', color: 'bg-gray-100 text-gray-800' },
  { value: 'facturas', label: 'Facturas', color: 'bg-slate-100 text-slate-800' },
  { value: 'fianzas', label: 'Fianzas', color: 'bg-stone-100 text-stone-800' },
  { value: 'furgonetas', label: 'Furgonetas', color: 'bg-blue-100 text-blue-800' },
  { value: 'gasolina', label: 'Gasolina', color: 'bg-amber-100 text-amber-800' },
  { value: 'hacienda', label: 'Hacienda', color: 'bg-orange-100 text-orange-800' },
  { value: 'impuestos', label: 'Impuestos', color: 'bg-rose-100 text-rose-800' },
  { value: 'limpieza', label: 'Limpieza', color: 'bg-teal-100 text-teal-800' },
  { value: 'mantenimiento', label: 'Mantenimiento', color: 'bg-amber-100 text-amber-800' },
  { value: 'material', label: 'Material', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'multas', label: 'Multas', color: 'bg-red-100 text-red-800' },
  { value: 'nominas', label: 'Nóminas', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'otros', label: 'Otros', color: 'bg-gray-100 text-gray-800' },
  { value: 'parking', label: 'Parking', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'prestamos', label: 'Préstamos', color: 'bg-purple-100 text-purple-800' },
  { value: 'publicidad', label: 'Publicidad', color: 'bg-pink-100 text-pink-800' },
  { value: 'salarios', label: 'Salarios', color: 'bg-green-100 text-green-800' },
  { value: 'sanciones', label: 'Sanciones', color: 'bg-red-100 text-red-800' },
  { value: 'seguridad_social', label: 'Seguridad Social', color: 'bg-teal-100 text-teal-800' },
  { value: 'seguros', label: 'Seguros', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'servicios', label: 'Servicios', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'sin_justificar', label: 'Sin justificar', color: 'bg-red-100 text-red-800' },
  { value: 'suministros', label: 'Suministros', color: 'bg-sky-100 text-sky-800' },
  { value: 'transporte', label: 'Transporte', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'web_plataforma', label: 'Web y plataforma', color: 'bg-blue-100 text-blue-800' },
];

const Finanzas = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('rentabilidad');
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});  const [añoSeleccionado, setAñoSeleccionado] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  
  // Categorías personalizadas (se pueden añadir nuevas)
  const [CATEGORIAS_GASTO, setCategoriasGasto] = useState(CATEGORIAS_GASTO_DEFAULT);
  const [nuevaCategoria, setNuevaCategoria] = useState('');

  // Abre el compositor de Emails con una plantilla para pedir la factura de un cargo.
  const pedirFacturaCargo = (m) => {
    const importeAbs = Math.abs(m.importe || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
    const asunto = `Solicitud de factura - cargo de ${importeAbs} del ${m.fecha}`;
    const cuerpo = [
      'Hola,',
      '',
      `Hemos recibido un cargo en nuestra cuenta de ${importeAbs} con fecha ${m.fecha}, con el concepto "${(m.concepto || '').trim()}".`,
      '',
      'No nos consta la factura correspondiente. ¿Podríais enviárnosla a este correo?',
      '',
      'Gracias,',
      'Duetto Catering',
    ].join('\n');
    navigate(`/emails?redactar=1&asunto=${encodeURIComponent(asunto)}&cuerpo=${encodeURIComponent(cuerpo)}`);
  };

  // Filtros de búsqueda
  const [filtros, setFiltros] = useState({
    categoria: '',
    proveedor: '',
    importeMin: '',
    importeMax: '',
    concepto: '',
    fechaDesde: '',
    fechaHasta: '',
    estado: '', // '', 'pendiente', 'vinculado'
    a3doc: '' // '', 'subido', 'sin_subir'
  });
  
  // Datos
  const [facturasEmitidas, setFacturasEmitidas] = useState([]);
  const [facturasEmitidasSeleccionadas, setFacturasEmitidasSeleccionadas] = useState(new Set());
  const [descargandoZip, setDescargandoZip] = useState(false);
  const [subiendoA3docId, setSubiendoA3docId] = useState(null);     // factura emitida subiéndose (individual)
  const [subiendoA3docLote, setSubiendoA3docLote] = useState(false); // subida en lote en curso
  const [a3docLoteProgreso, setA3docLoteProgreso] = useState('');    // texto de progreso del lote
  const [mostrarSelectorFacturas, setMostrarSelectorFacturas] = useState(false);
  const [facturasSoportadas, setFacturasSoportadas] = useState([]);
  const [cargosBancarios, setCargosBancarios] = useState([]);
  const [cargosSelA3doc, setCargosSelA3doc] = useState(new Set()); // selección para subir a a3doc en lote
  const [expandedCargos, setExpandedCargos] = useState(() => new Set()); // cargos de tarjeta con acordeón desplegado
  const [cargosTotalCount, setCargosTotalCount] = useState(0);
  const [cargosVinculadosCount, setCargosVinculadosCount] = useState(0);
  const [cargosPendientesCount, setCargosPendientesCount] = useState(0);
  const [comprasTarjetaSinFactura, setComprasTarjetaSinFactura] = useState(0);
  const [cargosLimit, setCargosLimit] = useState(25);
  const [cargosPage, setCargosPage] = useState(0);
  // Paginación facturas emitidas
  const [facturasLimit, setFacturasLimit] = useState(25);
  const [facturasPage, setFacturasPage] = useState(0);
  const [resumenGlobal, setResumenGlobal] = useState(null);
  const [trimestreSeleccionado, setTrimestreSeleccionado] = useState(() => {
    // Calcular trimestre por defecto: trimestre actual hasta el día 20 del mes siguiente
    const hoy = new Date();
    const mes = hoy.getMonth() + 1; // 1-12
    const dia = hoy.getDate();
    // Q1=Ene-Mar, Q2=Abr-Jun, Q3=Jul-Sep, Q4=Oct-Dic
    // Hasta el día 20 del mes siguiente al cierre del trimestre se muestra ese trimestre
    // Ej: si hoy es 16 Abril → aún en periodo del 1T (Q1 se muestra hasta 20 Abril)
    // Ej: si hoy es 21 Abril → ya es 2T
    if (mes <= 1 || (mes === 1 && dia <= 20)) return 4; // Ene 1-20 → aún Q4 año anterior (special case)
    if (mes <= 3 || (mes === 4 && dia <= 20)) return 1;
    if (mes <= 6 || (mes === 7 && dia <= 20)) return 2;
    if (mes <= 9 || (mes === 10 && dia <= 20)) return 3;
    return 4;
  });
  const [resumenMensualEmitidas, setResumenMensualEmitidas] = useState([]);
  const [resumenTrimestralEmitidas, setResumenTrimestralEmitidas] = useState([]);
  const [resumenMensualSoportadas, setResumenMensualSoportadas] = useState([]);
  const [resumenTrimestralSoportadas, setResumenTrimestralSoportadas] = useState([]);
  const [rentabilidadData, setRentabilidadData] = useState(null);
  const [gastosPorCategoria, setGastosPorCategoria] = useState([]);

  // Resumen Dashboard (tab Resumen)
  const [resumenDashboard, setResumenDashboard] = useState(null);
  const [cargandoResumen, setCargandoResumen] = useState(false);
  
  // Resumen Fiscal
  const [resumenFiscal, setResumenFiscal] = useState(null);
  const [cargandoFiscal, setCargandoFiscal] = useState(false);
  
  // Pendientes de facturar
  const [pendientesFacturar, setPendientesFacturar] = useState([]);
  const [cargandoPendientes, setCargandoPendientes] = useState(false);
  const [diasMaxPendientes, setDiasMaxPendientes] = useState(365);
  
  // Modales
  const [showSubirFactura, setShowSubirFactura] = useState(false);
  const [showNuevaFactura, setShowNuevaFactura] = useState(false);
  const [importandoCargos, setImportandoCargos] = useState(false);
  const [showImportarCargos, setShowImportarCargos] = useState(false);
  const [ultimaImportacion, setUltimaImportacion] = useState(null);
  const [showConciliacion, setShowConciliacion] = useState(false);
  const [showVincularFactura, setShowVincularFactura] = useState(false);
  const [showEditarCategoria, setShowEditarCategoria] = useState(false);
  const [showEditarFacturaVinculada, setShowEditarFacturaVinculada] = useState(false);
  // Incidencias / observaciones de facturas soportadas
  const [guardandoIncidencia, setGuardandoIncidencia] = useState(false);
  const [incidenciasAbiertas, setIncidenciasAbiertas] = useState(0);
  const [facturaIncidenciaId, setFacturaIncidenciaId] = useState(null);
  const [showEmailProveedor, setShowEmailProveedor] = useState(false);
  const [emailProveedorData, setEmailProveedorData] = useState({ destinatario: '', asunto: '', mensaje: '', adjuntar_pdf: true });
  const [enviandoEmailProv, setEnviandoEmailProv] = useState(false);
  const [showAvisarEquipo, setShowAvisarEquipo] = useState(false);
  const [empleadosLista, setEmpleadosLista] = useState([]);
  const [avisoEquipoData, setAvisoEquipoData] = useState({ empleado_id: '', mensaje: '' });
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const [showIncidenciasPanel, setShowIncidenciasPanel] = useState(false);
  const [incidenciasLista, setIncidenciasLista] = useState([]);
  const [incidenciasEstado, setIncidenciasEstado] = useState('');
  const [incidenciasCargando, setIncidenciasCargando] = useState(false);
  const [subiendoA3docIncId, setSubiendoA3docIncId] = useState(null);
  // Pestaña Banco: extracto + saldo
  const [movimientosBanco, setMovimientosBanco] = useState([]);
  const [saldoBanco, setSaldoBanco] = useState(null);
  const [cargandoMovimientos, setCargandoMovimientos] = useState(false);
  const [busquedaMovimiento, setBusquedaMovimiento] = useState('');
  const [reintentandoA3doc, setReintentandoA3doc] = useState(false);
  const [showMarcarGastoDirecto, setShowMarcarGastoDirecto] = useState(false);
  const [categoriaGastoDirecto, setCategoriaGastoDirecto] = useState('');
  const [descripcionGastoDirecto, setDescripcionGastoDirecto] = useState('');
  // Vinculación a operación de financiación desde el modal de Gasto Directo
  const [modoGastoDirecto, setModoGastoDirecto] = useState('gasto'); // 'gasto' | 'financiacion'
  const [operacionesFinanciacion, setOperacionesFinanciacion] = useState([]);
  const [operacionFinanciacionId, setOperacionFinanciacionId] = useState('');
  const [tipoMovimientoFinanciacion, setTipoMovimientoFinanciacion] = useState('cuota_capital');

  // Cargo seleccionado para editar/vincular
  const [cargoSeleccionado, setCargoSeleccionado] = useState(null);

  // Desglose de tarjeta consolidada
  // { cargo, subcargos, suma, diferencia, cuadrado, guardando, preview }
  const [desgloseTarjeta, setDesgloseTarjeta] = useState(null);
  // Selector inline de facturas de Drive para un subcargo. { subId, cargando, lista }
  const [facturasSugeridas, setFacturasSugeridas] = useState(null);
  // Fila "Añadir compra" del desglose
  const [nuevoSubcargo, setNuevoSubcargo] = useState({
    fecha_compra: '', concepto: '', proveedor: '', categoria: '', importe: '', notas: ''
  });
  // Ref al File del extracto pendiente de confirmar importación
  const extractoFileRef = React.useRef(null);
  const extractoInputRef = React.useRef(null);


  // Estados de extracción IA
  const [extracting, setExtracting] = useState(false);
  const [datosExtraidos, setDatosExtraidos] = useState(null);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  
  // Form factura soportada
  const [formFactura, setFormFactura] = useState({
    proveedor_nombre: '',
    proveedor_cif: '',
    numero_factura: '',
    fecha_factura: '',
    categoria: 'otros',
    base_imponible: 0,
    iva_4: 0,
    iva_10: 0,
    iva_21: 0,
    total_iva: 0,
    total: 0,
    lineas: [],
    notas: ''
  });

  // Estados para automatización Google Drive
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveArchivos, setDriveArchivos] = useState([]);
  const [driveSubcarpetas, setDriveSubcarpetas] = useState([]);
  const [driveCarpetaActual, setDriveCarpetaActual] = useState(null); // ID de carpeta actual
  const [driveRutaCarpetas, setDriveRutaCarpetas] = useState([]); // [{id, name}, ...] para breadcrumb
  const [cargandoDrive, setCargandoDrive] = useState(false);
  const [verificandoDrive, setVerificandoDrive] = useState(false);
  const [driveConectado, setDriveConectado] = useState(false);
  const [facturasProcessadas, setFacturasProcessadas] = useState([]);
  const [tarjetasDisponibles, setTarjetasDisponibles] = useState([]);
  const [empleadosTarjetas, setEmpleadosTarjetas] = useState([]);
  const [procesandoDrive, setProcesandoDrive] = useState(false);
  const [showConfigTarjetas, setShowConfigTarjetas] = useState(false);
  
  // Modal de Mover Archivo
  const [showMoverArchivo, setShowMoverArchivo] = useState(false);
  const [archivoParaMover, setArchivoParaMover] = useState(null);
  const [carpetasDisponibles, setCarpetasDisponibles] = useState([]);
  const [carpetaDestino, setCarpetaDestino] = useState('');
  const [moviendoArchivo, setMoviendoArchivo] = useState(false);
  
  // Modal de procesamiento de factura
  const [showProcesarFactura, setShowProcesarFactura] = useState(false);
  const [archivoEnProceso, setArchivoEnProceso] = useState(null);
  const [datosFacturaIA, setDatosFacturaIA] = useState(null);
  const [cargosMatch, setCargosMatch] = useState([]);
  const [subcargosMatch, setSubcargosMatch] = useState([]);  // Compras de tarjeta candidatas (subcargos)
  const [vinculandoSubcargo, setVinculandoSubcargo] = useState(null);  // subcargo_id en curso
  const [abonosMatch, setAbonosMatch] = useState([]);  // Abonos candidatos para notas de crédito
  const [esNotaCredito, setEsNotaCredito] = useState(false);  // Si la factura es nota de crédito/devolución
  const [procesandoIA, setProcesandoIA] = useState(false);
  const [cargosSeleccionados, setCargosSeleccionados] = useState([]);  // Array de cargos seleccionados
  const [abonoSeleccionadoDevolucion, setAbonoSeleccionadoDevolucion] = useState(null);  // Abono seleccionado para vincular devolución
  const [busquedaManual, setBusquedaManual] = useState('');  // Búsqueda manual de cargos
  const [buscandoCargos, setBuscandoCargos] = useState(false);
  const [mostrarBusquedaManual, setMostrarBusquedaManual] = useState(false);
  const [categoriaSugerida, setCategoriaSugerida] = useState(null);
  const [eliminandoFactura, setEliminandoFactura] = useState(false);
  const [filtroConceptoCargos, setFiltroConceptoCargos] = useState('');  // Filtro por concepto en cargos candidatos
  const [filtroFechaCargos, setFiltroFechaCargos] = useState('');  // Filtro por fecha en cargos candidatos
  const anioActualCargos = String(new Date().getFullYear());
  const [filtroAnioCargos, setFiltroAnioCargos] = useState(anioActualCargos);  // Año en cargos candidatos ('' = todos)
  const [filtroMesCargos, setFiltroMesCargos] = useState('');  // Mes '01'..'12' en cargos candidatos ('' = todos)
  const [filtroImporteCargos, setFiltroImporteCargos] = useState('');  // Importe en cargos candidatos ('' = todos)

  // Filtro común de la lista de cargos candidatos (modal procesar factura)
  const filtrarCargoCandidato = (c) => {
    // Por defecto se ocultan los cargos que ya tienen factura y no les queda
    // importe por cubrir (solo salen con "incluir cargos ya cubiertos")
    if (!incluirCargosVinculados) {
      const tieneFacturas = c.facturas_vinculadas?.length > 0;
      const sinRestante = typeof c.importe_restante !== 'number' || c.importe_restante <= 0.5;
      const vinculadoSinDetalle = !tieneFacturas && (c.factura_id || c.factura_vinculada_id);
      if ((tieneFacturas && sinRestante) || vinculadoSinDetalle) return false;
    }
    const matchConcepto = !filtroConceptoCargos || c.concepto?.toLowerCase().includes(filtroConceptoCargos.toLowerCase());
    const matchFecha = !filtroFechaCargos || c.fecha?.startsWith(filtroFechaCargos);
    const matchAnio = !filtroAnioCargos || c.fecha?.startsWith(filtroAnioCargos);
    const matchMes = !filtroMesCargos || c.fecha?.slice(5, 7) === filtroMesCargos;
    // Importe con tolerancia ±0,50€, contra el importe del cargo o su restante
    const importeBuscado = parseFloat(String(filtroImporteCargos).replace(',', '.'));
    const matchImporte = !filtroImporteCargos || isNaN(importeBuscado)
      || Math.abs(Math.abs(c.importe || 0) - importeBuscado) <= 0.5
      || (typeof c.importe_restante === 'number' && Math.abs(c.importe_restante - importeBuscado) <= 0.5);
    return matchConcepto && matchFecha && matchAnio && matchMes && matchImporte;
  };
  // Años disponibles en los candidatos (siempre incluye el año en curso)
  const aniosCargosCandidatos = [...new Set([anioActualCargos, ...cargosMatch.map(c => (c.fecha || '').slice(0, 4)).filter(a => a.length === 4)])].sort().reverse();
  // Rango de fechas para las búsquedas en servidor según los filtros activos
  const rangoFechasCargos = () => {
    if (filtroFechaCargos) return { desde: filtroFechaCargos, hasta: filtroFechaCargos };
    if (filtroAnioCargos && filtroMesCargos) return { desde: `${filtroAnioCargos}-${filtroMesCargos}-01`, hasta: `${filtroAnioCargos}-${filtroMesCargos}-31` };
    if (filtroAnioCargos) return { desde: `${filtroAnioCargos}-01-01`, hasta: `${filtroAnioCargos}-12-31` };
    return null;
  };
  // Rango de importe (±0,50€) para las búsquedas en servidor
  const rangoImporteCargos = () => {
    const v = parseFloat(String(filtroImporteCargos).replace(',', '.'));
    if (!filtroImporteCargos || isNaN(v)) return null;
    return { min: Math.max(0, v - 0.5), max: v + 0.5 };
  };
  const [datosDesdeCache, setDatosDesdeCache] = useState(false);  // Si los datos vienen de caché
  const [cargoYaVinculado, setCargoYaVinculado] = useState(null);  // Si la factura ya está vinculada
  const [incluirCargosVinculados, setIncluirCargosVinculados] = useState(false);  // Checkbox para incluir vinculados
  const [vinculandoFactura, setVinculandoFactura] = useState(false);  // Estado de loading para vincular
  const [vinculandoDevolucion, setVinculandoDevolucion] = useState(false);  // Estado de loading para vincular devolución
  
  // Estado para modal de exceso de pago
  const [excesoData, setExcesoData] = useState(null);

  // Duplicados de facturas pendientes de revisar (flag oculta_iva)
  const [duplicadosPendientes, setDuplicadosPendientes] = useState([]);
  const [showDuplicadosModal, setShowDuplicadosModal] = useState(false);
  const [resolviendoDuplicado, setResolviendoDuplicado] = useState(null); // id de la factura que se está resolviendo

  // Aviso de duplicado detectado al vincular: {original: {...}} o null
  const [avisoDuplicadoVincular, setAvisoDuplicadoVincular] = useState(null);
  
  // Checkbox para incluir facturas ya pagadas en búsqueda
  const [mostrarPagadas, setMostrarPagadas] = useState(false);
  
  // Estados para Sugerencias de Vinculación
  const [sugerenciasVinculacion, setSugerenciasVinculacion] = useState([]);
  const [cargandoSugerenciasGlobal, setCargandoSugerenciasGlobal] = useState(false);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(true);
  const [vinculandoSugerencia, setVinculandoSugerencia] = useState(null);  // ID del abono que se está vinculando
  
  // Estados para Gastos Fijos
  const [gastosFijos, setGastosFijos] = useState([]);
  const [patronesDetectados, setPatronesDetectados] = useState([]);
  const [cargandoGastosFijos, setCargandoGastosFijos] = useState(false);
  const [showNuevoGastoFijo, setShowNuevoGastoFijo] = useState(false);
  const [nuevoGastoFijo, setNuevoGastoFijo] = useState({ 
    nombre: '', 
    categoria: '', 
    importe_estimado: '', 
    dia_estimado: '', 
    proveedor: '',
    periodicidad: 'mensual'
  });
  const [comparativaMensual, setComparativaMensual] = useState(null);
  const [editandoGastoFijo, setEditandoGastoFijo] = useState(null);
  const [mostrarTodosPatrones, setMostrarTodosPatrones] = useState(false);
  const [gastosPersonal, setGastosPersonal] = useState(null);

  // Estados para Préstamos/Deudas
  const [prestamos, setPrestamos] = useState([]);
  const [resumenPrestamos, setResumenPrestamos] = useState(null);
  const [cargandoPrestamos, setCargandoPrestamos] = useState(false);
  const [showNuevoPrestamo, setShowNuevoPrestamo] = useState(false);
  const [nuevoPrestamo, setNuevoPrestamo] = useState({
    nombre: '', importe_total: '', num_cuotas: '', importe_cuota: '', cuotas_pagadas: '0', entidad: '', notas: '', tipo_interes: '', comision_apertura: ''
  });

  // Estados para Auto-procesamiento y alertas
  const [cargosSinFactura, setCargosSinFactura] = useState(null);
  const [statsAutoProcesamiento, setStatsAutoProcesamiento] = useState(null);
  const [ejecutandoAutoProcesamiento, setEjecutandoAutoProcesamiento] = useState(false);
  
  // Estado para facturas en Drive no vinculadas
  const [facturasNoVinculadas, setFacturasNoVinculadas] = useState(null);
  const [cargandoNoVinculadas, setCargandoNoVinculadas] = useState(false);
  
  // Estados para Abonos/Ingresos
  const [abonos, setAbonos] = useState([]);
  const [abonosTotal, setAbonosTotal] = useState(0);
  const [abonosTotalImporte, setAbonosTotalImporte] = useState(0);
  const [abonosPendientes, setAbonosPendientes] = useState(0);
  const [cargandoAbonos, setCargandoAbonos] = useState(false);
  const [filtroTipoAbono, setFiltroTipoAbono] = useState('');
  const [filtroAbonoVinculado, setFiltroAbonoVinculado] = useState(null);
  const [showVincularAbono, setShowVincularAbono] = useState(false);
  const [abonoSeleccionado, setAbonoSeleccionado] = useState(null);
  const [sugerenciasAbono, setSugerenciasAbono] = useState(null);
  const [cargandoSugerenciasAbono, setCargandoSugerenciasAbono] = useState(false);
  const [tipoVinculacionAbono, setTipoVinculacionAbono] = useState('factura'); // 'factura' | 'devolucion' | 'fianza'
  const [busquedaFacturaSoportada, setBusquedaFacturaSoportada] = useState('');
  const [resultadosBusquedaFactura, setResultadosBusquedaFactura] = useState([]);
  const [buscandoFacturas, setBuscandoFacturas] = useState(false);
  const [busquedaCobro, setBusquedaCobro] = useState('');
  const [resultadosBusquedaCobro, setResultadosBusquedaCobro] = useState({ facturas_emitidas: [], presupuestos: [] });
  const [buscandoCobros, setBuscandoCobros] = useState(false);
  const [itemsSeleccionadosCobro, setItemsSeleccionadosCobro] = useState([]); // Para vinculación múltiple
  const [vinculandoMultiple, setVinculandoMultiple] = useState(false);
  
  // Estados para Modal Vincular V3
  const [showVincularV3, setShowVincularV3] = useState(false);
  const [busquedaV3, setBusquedaV3] = useState('');
  const [resultadosV3, setResultadosV3] = useState([]);
  const [buscandoV3, setBuscandoV3] = useState(false);
  const [documentoV3Seleccionado, setDocumentoV3Seleccionado] = useState(null);
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState([]); // Multi-select: [{id, numero, cliente, pendiente, importe_asignado}]
  const [vinculandoV3, setVinculandoV3] = useState(false);
  const [sugerenciasV3, setSugerenciasV3] = useState([]);
  const [cargandoSugerenciasV3, setCargandoSugerenciasV3] = useState(false);
  
  // Estados para devolución manual
  const [mostrarFormDevolucion, setMostrarFormDevolucion] = useState(false);
  const [datosDevolucion, setDatosDevolucion] = useState({ proveedor: '', notas: '', numero_factura_original: '' });
  const [guardandoDevolucion, setGuardandoDevolucion] = useState(false);
  
  // Estado para historial de auto-procesamiento
  const [historialAutoProcesamiento, setHistorialAutoProcesamiento] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  
  // Estado para panel simplificado 2025+
  const [estadoDrive2025, setEstadoDrive2025] = useState(null);
  const [vinculacionesRecientes, setVinculacionesRecientes] = useState(null);
  const [cargandoEstado2025, setCargandoEstado2025] = useState(false);
  const [vinculando2025, setVinculando2025] = useState(false);

  // Estado para modal de validación de vinculaciones
  const [showValidacionModal, setShowValidacionModal] = useState(false);
  const [vinculacionParaValidar, setVinculacionParaValidar] = useState(null);
  const [validandoVinculacion, setValidandoVinculacion] = useState(false);

  const token = getAuthToken();
  const headers = React.useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // Cargar operaciones de financiación cuando se abre cualquiera de los modales
  // que necesitan el desplegable. Radix Dialog NO dispara onOpenChange cuando
  // el padre cambia el prop `open` directamente (sólo en interacciones internas),
  // así que el fetch tiene que vivir aquí, no dentro del onOpenChange.
  const recargarOperacionesFinanciacion = React.useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/finanzas/operaciones-financiacion?incluir_cerradas=true`,
        { headers }
      );
      const data = await res.json();
      setOperacionesFinanciacion(data.operaciones || []);
    } catch {
      setOperacionesFinanciacion([]);
    }
  }, [headers]);

  React.useEffect(() => {
    if (showMarcarGastoDirecto || showVincularV3) {
      recargarOperacionesFinanciacion();
    }
  }, [showMarcarGastoDirecto, showVincularV3, recargarOperacionesFinanciacion]);

  // Cargar datos solo de Rentabilidad (pestaña por defecto)
  const cargarRentabilidad = useCallback(async () => {
    try {
      const data = await fetchData(`${API_URL}/api/finanzas/rentabilidad-mensual?year=${añoSeleccionado}`, token);
      setRentabilidadData(data || null);
    } catch (error) {
      console.error('Error cargando rentabilidad:', error);
    }
  }, [añoSeleccionado, token]);

  // Cargar KPIs agregados para la tab Resumen
  const cargarResumenDashboard = useCallback(async () => {
    setCargandoResumen(true);
    try {
      const data = await fetchData(`${API_URL}/api/finanzas/resumen-dashboard?anio=${añoSeleccionado}`, token);
      setResumenDashboard(data || null);
    } catch (error) {
      console.error('Error cargando resumen dashboard:', error);
    } finally {
      setCargandoResumen(false);
    }
  }, [añoSeleccionado, token]);

  // Cargar resumen global (depende del trimestre seleccionado)
  const cargarResumenGlobal = useCallback(async (trimestre = null) => {
    try {
      const triParam = trimestre ? `&trimestre=${trimestre}` : '';
      const resumen = await fetchData(`${API_URL}/api/finanzas/resumen-global?anio=${añoSeleccionado}${triParam}`, token);
      setResumenGlobal(resumen);
    } catch (error) {
      console.error('Error cargando resumen global:', error);
    }
  }, [añoSeleccionado, token]);

  // Cargar datos de facturas emitidas y soportadas (solo cuando se necesitan)
  const cargarFacturas = useCallback(async () => {
    setLoading(true);
    try {
      const [
        emitidas,
        soportadas,
        mensualEmitidas,
        trimestralEmitidas,
        mensualSoportadas,
        trimestralSoportadas,
        categorias
      ] = await Promise.all([
        fetchData(`${API_URL}/api/finanzas/facturas-emitidas?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-soportadas?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-emitidas/resumen-mensual?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-emitidas/resumen-trimestral?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-soportadas/resumen-mensual?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-soportadas/resumen-trimestral?anio=${añoSeleccionado}`, token),
        fetchData(`${API_URL}/api/finanzas/facturas-soportadas/por-categoria?anio=${añoSeleccionado}`, token),
      ]);
      
      setFacturasEmitidas(Array.isArray(emitidas) ? emitidas : []);
      setFacturasSoportadas(Array.isArray(soportadas) ? soportadas : []);
      setResumenMensualEmitidas(Array.isArray(mensualEmitidas) ? mensualEmitidas : []);
      setResumenTrimestralEmitidas(Array.isArray(trimestralEmitidas) ? trimestralEmitidas : []);
      setResumenMensualSoportadas(Array.isArray(mensualSoportadas) ? mensualSoportadas : []);
      setResumenTrimestralSoportadas(Array.isArray(trimestralSoportadas) ? trimestralSoportadas : []);
      setGastosPorCategoria(Array.isArray(categorias) ? categorias : []);
    } catch (error) {
      console.error('Error cargando datos financieros:', error);
    } finally {
      setLoading(false);
    }
  }, [añoSeleccionado, token]);

  // Función para cargar cargos con filtros server-side
  const cargarCargosBancarios = useCallback(async () => {
    try {
      // Construir query params con filtros
      const params = new URLSearchParams();
      params.append('anio', añoSeleccionado);
      params.append('tipo', 'cargo');
      params.append('limit', cargosLimit);
      params.append('skip', cargosPage * cargosLimit);
      
      // Añadir filtros si están definidos
      if (filtros.estado) params.append('estado', filtros.estado);
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      if (filtros.proveedor) params.append('proveedor', filtros.proveedor);
      if (filtros.concepto) params.append('concepto', filtros.concepto);
      if (filtros.importeMin) params.append('importe_min', filtros.importeMin);
      if (filtros.importeMax) params.append('importe_max', filtros.importeMax);
      if (filtros.fechaDesde) params.append('fecha_desde', filtros.fechaDesde);
      if (filtros.fechaHasta) params.append('fecha_hasta', filtros.fechaHasta);
      if (filtros.a3doc) params.append('a3doc', filtros.a3doc);

      const { ok, data: cargos } = await safeFetch(`${API_URL}/api/finanzas/cargos-bancarios?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (ok) {
        setCargosBancarios(cargos?.data || []);
        setCargosTotalCount(cargos?.total || 0);
        setCargosVinculadosCount(cargos?.count_vinculados || 0);
        setCargosPendientesCount(cargos?.count_pendientes || 0);
        setComprasTarjetaSinFactura(cargos?.compras_tarjeta_sin_factura || 0);
      }
    } catch (error) {
      console.error('Error cargando cargos bancarios:', error);
    }
  }, [añoSeleccionado, cargosLimit, cargosPage, filtros, token]);

  // ===== Incidencias / observaciones de facturas soportadas =====
  const cargarIncidenciasAbiertas = useCallback(async () => {
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/drive/incidencias/abiertas-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ok) setIncidenciasAbiertas(data?.abiertas || 0);
    } catch (e) { /* silencioso */ }
  }, [token]);

  const guardarIncidencia = async (estado, facturaId, incidenciaActual, onSaved) => {
    if (!facturaId) { alert('Guarda/vincula la factura antes de añadir observaciones'); return; }
    setGuardandoIncidencia(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/drive/facturas/${facturaId}/incidencia`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texto: incidenciaActual?.texto || '', estado })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (onSaved) onSaved(data.incidencia);
        cargarIncidenciasAbiertas();
      } else {
        alert('No se pudo guardar la incidencia: ' + (data.detail || ''));
      }
    } catch (e) {
      alert('Error guardando la incidencia');
    } finally {
      setGuardandoIncidencia(false);
    }
  };

  const abrirEmailProveedor = async (facturaId) => {
    if (!facturaId) { alert('Guarda/vincula la factura antes de escribir al proveedor'); return; }
    setFacturaIncidenciaId(facturaId);
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/drive/facturas/${facturaId}/email-proveedor-defaults`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ok) {
        setEmailProveedorData({ destinatario: data.destinatario || '', asunto: data.asunto || '', mensaje: data.mensaje || '', adjuntar_pdf: true });
        setShowEmailProveedor(true);
      }
    } catch (e) {
      alert('No se pudieron cargar los datos del proveedor');
    }
  };

  const enviarEmailProveedor = async () => {
    if (!facturaIncidenciaId) return;
    if (!emailProveedorData.destinatario.trim()) { alert('Indica el email del proveedor'); return; }
    setEnviandoEmailProv(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/drive/facturas/${facturaIncidenciaId}/email-proveedor`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailProveedorData)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(data.mensaje || 'Email enviado');
        setShowEmailProveedor(false);
      } else {
        alert('No se pudo enviar: ' + (data.detail || ''));
      }
    } catch (e) {
      alert('Error enviando el email');
    } finally {
      setEnviandoEmailProv(false);
    }
  };

  const abrirAvisarEquipo = async (facturaId) => {
    if (!facturaId) { alert('Guarda/vincula la factura antes de avisar al equipo'); return; }
    setFacturaIncidenciaId(facturaId);
    if (empleadosLista.length === 0) {
      try {
        const { ok, data } = await safeFetch(`${API_URL}/api/empleados`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (ok) setEmpleadosLista((data || []).filter(e => e.activo !== false));
      } catch (e) { /* seguimos, el selector saldrá vacío */ }
    }
    setAvisoEquipoData({ empleado_id: '', mensaje: '' });
    setShowAvisarEquipo(true);
  };

  const enviarAvisoEquipo = async () => {
    if (!facturaIncidenciaId) return;
    if (!avisoEquipoData.empleado_id) { alert('Elige a quién avisar'); return; }
    setEnviandoAviso(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/drive/facturas/${facturaIncidenciaId}/notificar-equipo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(avisoEquipoData)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(`Aviso enviado a ${data.empleado}${data.email_enviado ? ' (notificación + email)' : ' (notificación interna)'}`);
        setShowAvisarEquipo(false);
      } else {
        alert('No se pudo avisar: ' + (data.detail || ''));
      }
    } catch (e) {
      alert('Error enviando el aviso');
    } finally {
      setEnviandoAviso(false);
    }
  };

  const abrirIncidenciasPanel = async () => {
    setIncidenciasEstado('');
    setShowIncidenciasPanel(true);
    await cargarIncidenciasPanel('');
  };

  const cargarIncidenciasPanel = async (estado) => {
    setIncidenciasCargando(true);
    try {
      const params = new URLSearchParams({ anio: String(añoSeleccionado) });
      if (trimestreSeleccionado) params.append('trimestre', String(trimestreSeleccionado));
      if (estado) params.append('estado', estado);
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/drive/incidencias?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (ok) setIncidenciasLista(data?.incidencias || []);
    } catch (e) {
      console.error('Error cargando incidencias:', e);
    } finally {
      setIncidenciasCargando(false);
    }
  };

  // Sube a a3doc una factura de incidencia PENDIENTE de vincular (ya tiene ficha OCR)
  const subirA3docPendiente = async (facturaId, onOk) => {
    setSubiendoA3docIncId(facturaId);
    try {
      const res = await fetch(`${API_URL}/api/a3doc/subir-factura-pendiente/${facturaId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (onOk) { await onOk(data); } else { await cargarIncidenciasPanel(incidenciasEstado); }
      } else {
        alert('No se pudo subir a a3doc: ' + (data.detail || 'error desconocido'));
      }
    } catch (e) {
      alert('Error subiendo la factura a a3doc');
    } finally {
      setSubiendoA3docIncId(null);
    }
  };

  useEffect(() => { cargarIncidenciasAbiertas(); }, [cargarIncidenciasAbiertas]);

  // Pestaña Banco: extracto de movimientos (lee de la sync diaria, sin llamadas al banco)
  const cargarMovimientosBanco = useCallback(async () => {
    setCargandoMovimientos(true);
    try {
      // El extracto muestra TODO el año (como el banco); no se filtra por el
      // trimestre fiscal (ese selector es para las pestañas 303/111, no aquí).
      const params = new URLSearchParams({ anio: String(añoSeleccionado) });
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/banco/movimientos?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (ok) {
        setMovimientosBanco(data.movimientos || []);
        setSaldoBanco(data);
      }
    } catch (e) {
      console.error('Error cargando movimientos del banco:', e);
    } finally {
      setCargandoMovimientos(false);
    }
  }, [añoSeleccionado, token]);

  useEffect(() => {
    if (activeTab === 'banco') cargarMovimientosBanco();
  }, [activeTab, cargarMovimientosBanco]);

  // Cargar alertas de cargos sin factura
  const cargarCargosSinFactura = useCallback(async () => {
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/cargos-sin-factura?dias_minimo=7&importe_minimo=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (ok) {
        setCargosSinFactura(data);
      }
    } catch (error) {
      console.error('Error cargando cargos sin factura:', error);
    }
  }, [token]);

  // Reintentar la subida a a3doc de la factura vinculada a un cargo (para los que fallaron)
  const handleReintentarA3doc = async () => {
    if (!cargoSeleccionado) return;
    setReintentandoA3doc(true);
    try {
      const res = await fetch(`${API_URL}/api/a3doc/reintentar-cargo/${cargoSeleccionado.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok) {
        // Estado final por factura devuelto por el backend (data.facturas)
        const estadoPorId = {};
        (data.facturas || []).forEach(f => { if (f.id) estadoPorId[f.id] = f; });
        setCargoSeleccionado(prev => {
          if (!prev) return prev;
          const aplicar = (f) => {
            const nuevo = f && estadoPorId[f.id];
            return nuevo ? { ...f, a3doc_document_id: nuevo.a3doc_document_id, a3doc_subido_en: nuevo.a3doc_subido_en } : f;
          };
          return {
            ...prev,
            facturas: prev.facturas ? prev.facturas.map(aplicar) : prev.facturas,
            factura: prev.factura ? aplicar(prev.factura) : prev.factura,
          };
        });
        cargarCargosBancarios();
        if (data.subidas > 0) {
          alert('Subida(s) a a3doc: ' + data.subidas
            + (data.errores?.length ? '\n\nCon errores: ' + data.errores.map(e => e.error).join('; ') : ''));
        } else if (data.subidas === 0 && data.errores?.length) {
          alert('No se pudo subir a a3doc: ' + data.errores[0].error);
        } else {
          alert('Todas las facturas del cargo ya estaban en a3doc.');
        }
      } else {
        alert('No se pudo subir a a3doc: ' + (data.detail || 'error desconocido'));
      }
    } catch (e) {
      alert('Error al reintentar la subida a a3doc.');
    } finally {
      setReintentandoA3doc(false);
    }
  };

  // Acordeón de subcargos de un cargo consolidado de tarjeta.
  const toggleCargoExpandido = (id) => setExpandedCargos((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Subir a a3doc en lote los cargos seleccionados, en tandas pequeñas (robusto, sin timeout).
  const subirCargosA3docLote = async () => {
    const ids = Array.from(cargosSelA3doc);
    if (ids.length === 0) { alert('Selecciona al menos un cargo con factura vinculada'); return; }
    const TANDA = 8;
    let subidas = 0, yaEstaban = 0, errores = 0;
    setSubiendoA3docLote(true);
    try {
      for (let i = 0; i < ids.length; i += TANDA) {
        const bloque = ids.slice(i, i + TANDA);
        setA3docLoteProgreso(`${Math.min(i + bloque.length, ids.length)}/${ids.length}`);
        try {
          const res = await fetch(`${API_URL}/api/a3doc/reintentar-cargos-lote`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ cargo_ids: bloque })
          });
          const data = await res.json();
          if (res.ok) {
            subidas += data.subidas || 0;
            yaEstaban += data.ya_estaban || 0;
            errores += (data.errores || []).length;
          } else {
            errores += bloque.length;
          }
        } catch (e) {
          errores += bloque.length;
        }
      }
      alert(`Subidas a a3doc: ${subidas}\nYa estaban: ${yaEstaban}` + (errores ? `\nErrores: ${errores}` : ''));
      setCargosSelA3doc(new Set());
      cargarCargosBancarios();
    } finally {
      setSubiendoA3docLote(false);
      setA3docLoteProgreso('');
    }
  };

  // Cargar facturas drive marcadas como posibles duplicados (oculta_iva)
  const cargarDuplicadosPendientes = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/finanzas/facturas-drive/duplicados-pendientes`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setDuplicadosPendientes(data.items || []);
      }
    } catch (error) {
      console.error('Error cargando duplicados pendientes:', error);
    }
  }, [token]);

  const resolverDuplicado = async (facturaId, accion) => {
    setResolviendoDuplicado(facturaId);
    try {
      const response = await fetch(
        `${API_URL}/api/finanzas/facturas-drive/${facturaId}/resolver-duplicado?accion=${accion}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        await cargarDuplicadosPendientes();
        await cargarResumenGlobal(trimestreSeleccionado);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(err.detail || 'Error resolviendo duplicado');
      }
    } catch (error) {
      console.error('Error resolviendo duplicado:', error);
      alert('Error: ' + error.message);
    } finally {
      setResolviendoDuplicado(null);
    }
  };

  // Ejecutar auto-procesamiento manual
  const ejecutarAutoProcesamiento = async () => {
    setEjecutandoAutoProcesamiento(true);
    try {
      const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/auto-procesar/ejecutar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (ok) {
        alert(`✅ ${data.message}\n\nProcesadas: ${data.stats?.procesadas || 0}\nVinculadas auto: ${data.stats?.vinculadas_auto || 0}\nPendientes: ${data.stats?.pendientes || 0}`);
        cargarCargosBancarios();
        cargarCargosSinFactura();
      }
    } catch (error) {
      console.error('Error en auto-procesamiento:', error);
      alert('Error ejecutando auto-procesamiento');
    } finally {
      setEjecutandoAutoProcesamiento(false);
    }
  };

  useEffect(() => {
    if (token) {
      // Pestaña por defecto = Resumen: cargar KPIs agregados
      cargarResumenDashboard();
      // También mantenemos rentabilidad y resumen global para cuando se naveguen esas tabs
      cargarRentabilidad();
      // Cargar resumen global con el trimestre seleccionado
      cargarResumenGlobal(trimestreSeleccionado);
      // Comprobar si hay facturas duplicadas pendientes de revisar
      cargarDuplicadosPendientes();
    }
  }, [añoSeleccionado, token, cargarResumenDashboard, cargarRentabilidad, cargarResumenGlobal, trimestreSeleccionado, cargarDuplicadosPendientes]);

  // Auto-procesar facturas cuando se entra a la pestaña de Drive/Automatización
  // DESACTIVADO - ejecutar manualmente para evitar costes inesperados
  // useEffect(() => {
  //   if (activeTab === 'automatizacion') {
  //     const autoProcesarEnSilencio = async () => { ... };
  //     autoProcesarEnSilencio();
  //   }
  // }, [activeTab]);

  // Cargar archivos de Drive automáticamente al entrar en la pestaña
  useEffect(() => {
    if (activeTab === 'automatizacion' && !driveConectado && !verificandoDrive) {
      const cargarDriveAutomatico = async () => {
        const tkn = getAuthToken();
        if (!tkn) return;
        
        setVerificandoDrive(true);
        try {
          const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/drive/archivos`, { 
            headers: { 
              'Authorization': `Bearer ${tkn}`,
              'Content-Type': 'application/json'
            }
          });
          if (ok) {
            setDriveArchivos(data.archivos || []);
            setDriveSubcarpetas(data.subcarpetas || []);
            setDriveConectado(true);
          }
        } catch (error) {
          console.error('Error conectando a Drive:', error);
        } finally {
          setVerificandoDrive(false);
        }
      };
      cargarDriveAutomatico();
    }
  }, [activeTab, driveConectado, verificandoDrive]);

  // Cargar abonos cuando se selecciona el tab de ingresos
  useEffect(() => {
    if (activeTab === 'ingresos') {
      cargarAbonos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, añoSeleccionado, filtroTipoAbono, filtroAbonoVinculado]);

  useEffect(() => {
    if (activeTab === 'ingresos') {
      cargarUltimaImportacion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Recarga el contenido de la carpeta de Drive actualmente visible (sin recargar toda la pagina).
  const recargarContenidoDrive = async () => {
    if (!driveConectado) return;
    setCargandoDrive(true);
    try {
      const url = driveCarpetaActual
        ? `${API_URL}/api/finanzas/drive/archivos?carpeta_id=${driveCarpetaActual}`
        : `${API_URL}/api/finanzas/drive/archivos`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      setDriveArchivos(data.archivos || []);
      setDriveSubcarpetas(data.subcarpetas || []);
    } catch (e) {
      console.error('Error recargando contenido Drive:', e);
    } finally {
      setCargandoDrive(false);
    }
  };

  // Función para recargar datos de la pestaña activa (accesible desde cualquier handler)
  const cargarDatosPestaña = async () => {
    switch (activeTab) {
        case 'resumen':
          cargarResumenDashboard();
          break;
        case 'rentabilidad':
          cargarRentabilidad();
          break;
        case 'emitidas':
          if (facturasEmitidas.length === 0) cargarFacturas();
          break;
        case 'soportadas':
          if (facturasSoportadas.length === 0) {
            cargarFacturas();
          }
          cargarCargosBancarios();
          break;
        case 'pendientes':
          // Cargar presupuestos pendientes de facturar
          setCargandoPendientes(true);
          try {
            const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/pendientes-facturar?dias_max=${diasMaxPendientes}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (ok) {
              setPendientesFacturar(data.presupuestos || []);
            }
          } catch (e) {
            console.error('Error cargando pendientes:', e);
          } finally {
            setCargandoPendientes(false);
          }
          break;
        case 'fiscal':
          // Cargar resumen fiscal
          setCargandoFiscal(true);
          try {
            const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/resumen-fiscal?anio=${añoSeleccionado}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (ok) {
              setResumenFiscal(data);
            }
          } catch (e) {
            console.error('Error cargando resumen fiscal:', e);
          } finally {
            setCargandoFiscal(false);
          }
          break;
        case 'gastos-fijos':
          // Cargar gastos fijos + personal
          setCargandoGastosFijos(true);
          try {
            const [gfRes, personalRes] = await Promise.all([
              safeFetch(`${API_URL}/api/finanzas/gastos-fijos`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }),
              fetch(`${API_URL}/api/finanzas/gastos-fijos/personal`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })
            ]);
            if (gfRes.ok) {
              setGastosFijos(gfRes.data.gastos_fijos || gfRes.data.gastos || []);
            }
            if (personalRes.ok) {
              const personalData = await personalRes.json();
              setGastosPersonal(personalData);
            }
          } catch (e) {
            console.error('Error cargando gastos fijos:', e);
          } finally {
            setCargandoGastosFijos(false);
          }
          break;
        case 'prestamos':
          // Cargar préstamos
          setCargandoPrestamos(true);
          try {
            const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/prestamos`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (ok) {
              setPrestamos(data.prestamos || []);
            }
          } catch (e) {
            console.error('Error cargando préstamos:', e);
          } finally {
            setCargandoPrestamos(false);
          }
          break;
        default:
          break;
      }
  };

  // Cargar datos específicos de cada pestaña al seleccionarla
  useEffect(() => {
    if (token) {
      cargarDatosPestaña();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, añoSeleccionado, diasMaxPendientes, token]);

  // Al volver a esta pestaña del navegador (p. ej. tras emitir una factura en la
  // ficha, que se abre en otra), se recarga lo que la factura cambia sin pulsar
  // "Actualizar": Resumen, Pendientes de facturar y Emitidas. Solo si se estuvo
  // fuera unos segundos, para no recargar al cambiar de ventana sin más.
  const recargarAlVolverRef = React.useRef(null);
  recargarAlVolverRef.current = () => {
    if (!token) return;
    if (activeTab === 'resumen' || activeTab === 'pendientes') cargarDatosPestaña();
    else if (activeTab === 'emitidas') cargarFacturas();
  };
  useEffect(() => {
    let ocultaDesde = null;
    const alCambiarVisibilidad = () => {
      if (document.visibilityState === 'hidden') {
        ocultaDesde = Date.now();
      } else if (ocultaDesde && Date.now() - ocultaDesde >= 5000) {
        ocultaDesde = null;
        recargarAlVolverRef.current();
      } else {
        ocultaDesde = null;
      }
    };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => document.removeEventListener('visibilitychange', alCambiarVisibilidad);
  }, []);

  // Recargar cargos cuando cambian los filtros o paginación
  useEffect(() => {
    cargarCargosBancarios();
  }, [cargarCargosBancarios]);
  
  // Reset página cuando cambian los filtros
  useEffect(() => {
    setCargosPage(0);
  }, [filtros.estado, filtros.categoria, filtros.proveedor, filtros.concepto, filtros.importeMin, filtros.importeMax, filtros.fechaDesde, filtros.fechaHasta]);

  // Cargar facturas en Drive que no están vinculadas
  const cargarFacturasNoVinculadas = async () => {
    setCargandoNoVinculadas(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/drive/facturas-no-vinculadas`, { headers });
      if (response.ok) {
        const data = await response.json();
        setFacturasNoVinculadas(data);
      }
    } catch (error) {
      console.error('Error cargando facturas no vinculadas:', error);
    } finally {
      setCargandoNoVinculadas(false);
    }
  };

  // Cargar abonos/ingresos
  const cargarUltimaImportacion = async () => {
    try {
      const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/ultima-importacion`, { headers });
      if (response.ok) {
        const data = await response.json();
        setUltimaImportacion(data && data.fecha ? data : null);
      }
    } catch (error) {
      console.error('Error cargando ultima importacion:', error);
    }
  };

  const cargarAbonos = async () => {
    setCargandoAbonos(true);
    try {
      const params = new URLSearchParams({ anio: añoSeleccionado, limit: 100 });
      if (filtroTipoAbono) params.append('tipo_abono', filtroTipoAbono);
      if (filtroAbonoVinculado !== null) params.append('vinculado', filtroAbonoVinculado);
      
      const response = await fetch(`${API_URL}/api/finanzas/abonos?${params}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAbonos(data.abonos || []);
        setAbonosTotal(data.total || 0);
        setAbonosTotalImporte(data.total_importe || 0);
        setAbonosPendientes(data.pendientes_vincular || 0);
      }
    } catch (error) {
      console.error('Error cargando abonos:', error);
    } finally {
      setCargandoAbonos(false);
    }
  };

  // Cargar sugerencias de vinculación automáticas
  const cargarSugerenciasVinculacion = async () => {
    setCargandoSugerenciasGlobal(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/sugerencias-vinculacion?limit=30`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSugerenciasVinculacion(data.sugerencias || []);
      }
    } catch (error) {
      console.error('Error cargando sugerencias:', error);
    } finally {
      setCargandoSugerenciasGlobal(false);
    }
  };

  // Vincular desde sugerencia con un clic
  const vincularDesdeSugerencia = async (abonoId, tipo, itemId) => {
    setVinculandoSugerencia(abonoId);
    try {
      const response = await fetch(
        `${API_URL}/api/finanzas/abonos/${abonoId}/vincular-sugerencia?tipo=${tipo}&item_id=${itemId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(`${data.mensaje}`);
        cargarAbonos();
        cargarSugerenciasVinculacion();
        // Detectar exceso de pago
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Error vinculando'}`);
      }
    } catch (error) {
      console.error('Error vinculando sugerencia:', error);
      alert('Error al vincular');
    } finally {
      setVinculandoSugerencia(null);
    }
  };

  // Cargar sugerencias de vinculación para un abono
  const cargarSugerenciasAbono = async (abonoId) => {
    setCargandoSugerenciasAbono(true);
    setSugerenciasAbono(null);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/sugerencias/${abonoId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSugerenciasAbono(data.sugerencias);
      }
    } catch (error) {
      console.error('Error cargando sugerencias:', error);
    } finally {
      setCargandoSugerenciasAbono(false);
    }
  };

  // Vincular abono con factura emitida
  const vincularAbonoFactura = async (abonoId, facturaId) => {
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoId}/vincular-factura-emitida?factura_id=${facturaId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      if (response.ok) {
        alert(`Abono vinculado. ${data.estado_cobro === 'cobrada' ? 'Factura cobrada al 100%' : `Cobrado ${data.total_cobrado}€ de ${data.total_factura}€`}`);
        setShowVincularAbono(false);
        cargarAbonos();
        // Detectar exceso de pago
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      } else {
        alert('Error: ' + (data.detail || 'No se pudo vincular'));
      }
    } catch (error) {
      console.error('Error vinculando abono:', error);
      alert('Error al vincular el abono: ' + error.message);
    }
  };

  // Buscar facturas emitidas y presupuestos para cobros de clientes
  const buscarParaCobro = async (query, verTodos = false) => {
    if (!query && !verTodos) {
      setResultadosBusquedaCobro({ items: [], facturas_emitidas: [], presupuestos: [] });
      return;
    }
    setBuscandoCobros(true);
    try {
      const importeRef = abonoSeleccionado?.importe || 0;
      const url = `${API_URL}/api/finanzas/cobros/buscar?q=${encodeURIComponent(query || '')}&limit=50&solo_pendientes=true&ver_todos=${verTodos}&incluir_pagadas=${mostrarPagadas}${importeRef > 0 ? `&importe_ref=${importeRef}` : ''}`;
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setResultadosBusquedaCobro(data);
      }
    } catch (error) {
      console.error('Error buscando:', error);
    } finally {
      setBuscandoCobros(false);
    }
  };

  // Vincular abono con presupuesto
  const vincularAbonoPresupuesto = async (abonoId, presupuestoId) => {
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoId}/vincular-presupuesto?presupuesto_id=${presupuestoId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      if (response.ok) {
        let mensaje = data.message;
        if (data.estado_pago) {
          mensaje += `\n\n📊 Estado de pago: ${data.estado_pago}`;
          if (data.total_pagado !== undefined) {
            mensaje += `\n💰 Total pagado: ${formatNumber(data.total_pagado)}`;
          }
          if (data.pendiente !== undefined && data.pendiente > 0) {
            mensaje += `\n⏳ Pendiente: ${formatNumber(data.pendiente)}`;
          }
        }
        alert(mensaje);
        setShowVincularAbono(false);
        setBusquedaCobro('');
        setResultadosBusquedaCobro({ facturas_emitidas: [], presupuestos: [] });
        cargarAbonos();
        // Detectar exceso de pago
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      } else {
        alert('Error: ' + (data.detail || 'No se pudo vincular'));
      }
    } catch (error) {
      console.error('Error vinculando:', error);
      alert('Error al vincular: ' + error.message);
    }
  };

  // Función para vincular múltiples items (facturas y/o presupuestos) a un abono
  const vincularMultiplesItems = async () => {
    if (!abonoSeleccionado || itemsSeleccionadosCobro.length === 0) {
      alert('Por favor, selecciona al menos una factura o presupuesto');
      return;
    }
    
    setVinculandoMultiple(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoSeleccionado.id}/vincular-multiple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: itemsSeleccionadosCobro })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert(`${data.message}`);
        setShowVincularAbono(false);
        setBusquedaCobro('');
        setResultadosBusquedaCobro({ facturas_emitidas: [], presupuestos: [] });
        setItemsSeleccionadosCobro([]);
        cargarAbonos();
        // Detectar exceso de pago
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      } else {
        alert('Error: ' + (data.detail || 'No se pudo vincular'));
      }
    } catch (error) {
      console.error('Error vinculando múltiples:', error);
      alert('Error al vincular: ' + error.message);
    } finally {
      setVinculandoMultiple(false);
    }
  };

  // Función para toggle selección de item para cobro
  const toggleItemSeleccionadoCobro = (item, tipo) => {
    // Warning if item is already paid
    if (item.estado_cobro === 'pagada') {
      if (!window.confirm(`Esta factura/presupuesto ya está marcada como pagada (${formatNumber(item.total)}). ¿Vincular solo para trazabilidad sin afectar a los importes?`)) {
        return;
      }
    }
    const itemId = `${tipo}_${item.id}`;
    setItemsSeleccionadosCobro(prev => {
      const exists = prev.find(i => i.itemId === itemId);
      if (exists) {
        return prev.filter(i => i.itemId !== itemId);
      } else {
        return [...prev, { 
          itemId, 
          id: item.id, 
          tipo, 
          nombre: tipo === 'factura' ? item.numero_factura : `#${item.numero}`,
          pendiente: item.pendiente_cobro || 0
        }];
      }
    });
  };

  // Verificar si un item está seleccionado
  const isItemSeleccionadoCobro = (item, tipo) => {
    const itemId = `${tipo}_${item.id}`;
    return itemsSeleccionadosCobro.some(i => i.itemId === itemId);
  };

  // Calcular total seleccionado
  const totalSeleccionadoCobro = itemsSeleccionadosCobro.reduce((sum, item) => sum + (item.pendiente || 0), 0);

  // Guardar devolución manual (sin factura soportada)
  const guardarDevolucionManual = async () => {
    if (!abonoSeleccionado || !datosDevolucion.proveedor.trim()) {
      alert('Por favor, indica el nombre del proveedor');
      return;
    }
    
    setGuardandoDevolucion(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoSeleccionado.id}/marcar-devolucion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datosDevolucion)
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        setShowVincularAbono(false);
        setMostrarFormDevolucion(false);
        setDatosDevolucion({ proveedor: '', notas: '', numero_factura_original: '' });
        cargarAbonos();
      } else {
        const data = await response.json();
        alert('Error: ' + (data.detail || 'No se pudo guardar'));
      }
    } catch (error) {
      console.error('Error guardando devolución:', error);
      alert('Error al guardar: ' + error.message);
    } finally {
      setGuardandoDevolucion(false);
    }
  };

  // Cargar pendientes de cobro al abrir el modal
  const cargarPendientesCobro = async () => {
    setBuscandoCobros(true);
    try {
      const importeRef = abonoSeleccionado?.importe || 0;
      const url = `${API_URL}/api/finanzas/cobros/buscar?solo_pendientes=true&limit=30&incluir_pagadas=${mostrarPagadas}${importeRef > 0 ? `&importe_ref=${importeRef}` : ''}`;
      const response = await fetch(url, { headers });
      if (response.ok) {
        const data = await response.json();
        setResultadosBusquedaCobro(data);
      }
    } catch (error) {
      console.error('Error cargando pendientes:', error);
    } finally {
      setBuscandoCobros(false);
    }
  };

  // Buscar facturas soportadas para vincular (devoluciones/fianzas)
  const buscarFacturasSoportadas = async (query) => {
    if (!query || query.length < 2) {
      setResultadosBusquedaFactura([]);
      return;
    }
    setBuscandoFacturas(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/facturas-soportadas/buscar?q=${encodeURIComponent(query)}&limit=15`, { headers });
      if (response.ok) {
        const data = await response.json();
        setResultadosBusquedaFactura(data.facturas || []);
      }
    } catch (error) {
      console.error('Error buscando facturas:', error);
    } finally {
      setBuscandoFacturas(false);
    }
  };

  // Vincular abono con factura soportada (devolución/fianza)
  const vincularAbonoFacturaSoportada = async (abonoId, facturaId, tipo) => {
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoId}/vincular-factura-soportada?factura_id=${facturaId}&tipo=${tipo}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      if (response.ok) {
        alert(data.message);
        setShowVincularAbono(false);
        setBusquedaFacturaSoportada('');
        setResultadosBusquedaFactura([]);
        cargarAbonos();
      } else {
        alert('Error: ' + (data.detail || 'No se pudo vincular'));
      }
    } catch (error) {
      console.error('Error vinculando abono:', error);
      alert('Error al vincular el abono: ' + error.message);
    }
  };

  // Desvincular abono
  const desvincularAbono = async (abonoId) => {
    if (!window.confirm('¿Eliminar la vinculación de este abono?')) return;
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoId}/desvincular`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = {};
      }
      
      if (response.ok) {
        alert('Vinculación eliminada');
        cargarAbonos();
      } else {
        alert('Error: ' + (data.detail || 'No se pudo desvincular'));
      }
    } catch (error) {
      console.error('Error desvinculando:', error);
      alert('Error al desvincular: ' + error.message);
    }
  };

  // ============================================================================
  // FUNCIONES PARA VINCULACIÓN V3
  // ============================================================================
  
  // Buscar documentos en V3 (facturas/presupuestos)
  const buscarDocumentosV3 = async (q) => {
    if (!q || q.length < 1) {
      setResultadosV3([]);
      return;
    }
    setBuscandoV3(true);
    try {
      const response = await fetch(`${API_URL}/api/cobros-v3/buscar?q=${encodeURIComponent(q)}&limit=20&incluir_pagadas=${mostrarPagadas}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setResultadosV3(data.items || []);
      }
    } catch (error) {
      console.error('Error buscando en V3:', error);
    } finally {
      setBuscandoV3(false);
    }
  };

  // Cargar sugerencias inteligentes para el modal V3
  const cargarSugerenciasV3 = async (abonoId) => {
    setCargandoSugerenciasV3(true);
    setSugerenciasV3([]);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/sugerencias/${abonoId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setSugerenciasV3(data.sugerencias?.facturas_emitidas || []);
      }
    } catch (error) {
      console.error('Error cargando sugerencias V3:', error);
    } finally {
      setCargandoSugerenciasV3(false);
    }
  };

  // Toggle multi-select de documentos — siempre asigna el pendiente completo.
  // `doc.key` es la clave ÚNICA de selección: en multievento facturado por
  // jornada cada factura es su propio documento (key = factura_id); en el resto
  // se agrupa por presupuesto (key = presupuesto_id). Así dos facturas del mismo
  // presupuesto (mismas jornadas) se tratan como documentos independientes.
  const togglePresupuestoSeleccion = (doc) => {
    const key = doc.key || (doc.es_por_jornada ? doc.factura_id : (doc.presupuesto_id || doc.id));
    setDocumentosSeleccionados(prev => {
      const existing = prev.find(d => d.key === key);
      if (existing) {
        return prev.filter(d => d.key !== key);
      }
      // If pendiente is 0 (already paid via legacy), use the ingreso amount or the total as fallback
      const pendiente = doc.pendiente || 0;
      const importeAbono = abonoSeleccionado ? abonoSeleccionado.importe : 0;
      const totalAsignadoActual = prev.reduce((s, d) => s + d.importe_asignado, 0);
      const restanteIngreso = Math.max(0, importeAbono - totalAsignadoActual);
      const importeDefault = pendiente > 0 ? Math.min(pendiente, restanteIngreso) : Math.min(doc.total || importeAbono, restanteIngreso);
      return [...prev, {
        key,
        presupuesto_id: doc.presupuesto_id || doc.id,
        factura_id: doc.factura_id || null,
        documento_tipo: doc.documento_tipo || (doc.factura_id ? 'factura' : 'presupuesto'),
        es_por_jornada: !!doc.es_por_jornada,
        numero: doc.numero,
        cliente: doc.cliente,
        factura_numero: doc.factura_numero,
        pendiente: doc.pendiente,
        importe_asignado: Math.round(importeDefault * 100) / 100,
      }];
    });
  };
  
  // Vincular la factura de Drive a una compra de tarjeta (subcargo)
  const vincularFacturaASubcargo = async (cand) => {
    if (!datosFacturaIA || vinculandoSubcargo) return;
    if (datosFacturaIA?.incidencia?.estado === 'abierta') { alert('Esta factura tiene una incidencia abierta. Resuelvela antes de vincularla.'); return; }
    setVinculandoSubcargo(cand.subcargo_id);
    try {
      const url = `${API_URL}/api/finanzas/drive/vincular-factura-subcargo?file_id=${archivoEnProceso.id}&cargo_id=${cand.cargo_id}&subcargo_id=${cand.subcargo_id}`;
      const { ok, data } = await safeFetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(datosFacturaIA),
      });
      if (ok && data.success) {
        const a = data.acciones || {};
        const lineaDrive = a.movida_drive
          ? `\nArchivada en Drive: ${a.carpeta_destino || 'Vinculadas'}`
          : '\nNO se pudo mover el PDF en Drive; hazlo luego a mano.';
        const lineaA3doc = a.subida_a3doc ? '\nCopiada a a3doc (Facturas recibidas)' : '\nNO se pudo copiar a a3doc; subela luego desde el cargo.';
        alert(`Factura vinculada a la compra "${cand.concepto || ''}" (${formatNumber(cand.importe)})\n${lineaDrive}${lineaA3doc}`);
        setShowProcesarFactura(false);
        setArchivoEnProceso(null);
        setDatosFacturaIA(null);
        setCargosMatch([]);
        setSubcargosMatch([]);
        setCargosSeleccionados([]);
        cargarDatosPestaña();
        recargarContenidoDrive();
      } else {
        const det = data.detail;
        alert((typeof det === 'string' ? det : det?.mensaje) || data.message || 'Error vinculando la factura a la compra');
      }
    } catch (e) {
      console.error('Error vinculando subcargo:', e);
      alert('Error de conexion al vincular');
    } finally {
      setVinculandoSubcargo(null);
    }
  };

  // Vincular abono usando el sistema V3
  const vincularConV3 = async () => {
    if (!abonoSeleccionado || documentosSeleccionados.length === 0) {
      alert('Selecciona al menos un presupuesto para vincular');
      return;
    }
    
    const totalAsignado = documentosSeleccionados.reduce((s, d) => s + d.importe_asignado, 0);
    if (totalAsignado <= 0) {
      alert('El importe asignado debe ser mayor que 0');
      return;
    }

    setVinculandoV3(true);
    try {
      if (documentosSeleccionados.length === 1) {
        // Single vincular
        const doc = documentosSeleccionados[0];
        const response = await safeFetch(`${API_URL}/api/cobros-unificado/vincular`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            presupuesto_id: doc.presupuesto_id,
            // Multievento por jornada: vincular a la factura concreta, no al presupuesto
            factura_id: doc.es_por_jornada ? doc.factura_id : undefined,
            ingreso_id: abonoSeleccionado.id,
            importe: doc.importe_asignado,
          })
        });
        const data = response.data || {};
        if (!response.ok) throw new Error(data.detail || 'Error al vincular');
        alert(`Vinculado: ${doc.importe_asignado.toFixed(2)}€ a ${doc.factura_numero ? `factura ${doc.factura_numero}` : `presupuesto #${doc.numero}`}`);
        // Detect excess
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      } else {
        // Multi vincular
        const payload = {
          ingreso_id: abonoSeleccionado.id,
          vinculos: documentosSeleccionados.map(d => ({
            presupuesto_id: d.presupuesto_id,
            // Multievento por jornada: vincular a la factura concreta
            factura_id: d.es_por_jornada ? d.factura_id : undefined,
            importe: d.importe_asignado,
          })),
        };
        const response = await safeFetch(`${API_URL}/api/cobros-unificado/vincular-multiple`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = response.data || {};
        if (!response.ok) throw new Error(data.detail || 'Error al vincular');
        alert(data.message || 'Vinculado correctamente');
        // Detect excess from multi-link
        if (data.exceso && data.exceso.importe > 0.01) {
          setExcesoData(data.exceso);
        }
      }
      
      setShowVincularV3(false);
      setAbonoSeleccionado(null);
      setDocumentosSeleccionados([]);
      setDocumentoV3Seleccionado(null);
      setBusquedaV3('');
      setResultadosV3([]);
      setSugerenciasV3([]);
      cargarAbonos();
    } catch (error) {
      console.error('Error vinculando:', error);
      alert('Error al vincular: ' + error.message);
    } finally {
      setVinculandoV3(false);
    }
  };

  // Vincular abono a operación de financiación
  const vincularAbonoAFinanciacion = async () => {
    if (!abonoSeleccionado || !operacionFinanciacionId) {
      alert('Selecciona una operación de financiación');
      return;
    }
    setVinculandoV3(true);
    try {
      const params = new URLSearchParams({
        operacion_id: operacionFinanciacionId,
        tipo_movimiento: 'desembolso',
      });
      const res = await fetch(`${API_URL}/api/finanzas/abonos/${abonoSeleccionado.id}/vincular-financiacion?${params.toString()}`, {
        method: 'POST',
        headers,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Error al vincular');
      }
      alert(
        data.warning
          ? `Ingreso vinculado a la operación de financiación\n\n⚠️ ${data.warning}`
          : 'Ingreso vinculado a la operación de financiación'
      );
      setShowVincularV3(false);
      setAbonoSeleccionado(null);
      setOperacionFinanciacionId('');
      setTipoVinculacionAbono('factura');
      cargarAbonos();
      // Refrescar operaciones para que la próxima apertura del modal
      // muestre el saldo de deuda actualizado (un desembolso aumenta
      // el capital pendiente; una devolución/cuota lo reduce).
      recargarOperacionesFinanciacion();
    } catch (error) {
      console.error('Error vinculando a financiación:', error);
      alert('Error: ' + error.message);
    } finally {
      setVinculandoV3(false);
    }
  };

  // Cargar historial de auto-procesamiento
  const cargarHistorialAutoProcesamiento = async () => {
    setCargandoHistorial(true);
    try {
      const response = await fetch(`${API_URL}/api/finanzas/auto-procesar/estadisticas`, { headers });
      if (response.ok) {
        const data = await response.json();
        setHistorialAutoProcesamiento(data);
      }
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setCargandoHistorial(false);
    }
  };

  // Extraer factura con IA
  const extraerFacturaConIA = async () => {
    if (!archivoSeleccionado) return;
    
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', archivoSeleccionado);
      
      const response = await fetch(`${API_URL}/api/finanzas/facturas-soportadas/extraer-ia`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        setDatosExtraidos(result.datos);
        // Rellenar formulario con datos extraídos
        setFormFactura({
          proveedor_nombre: result.datos.proveedor_nombre || '',
          proveedor_cif: result.datos.proveedor_cif || '',
          numero_factura: result.datos.numero_factura || '',
          fecha_factura: result.datos.fecha_factura || '',
          categoria: result.datos.categoria_sugerida || 'otros',
          base_imponible: result.datos.base_imponible || 0,
          iva_4: result.datos.iva_4 || 0,
          iva_10: result.datos.iva_10 || 0,
          iva_21: result.datos.iva_21 || 0,
          total_iva: result.datos.total_iva || 0,
          total: result.datos.total || 0,
          lineas: result.datos.lineas || [],
          notas: '',
          extraido_por_ia: true,
          confianza_extraccion: result.datos.confianza
        });
        setShowSubirFactura(false);
        setShowNuevaFactura(true);
      } else {
        alert('Error extrayendo datos: ' + result.mensaje);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error procesando factura');
    } finally {
      setExtracting(false);
    }
  };

  // Guardar factura soportada
  const guardarFacturaSoportada = async () => {
    try {
      const response = await fetch(`${API_URL}/api/finanzas/facturas-soportadas`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formFactura)
      });
      
      if (response.ok) {
        setShowNuevaFactura(false);
        setFormFactura({
          proveedor_nombre: '',
          proveedor_cif: '',
          numero_factura: '',
          fecha_factura: '',
          categoria: 'otros',
          base_imponible: 0,
          iva_4: 0,
          iva_10: 0,
          iva_21: 0,
          total_iva: 0,
          total: 0,
          lineas: [],
          notas: ''
        });
        setDatosExtraidos(null);
        cargarDatosPestaña();
      }
    } catch (error) {
      console.error('Error guardando factura:', error);
    }
  };

  // Importar cargos bancarios
  const importarCargosBancarios = async (file) => {
    setImportandoCargos(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Timeout largo (5 min) para archivos grandes
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);
      
      const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/importar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      const result = await response.json();
      
      if (result.success) {
        // Mostrar resumen detallado
        let mensaje = `✅ Importación completada\n\n`;
        mensaje += `📊 Total importados: ${result.total_importados || result.cargos_importados}\n`;
        if (result.cargos_importados !== undefined && result.abonos_importados !== undefined) {
          mensaje += `   • Cargos (gastos): ${result.cargos_importados}\n`;
          mensaje += `   • Abonos (ingresos): ${result.abonos_importados}\n`;
        }
        if (result.duplicados_omitidos > 0) {
          mensaje += `\n⚠️ Duplicados omitidos: ${result.duplicados_omitidos}`;
        }
        if (result.facturas_auto_vinculadas > 0) {
          mensaje += `\n🔗 Facturas vinculadas: ${result.facturas_auto_vinculadas}`;
        }
        if (result.total_errores > 0) {
          mensaje += `\n\n❌ Errores encontrados: ${result.total_errores}`;
          // Mostrar detalles de los primeros errores
          if (result.errores && result.errores.length > 0) {
            mensaje += `\n\n📋 Detalle de errores:`;
            result.errores.slice(0, 5).forEach(err => {
              mensaje += `\n• Fila ${err.fila}: ${err.error}`;
              if (err.datos) {
                const datos = err.datos;
                if (datos.fecha || datos.importe) {
                  mensaje += `\n  ↳ Fecha: ${datos.fecha || 'N/A'}, Importe: ${datos.importe || 'N/A'}`;
                } else if (datos.raw) {
                  mensaje += `\n  ↳ ${datos.raw}`;
                }
              }
            });
            if (result.total_errores > 5) {
              mensaje += `\n... y ${result.total_errores - 5} errores más`;
            }
          }
        }
        
        alert(mensaje);
        setShowImportarCargos(false);
        cargarDatosPestaña();
        cargarUltimaImportacion();
        // También recargar abonos si estamos en ese tab
        if (activeTab === 'ingresos') {
          cargarAbonos();
        }
      } else {
        alert('Error importando: ' + (result.mensaje || result.detail || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.name === 'AbortError') {
        alert('La importación está tardando demasiado. Los registros pueden haberse importado correctamente. Recarga la página para verificar.');
      } else {
        alert('Error importando archivo. Si el proceso fue largo, los registros podrían haberse importado. Recarga para verificar.');
      }
      // Recargar datos de todas formas por si se importó parcialmente
      cargarDatosPestaña();
      if (activeTab === 'ingresos') {
        cargarAbonos();
      }
    } finally {
      setImportandoCargos(false);
    }
  };

  // Descargar factura emitida
  const descargarFactura = async (facturaId, numeroFactura) => {
    try {
      const response = await fetch(`${API_URL}/api/facturas/${facturaId}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar factura');
      }
      
      const contentType = response.headers.get('content-type');
      const blob = await response.blob();
      
      // Crear URL para descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${numeroFactura || facturaId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error descargando factura:', error);
      alert('Error al descargar la factura');
    }
  };

  // Subir una factura emitida a a3doc (manual, individual)
  const subirFacturaEmitidaA3doc = async (facturaId) => {
    setSubiendoA3docId(facturaId);
    try {
      const res = await fetch(`${API_URL}/api/facturas/${facturaId}/subir-a3doc`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (res.ok && data.subido) {
        cargarFacturas();
      } else {
        alert('No se pudo subir a a3doc: ' + (data.detail || 'error desconocido'));
      }
    } catch (e) {
      alert('Error al subir la factura a a3doc.');
    } finally {
      setSubiendoA3docId(null);
    }
  };

  // Subir en lote las facturas emitidas seleccionadas a a3doc, en tandas pequeñas (robusto).
  const subirFacturasEmitidasA3docLote = async () => {
    const ids = Array.from(facturasEmitidasSeleccionadas);
    if (ids.length === 0) { alert('Selecciona al menos una factura'); return; }
    const TANDA = 8;
    let subidas = 0, yaEstaban = 0, errores = 0;
    setSubiendoA3docLote(true);
    try {
      for (let i = 0; i < ids.length; i += TANDA) {
        const bloque = ids.slice(i, i + TANDA);
        setA3docLoteProgreso(`${Math.min(i + bloque.length, ids.length)}/${ids.length}`);
        try {
          const res = await fetch(`${API_URL}/api/facturas/subir-a3doc-lote`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ factura_ids: bloque })
          });
          const data = await res.json();
          if (res.ok) {
            subidas += data.subidas || 0;
            yaEstaban += data.ya_estaban || 0;
            errores += (data.errores || []).length;
          } else {
            errores += bloque.length;
          }
        } catch (e) {
          errores += bloque.length;
        }
      }
      alert(`Subidas a a3doc: ${subidas}\nYa estaban: ${yaEstaban}` + (errores ? `\nErrores: ${errores}` : ''));
      setFacturasEmitidasSeleccionadas(new Set());
      cargarFacturas();
    } finally {
      setSubiendoA3docLote(false);
      setA3docLoteProgreso('');
    }
  };

  const descargarZipFacturasSeleccionadas = async () => {
    const ids = Array.from(facturasEmitidasSeleccionadas);
    if (ids.length === 0) {
      alert('Selecciona al menos una factura');
      return;
    }
    setDescargandoZip(true);
    try {
      const response = await fetch(`${API_URL}/api/facturas/descargar-zip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al descargar ZIP');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `facturas_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setFacturasEmitidasSeleccionadas(new Set());
    } catch (error) {
      alert('❌ ' + error.message);
    } finally {
      setDescargandoZip(false);
    }
  };

  // Descartar solicitud de factura
  const handleDescartarFactura = async (presupuestoId) => {
    if (!window.confirm('¿Estás seguro de que quieres descartar esta solicitud? El presupuesto desaparecerá de la lista de pendientes.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/pendientes-facturar/${presupuestoId}/descartar`, {
        method: 'POST',
        headers
      });
      
      if (response.ok) {
        // Eliminar de la lista localmente
        setPendientesFacturar(prev => prev.filter(p => p.id !== presupuestoId));
        // Opcional: Recargar datos
        // cargarDatosPestaña(); 
      } else {
        alert('Error al descartar la solicitud');
      }
    } catch (error) {
      console.error('Error descartando factura:', error);
      alert('Error de conexión');
    }
  };

  // Formatear número
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(num || 0);
  };

  const formatCurrency = formatNumber;

  // ===== Desglose de tarjeta consolidada =====
  // Detecta la liquidación mensual de la tarjeta: por la máscara del PAN
  // (ej: ************7982, importación Excel) o por el texto del banco vía
  // Enable Banking ("ADEUDO MENSUAL DE TARJETA // RECIBO TARJETA CRÉDITO // ...")
  const pareceTarjeta = (c) => {
    const concepto = (c?.concepto || '').trim();
    return /^\*+\d{4}$/.test(concepto)
      || /(ADEUDO\s+MENSUAL\s+DE\s+TARJETA|RECIBO\s+TARJETA\s+CR[EÉ]DITO)/i.test(concepto);
  };

  const abrirDesglose = (cargo) => {
    setDesgloseTarjeta({
      cargo,
      subcargos: cargo.subcargos || [],
      suma: null,
      diferencia: cargo.subcargos_diferencia ?? null,
      cuadrado: cargo.subcargos_cuadrados === true,
      guardando: false,
      preview: null,
    });
  };

  // Aplica el shape { subcargos, suma, diferencia, cuadrado } al modal y refresca la lista
  const aplicarShapeSubcargos = (data) => {
    setDesgloseTarjeta((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        subcargos: data.subcargos || [],
        suma: data.suma ?? null,
        diferencia: data.diferencia ?? null,
        cuadrado: data.cuadrado === true,
        cargo: {
          ...prev.cargo,
          subcargos: data.subcargos || [],
          subcargos_diferencia: data.diferencia ?? null,
          subcargos_cuadrados: data.cuadrado === true,
          vinculado: data.cuadrado === true ? true : prev.cargo.vinculado,
        },
      };
    });
    // Refrescar la lista de cargos (misma función usada tras vincular/marcar gasto directo)
    cargarCargosBancarios();
  };

  // Reemplaza un subcargo por el devuelto (mismo id) tras vincular/desvincular factura.
  // No toca suma/cuadre porque enlazar factura no cambia importes.
  const actualizarSubcargoEnModal = (subcargoActualizado) => {
    if (!subcargoActualizado) return;
    setDesgloseTarjeta((prev) => {
      if (!prev) return prev;
      const nuevos = (prev.subcargos || []).map((s) =>
        s.id === subcargoActualizado.id ? subcargoActualizado : s
      );
      return {
        ...prev,
        subcargos: nuevos,
        cargo: { ...prev.cargo, subcargos: nuevos },
      };
    });
    cargarCargosBancarios();
  };

  // Cargar categoría sugerida basada en proveedor
  const cargarCategoriaSugerida = async (proveedor) => {
    if (!proveedor || proveedor.length < 3) {
      setCategoriaSugerida(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/finanzas/proveedor/categoria-sugerida?proveedor=${encodeURIComponent(proveedor)}`, { headers });
      const data = await res.json();
      if (data.categoria_sugerida && data.confianza >= 50) {
        setCategoriaSugerida(data);
        // Auto-aplicar si la confianza es alta y no hay categoría ya establecida
        if (data.confianza >= 70 && datosFacturaIA && !datosFacturaIA.categoria) {
          setDatosFacturaIA(prev => ({...prev, categoria: data.categoria_sugerida}));
        }
      } else {
        setCategoriaSugerida(null);
      }
    } catch (e) {
      console.error('Error cargando categoría sugerida:', e);
    }
  };

  // Eliminar cargo bancario individual
  const eliminarCargo = async (cargoId, cargo) => {
    const concepto = cargo?.concepto?.substring(0, 50) || 'Sin concepto';
    const importe = cargo?.importe ? Math.abs(cargo.importe).toFixed(2) : '0';
    
    if (!window.confirm(`¿Eliminar este cargo?\n\nConcepto: ${concepto}\nImporte: €${importe}\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${cargoId}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Cargo eliminado correctamente');
        cargarDatosPestaña(); // Recargar la lista
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'No se pudo eliminar el cargo'));
      }
    } catch (error) {
      console.error('Error eliminando cargo:', error);
      alert('Error al eliminar el cargo');
    }
  };

  // Eliminar abono/ingreso
  const eliminarAbono = async (abonoId, abono) => {
    const concepto = abono?.concepto?.substring(0, 50) || 'Sin concepto';
    const importe = abono?.importe ? abono.importe.toFixed(2) : '0';
    
    // Verificar si está vinculado
    if (abono?.vinculado || abono?.factura_emitida_id || abono?.presupuesto_id) {
      alert('No se puede eliminar un abono vinculado.\n\nPrimero desvincúlelo usando el botón "Desvincular".');
      return;
    }
    
    if (!window.confirm(`¿Eliminar este ingreso?\n\nConcepto: ${concepto}\nImporte: €${importe}\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/abonos/${abonoId}`, {
        method: 'DELETE',
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'Ingreso eliminado correctamente');
        cargarAbonos(); // Recargar la lista de abonos
      } else {
        const error = await response.json();
        alert('Error: ' + (error.detail || 'No se pudo eliminar el ingreso'));
      }
    } catch (error) {
      console.error('Error eliminando abono:', error);
      alert('Error al eliminar el ingreso');
    }
  };

  // Eliminar factura de Drive
  const eliminarFacturaDrive = async (facturaId) => {
    if (!window.confirm('¿Eliminar esta factura? Los cargos vinculados se desvincularán.')) return;
    
    setEliminandoFactura(true);
    try {
      const res = await fetch(`${API_URL}/api/finanzas/drive/facturas/${facturaId}`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ ${data.mensaje}`);
        setShowProcesarFactura(false);
        setArchivoEnProceso(null);
        setDatosFacturaIA(null);
        cargarDatosPestaña();
      } else {
        alert(data.detail || 'Error eliminando factura');
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setEliminandoFactura(false);
    }
  };

  // Renderizar resumen global con selector de trimestre
  const renderResumenGlobal = () => {
    if (!resumenGlobal) return null;

    const trimestres = [
      { value: 1, label: '1T' },
      { value: 2, label: '2T' },
      { value: 3, label: '3T' },
      { value: 4, label: '4T' },
      { value: null, label: 'Anual' },
    ];
    
    return (
      <div className="space-y-3 mb-6">
        {/* Selector de trimestre */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="trimestre-selector">
          {trimestres.map(t => (
            <button
              key={t.label}
              data-testid={`trimestre-btn-${t.label}`}
              onClick={() => {
                setTrimestreSeleccionado(t.value);
                cargarResumenGlobal(t.value);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                trimestreSeleccionado === t.value
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t.label}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-2">
            {resumenGlobal.emitidas?.count || 0} emitidas · {resumenGlobal.soportadas?.count || 0} gastos
          </span>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Emitidas - Base Imponible */}
          <Card className="border-l-4 border-l-emerald-500 border-slate-200" data-testid="card-emitidas">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium mb-1">Facturado (Base Imp.)</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="emitidas-subtotal">{formatNumber(resumenGlobal.emitidas?.subtotal)}</p>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-400">IVA rep: {formatNumber(resumenGlobal.emitidas?.iva)}</span>
                <span className="text-xs text-emerald-600 font-medium">Total: {formatNumber(resumenGlobal.emitidas?.total)}</span>
              </div>
              {resumenGlobal.emitidas?.count_pendiente_cobro > 0 && (
                <div
                  className="mt-2 pt-2 border-t border-slate-200 flex justify-between items-center"
                  title="Importe total pendiente de cobrar de las facturas emitidas en el periodo. Se actualiza al vincular ingresos."
                  data-testid="facturas-sin-cobrar"
                >
                  <span className="text-xs text-amber-600 font-medium">
                    ⚠ Sin cobrar ({resumenGlobal.emitidas.count_pendiente_cobro})
                  </span>
                  <span className="text-xs text-amber-700 font-semibold">
                    {formatNumber(resumenGlobal.emitidas.pendiente_cobro)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Soportadas - Base Imponible */}
          <Card className="border-l-4 border-l-red-500 border-slate-200" data-testid="card-soportadas">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium mb-1">Gastos (Base Imp.)</p>
              <p className="text-xl font-bold text-red-700" data-testid="soportadas-subtotal">{formatNumber(resumenGlobal.soportadas?.subtotal)}</p>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-400">IVA sop: {formatNumber(resumenGlobal.soportadas?.iva)}</span>
                <span className="text-xs text-red-600 font-medium">Total: {formatNumber(resumenGlobal.soportadas?.total)}</span>
              </div>
              {resumenGlobal.cargos_pendientes?.count > 0 && (
                <div
                  className="mt-2 pt-2 border-t border-slate-200 flex justify-between items-center"
                  title="Cargos bancarios sin factura vinculada ni categoría estructural. No deducibles en 303."
                  data-testid="cargos-sin-vincular"
                >
                  <span className="text-xs text-amber-600 font-medium">
                    ⚠ Sin vincular ({resumenGlobal.cargos_pendientes.count})
                  </span>
                  <span className="text-xs text-amber-700 font-semibold">
                    {formatNumber(resumenGlobal.cargos_pendientes.importe)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Resultado Neto */}
          <Card className={`border-l-4 ${resumenGlobal.resultado_neto >= 0 ? 'border-l-blue-500' : 'border-l-orange-500'} border-slate-200`} data-testid="card-resultado">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium mb-1">
                {resumenGlobal.resultado_neto >= 0 ? 'Beneficio Neto' : 'Pérdida Neta'}
              </p>
              <p className={`text-xl font-bold ${resumenGlobal.resultado_neto >= 0 ? 'text-blue-700' : 'text-orange-700'}`} data-testid="resultado-neto">
                {formatNumber(resumenGlobal.resultado_neto)}
              </p>
              <p className="text-xs text-slate-400 mt-1.5">Base imponible emitidas - gastos</p>
            </CardContent>
          </Card>
          
          {/* Liquidación IVA */}
          <Card className={`border-l-4 ${resumenGlobal.liquidacion_iva >= 0 ? 'border-l-purple-500' : 'border-l-teal-500'} border-slate-200`} data-testid="card-iva">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 font-medium mb-1">
                IVA {resumenGlobal.liquidacion_iva >= 0 ? 'a Pagar' : 'a Compensar'}
              </p>
              <p className={`text-xl font-bold ${resumenGlobal.liquidacion_iva >= 0 ? 'text-purple-700' : 'text-teal-700'}`} data-testid="liquidacion-iva">
                {formatNumber(Math.abs(resumenGlobal.liquidacion_iva))}
              </p>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-slate-400">Rep: {formatNumber(resumenGlobal.iva_repercutido)}</span>
                <span className="text-xs text-slate-400">Sop: {formatNumber(resumenGlobal.iva_soportado)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // Función para eliminar factura emitida
  const handleEliminarFacturaEmitida = async (factura) => {
    if (!window.confirm(`¿Estás seguro de eliminar la factura #${extraerNumeroFactura(factura.numero_factura) || factura.numero}?\n\nEsto también actualizará el presupuesto vinculado.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/finanzas/facturas-emitidas/${factura.id}`, {
        method: 'DELETE',
        headers
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al eliminar');
      }
      
      const result = await response.json();
      alert(`✅ Factura eliminada correctamente${result.presupuesto_actualizado ? `\nPresupuesto #${result.presupuesto_actualizado} actualizado` : ''}`);
      
      // Recargar facturas
      cargarDatosPestaña();
    } catch (error) {
      alert('❌ Error: ' + error.message);
    }
  };

  // Renderizar tabla de facturas emitidas
  const renderTablaEmitidas = () => (
    <div className="overflow-x-auto">
      {facturasEmitidasSeleccionadas.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <span className="text-sm text-blue-800 font-medium">
            {facturasEmitidasSeleccionadas.size} factura{facturasEmitidasSeleccionadas.size !== 1 ? 's' : ''} seleccionada{facturasEmitidasSeleccionadas.size !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFacturasEmitidasSeleccionadas(new Set())}
            >
              Limpiar
            </Button>
            <Button
              size="sm"
              onClick={subirFacturasEmitidasA3docLote}
              disabled={subiendoA3docLote}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {subiendoA3docLote ? (
                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Subiendo {a3docLoteProgreso}...</>
              ) : (
                <><Cloud className="w-4 h-4 mr-1" /> Subir a a3doc</>
              )}
            </Button>
            <Button
              size="sm"
              onClick={descargarZipFacturasSeleccionadas}
              disabled={descargandoZip}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {descargandoZip ? (
                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Generando ZIP...</>
              ) : (
                <><Download className="w-4 h-4 mr-1" /> Descargar ZIP</>
              )}
            </Button>
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-3 text-center w-8">
              <input
                type="checkbox"
                aria-label="Seleccionar página"
                checked={(() => {
                  const page = facturasEmitidas.slice(facturasPage * facturasLimit, (facturasPage + 1) * facturasLimit);
                  return page.length > 0 && page.every(f => facturasEmitidasSeleccionadas.has(f.id));
                })()}
                onChange={(e) => {
                  const page = facturasEmitidas.slice(facturasPage * facturasLimit, (facturasPage + 1) * facturasLimit);
                  setFacturasEmitidasSeleccionadas(prev => {
                    const next = new Set(prev);
                    if (e.target.checked) page.forEach(f => next.add(f.id));
                    else page.forEach(f => next.delete(f.id));
                    return next;
                  });
                }}
              />
            </th>
            <th className="px-3 py-3 text-left font-medium text-gray-600">Factura</th>
            <th className="px-3 py-3 text-left font-medium text-gray-600">Cliente</th>
            <th className="px-2 py-3 text-left font-medium text-gray-600">Presup.</th>
            <th className="px-2 py-3 text-left font-medium text-gray-600">Fecha</th>
            <th className="px-2 py-3 text-right font-medium text-gray-600">Total</th>
            <th className="px-2 py-3 text-center font-medium text-gray-600">Estado</th>
            <th className="px-2 py-3 text-right font-medium text-gray-600">Pagado</th>
            <th className="px-2 py-3 text-center font-medium text-gray-600">a3doc</th>
            <th className="px-2 py-3 text-center font-medium text-gray-600">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {facturasEmitidas
            .slice(facturasPage * facturasLimit, (facturasPage + 1) * facturasLimit)
            .map((factura) => {
              const totalFactura = factura.importe_total || factura.total || 0;
              const pagado = factura.total_pagado || 0;
              const estadoPago = factura.estado_pago || (pagado >= totalFactura ? 'pagado' : pagado > 0 ? 'parcial' : 'pendiente');
              const esAnulada = factura.estado === 'anulada';
              const esRectificativa = (factura.tipo_factura || '').startsWith('R');

              return (
            <tr key={factura.id} className={`hover:bg-gray-50 ${esAnulada ? 'opacity-60' : ''}`}>
              <td className="px-2 py-3 text-center">
                <input
                  type="checkbox"
                  aria-label={`Seleccionar factura ${factura.numero_factura}`}
                  checked={facturasEmitidasSeleccionadas.has(factura.id)}
                  onChange={(e) => {
                    setFacturasEmitidasSeleccionadas(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(factura.id);
                      else next.delete(factura.id);
                      return next;
                    });
                  }}
                />
              </td>
              <td className="px-3 py-3 font-medium">
                {factura.presupuesto_id ? (
                  <a
                    href={`/presupuestos/${factura.presupuesto_id}?openFactura=true`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer inline-flex items-center gap-1"
                    data-testid={`factura-link-${factura.id}`}
                  >
                    {extraerNumeroFactura(factura.numero_factura) || factura.numero || '-'}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-blue-600">{extraerNumeroFactura(factura.numero_factura) || factura.numero || '-'}</span>
                )}
                {esRectificativa && (
                  <span
                    className="ml-1 inline-flex items-center text-[10px] font-medium text-purple-700 bg-purple-100 rounded px-1 py-0.5"
                    title={factura.factura_rectificada_numero ? `Rectifica a ${factura.factura_rectificada_numero}` : 'Factura rectificativa'}
                  >
                    Rectificativa
                  </span>
                )}
                {esAnulada && (
                  <span
                    className="ml-1 inline-flex items-center text-[10px] font-medium text-gray-600 bg-gray-200 rounded px-1 py-0.5"
                    title={factura.anulada_por_rectificativa ? `Anulada por ${factura.anulada_por_rectificativa}` : 'Anulada'}
                  >
                    Anulada
                  </span>
                )}
              </td>
              <td className="px-3 py-3 text-gray-700 max-w-[200px] truncate" title={factura.cliente_nombre || factura.cliente_razon_social || '-'}>
                {factura.cliente_nombre || factura.cliente_razon_social || '-'}
              </td>
              <td className="px-2 py-3">
                {factura.presupuesto_id ? (
                  <a
                    href={`/presupuestos/${factura.presupuesto_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer inline-flex items-center gap-1"
                    data-testid={`presupuesto-link-${factura.id}`}
                  >
                    {factura.numero_presupuesto || '-'}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-gray-500">{factura.numero_presupuesto || '-'}</span>
                )}
              </td>
              <td className="px-2 py-3 text-sm">{formatFecha(factura.fecha_factura)}</td>
              <td className={`px-2 py-3 text-right font-bold ${totalFactura < 0 ? 'text-red-600' : ''}`}>{formatNumber(totalFactura)}</td>
              <td className="px-2 py-3 text-center">
                {esAnulada ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                    Anulada
                  </span>
                ) : esRectificativa ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    Abono
                  </span>
                ) : (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    estadoPago === 'pagado' ? 'bg-green-100 text-green-800' :
                    estadoPago === 'parcial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {estadoPago === 'pagado' ? '✓ Pagado' : estadoPago === 'parcial' ? 'Parcial' : 'Pendiente'}
                  </span>
                )}
              </td>
              <td className="px-2 py-3 text-right text-sm">
                {pagado > 0 ? (
                  <span className="text-green-600">{formatNumber(pagado)}</span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-2 py-3 text-center">
                {factura.a3doc_document_id ? (
                  <span
                    title={factura.a3doc_subido_en ? `Subido a a3doc el ${formatFechaHora(factura.a3doc_subido_en)}` : 'Subido a a3doc'}
                    className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded px-1 py-0.5"
                  >
                    <CheckCircle2 className="w-3 h-3" /> a3doc
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Subir esta factura a a3doc"
                    disabled={subiendoA3docId === factura.id}
                    onClick={() => subirFacturaEmitidaA3doc(factura.id)}
                    className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 text-[10px] px-1.5 py-0.5 h-auto"
                  >
                    {subiendoA3docId === factura.id
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <><Cloud className="w-3 h-3 mr-0.5" /> Subir</>}
                  </Button>
                )}
              </td>
              <td className="px-2 py-3">
                <div className="flex items-center justify-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Descargar PDF"
                    onClick={() => descargarFactura(factura.id, factura.numero_factura)}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost" 
                    size="sm"
                    title="Eliminar factura"
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    onClick={() => handleEliminarFacturaEmitida(factura)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
              );
            })}
        </tbody>
      </table>
      {facturasEmitidas.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No hay facturas emitidas para el año {añoSeleccionado}
        </div>
      )}
      
      {/* Paginación Facturas Emitidas */}
      {facturasEmitidas.length > 0 && (
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Mostrar:</span>
            <select
              value={facturasLimit}
              onChange={(e) => {
                setFacturasLimit(Number(e.target.value));
                setFacturasPage(0);
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-500">
              de {facturasEmitidas.length} registros
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={facturasPage === 0}
              onClick={() => setFacturasPage(p => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-gray-600">
              Página {facturasPage + 1} de {Math.ceil(facturasEmitidas.length / facturasLimit) || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={(facturasPage + 1) * facturasLimit >= facturasEmitidas.length}
              onClick={() => setFacturasPage(p => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Renderizar tabla de facturas soportadas
  const renderTablaSoportadas = () => {
    // Los cargos ya vienen filtrados desde el servidor (paginación server-side)
    const gastosFiltrados = cargosBancarios;
    
    // Usar contadores del servidor
    const vinculados = cargosVinculadosCount;
    const pendientes = cargosPendientesCount;
    
    // Calcular totales de IVA y base de los cargos de esta página
    const totalFiltrado = gastosFiltrados.reduce((sum, c) => sum + (c.importe || 0), 0);
    const facturasDeCargo = (c) => (c.facturas && c.facturas.length > 0) ? c.facturas : (c.factura ? [c.factura] : []);
    const totalBase = gastosFiltrados.reduce((sum, c) => sum + facturasDeCargo(c).reduce((s, f) => s + (f.importe_base || 0), 0), 0);
    const totalIVA = gastosFiltrados.reduce((sum, c) => sum + facturasDeCargo(c).reduce((s, f) => s + (f.importe_iva || 0), 0), 0);
    
    // Función para descargar listado filtrado a Excel
    const descargarListado = async () => {
      try {
        // Construir URL con filtros
        const params = new URLSearchParams();
        if (filtros.estado) params.append('estado', filtros.estado);
        if (filtros.categoria) params.append('categoria', filtros.categoria);
        if (filtros.fechaDesde) params.append('fecha_desde', filtros.fechaDesde);
        if (filtros.fechaHasta) params.append('fecha_hasta', filtros.fechaHasta);
        if (filtros.proveedor) params.append('proveedor', filtros.proveedor);
        if (filtros.importeMin) params.append('importe_min', filtros.importeMin);
        if (filtros.importeMax) params.append('importe_max', filtros.importeMax);
        if (filtros.concepto) params.append('concepto', filtros.concepto);
        
        const url = `${API_URL}/api/finanzas/cargos-bancarios/exportar-excel?${params.toString()}`;
        
        // Usar XMLHttpRequest para manejar blob
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'blob';
        
        xhr.onload = function() {
          if (xhr.status === 200) {
            const blob = xhr.response;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Obtener nombre del archivo del header o usar uno por defecto
            const contentDisposition = xhr.getResponseHeader('Content-Disposition');
            let filename = 'cargos_bancarios.xlsx';
            if (contentDisposition) {
              const match = contentDisposition.match(/filename=(.+)/);
              if (match) filename = match[1];
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
          } else {
            alert('Error al descargar el archivo');
          }
        };
        
        xhr.onerror = function() {
          alert('Error de conexión al descargar');
        };
        
        xhr.send();
      } catch (e) {
        console.error('Error descargando:', e);
        alert('Error al descargar: ' + e.message);
      }
    };
    
    return (
      <div>
        {/* Resumen rápido arriba */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-100" onClick={() => setFiltros({...filtros, estado: ''})}>
            <p className="text-xs text-gray-500">Total Cargos</p>
            <p className="text-lg font-bold text-gray-800">{cargosTotalCount}</p>
          </div>
          <div className={`rounded-lg p-3 text-center cursor-pointer hover:bg-green-100 ${filtros.estado === 'vinculado' ? 'bg-green-200 ring-2 ring-green-500' : 'bg-green-50'}`} onClick={() => setFiltros({...filtros, estado: filtros.estado === 'vinculado' ? '' : 'vinculado'})}>
            <p className="text-xs text-green-600">Vinculados</p>
            <p className="text-lg font-bold text-green-700">{vinculados}</p>
          </div>
          <div className={`rounded-lg p-3 text-center cursor-pointer hover:bg-yellow-100 ${filtros.estado === 'pendiente' ? 'bg-yellow-200 ring-2 ring-yellow-500' : 'bg-yellow-50'}`} onClick={() => setFiltros({...filtros, estado: filtros.estado === 'pendiente' ? '' : 'pendiente'})}>
            <p className="text-xs text-yellow-600">Pendientes</p>
            <p className="text-lg font-bold text-yellow-700">{pendientes}</p>
            {comprasTarjetaSinFactura > 0 && (
              <p className="text-[10px] text-yellow-700" title="Compras dentro de los adeudos de tarjeta que aún no tienen factura">
                {comprasTarjetaSinFactura} {comprasTarjetaSinFactura === 1 ? 'compra de tarjeta sin factura' : 'compras de tarjeta sin factura'}
              </p>
            )}
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <p className="text-xs text-red-600">Total IVA Deducible</p>
            <p className="text-lg font-bold text-red-700">{formatNumber(totalIVA)}</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtros</span>
              {(filtros.categoria || filtros.proveedor || filtros.importeMin || filtros.importeMax || filtros.concepto || filtros.fechaDesde || filtros.fechaHasta || filtros.estado || filtros.a3doc) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setFiltros({ categoria: '', proveedor: '', importeMin: '', importeMax: '', concepto: '', fechaDesde: '', fechaHasta: '', estado: '', a3doc: '' })}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  <X className="w-3 h-3 mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={descargarListado}
              className="text-xs bg-green-50 border-green-300 hover:bg-green-100"
            >
              <Download className="w-3 h-3 mr-1" />
              Exportar Excel ({cargosTotalCount})
            </Button>
          </div>

          {/* Accesos rápidos de filtrado */}
          {(() => {
            const trimestres = [
              { id: 'T1', desde: `${añoSeleccionado}-01-01`, hasta: `${añoSeleccionado}-03-31` },
              { id: 'T2', desde: `${añoSeleccionado}-04-01`, hasta: `${añoSeleccionado}-06-30` },
              { id: 'T3', desde: `${añoSeleccionado}-07-01`, hasta: `${añoSeleccionado}-09-30` },
              { id: 'T4', desde: `${añoSeleccionado}-10-01`, hasta: `${añoSeleccionado}-12-31` },
            ];
            const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((nombre, i) => {
              const mm = String(i + 1).padStart(2, '0');
              const ultimoDia = new Date(añoSeleccionado, i + 1, 0).getDate();
              return { nombre, desde: `${añoSeleccionado}-${mm}-01`, hasta: `${añoSeleccionado}-${mm}-${ultimoDia}` };
            });
            return (
              <>
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="text-xs text-gray-400 mr-1">Trimestre:</span>
                  {trimestres.map(t => {
                    const activo = filtros.fechaDesde === t.desde && filtros.fechaHasta === t.hasta;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setFiltros({ ...filtros, fechaDesde: activo ? '' : t.desde, fechaHasta: activo ? '' : t.hasta })}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${activo ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                      >
                        {t.id} {añoSeleccionado}
                      </button>
                    );
                  })}
                  <span className="w-px h-4 bg-gray-300 mx-1" />
                  <button
                    onClick={() => setFiltros({ ...filtros, a3doc: filtros.a3doc === 'sin_subir' ? '' : 'sin_subir' })}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${filtros.a3doc === 'sin_subir' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'}`}
                  >
                    Sin a3doc
                  </button>
                  <button
                    onClick={() => setFiltros({ ...filtros, a3doc: filtros.a3doc === 'subido' ? '' : 'subido' })}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${filtros.a3doc === 'subido' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-300 hover:bg-green-50'}`}
                  >
                    En a3doc
                  </button>
                  <span className="w-px h-4 bg-gray-300 mx-1" />
                  <button
                    onClick={abrirIncidenciasPanel}
                    className="text-xs px-2.5 py-1 rounded-full border transition bg-white text-amber-800 border-amber-300 hover:bg-amber-50 inline-flex items-center gap-1"
                    title="Ver incidencias de facturas del periodo seleccionado"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    Incidencias{incidenciasAbiertas > 0 ? ` (${incidenciasAbiertas})` : ''}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                  <span className="text-xs text-gray-400 mr-1">Mes:</span>
                  {meses.map(m => {
                    const activo = filtros.fechaDesde === m.desde && filtros.fechaHasta === m.hasta;
                    return (
                      <button
                        key={m.nombre}
                        onClick={() => setFiltros({ ...filtros, fechaDesde: activo ? '' : m.desde, fechaHasta: activo ? '' : m.hasta })}
                        className={`text-xs px-2 py-1 rounded-full border transition ${activo ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                      >
                        {m.nombre}
                      </button>
                    );
                  })}
                </div>
              </>
            );
          })()}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {/* Filtro Categoría */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Categoría</label>
              <Select value={filtros.categoria || 'todas'} onValueChange={(v) => setFiltros({...filtros, categoria: v === 'todas' ? '' : v})}>
                <SelectTrigger className="h-8 text-xs bg-white">
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent className="bg-white border shadow-lg z-50 max-h-60">
                  <SelectItem value="todas">Todas</SelectItem>
                  {CATEGORIAS_GASTO.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Filtro Proveedor */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Proveedor</label>
              <Input 
                placeholder="Buscar..." 
                value={filtros.proveedor}
                onChange={(e) => setFiltros({...filtros, proveedor: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
            
            {/* Filtro Concepto */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Concepto</label>
              <Input 
                placeholder="Buscar..." 
                value={filtros.concepto}
                onChange={(e) => setFiltros({...filtros, concepto: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
            
            {/* Filtro Importe Mínimo */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Importe mín.</label>
              <Input 
                type="number"
                placeholder="0.00" 
                value={filtros.importeMin}
                onChange={(e) => setFiltros({...filtros, importeMin: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
            
            {/* Filtro Importe Máximo */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Importe máx.</label>
              <Input 
                type="number"
                placeholder="0.00" 
                value={filtros.importeMax}
                onChange={(e) => setFiltros({...filtros, importeMax: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
            
            {/* Filtro Fecha Desde */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Desde</label>
              <Input 
                type="date"
                value={filtros.fechaDesde}
                onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
            
            {/* Filtro Fecha Hasta */}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Hasta</label>
              <Input 
                type="date"
                value={filtros.fechaHasta}
                onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
                className="h-8 text-xs"
              />
            </div>
          </div>
        </div>

        {/* Barra de acción para subida en lote a a3doc */}
        {cargosSelA3doc.size > 0 && (
          <div className="mb-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            <span className="text-sm text-amber-800 font-medium">
              {cargosSelA3doc.size} cargo{cargosSelA3doc.size !== 1 ? 's' : ''} seleccionado{cargosSelA3doc.size !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setCargosSelA3doc(new Set())}>Limpiar</Button>
              <Button
                size="sm"
                onClick={subirCargosA3docLote}
                disabled={subiendoA3docLote}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {subiendoA3docLote
                  ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Subiendo {a3docLoteProgreso}...</>
                  : <><Cloud className="w-4 h-4 mr-1" /> Subir a a3doc</>}
              </Button>
            </div>
          </div>
        )}

        {/* Tabla rediseñada - compacta */}
        <div className="overflow-x-auto max-h-[500px] border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-center w-8">
                  <input
                    type="checkbox"
                    aria-label="Seleccionar cargos con factura para a3doc"
                    checked={(() => {
                      const elegibles = gastosFiltrados.filter(c => c.factura_id && c.factura && (c.facturas || (c.factura ? [c.factura] : [])).some(f => !f.a3doc_document_id));
                      return elegibles.length > 0 && elegibles.every(c => cargosSelA3doc.has(c.id));
                    })()}
                    onChange={(e) => {
                      const elegibles = gastosFiltrados.filter(c => c.factura_id && c.factura && (c.facturas || (c.factura ? [c.factura] : [])).some(f => !f.a3doc_document_id));
                      setCargosSelA3doc(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) elegibles.forEach(c => next.add(c.id));
                        else elegibles.forEach(c => next.delete(c.id));
                        return next;
                      });
                    }}
                  />
                </th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700">PROVEEDOR</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">F. CARGO</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 whitespace-nowrap">F. FACT.</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700">CAT.</th>
                <th className="px-2 py-2 text-right font-semibold text-red-700">IMPORTE</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-700" style={{maxWidth: '180px'}}>CONCEPTO</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 whitespace-nowrap">BASE/IVA</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-600 whitespace-nowrap">a3doc</th>
                <th className="px-2 py-2 text-center font-semibold text-gray-700 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gastosFiltrados.map((cargo) => {
                const tieneFactura = !!cargo.factura_id && cargo.factura;
                const facturasVinculadas = (cargo.facturas && cargo.facturas.length > 0)
                  ? cargo.facturas
                  : (tieneFactura ? [cargo.factura] : []);
                const esGastoDirecto = cargo.es_gasto_directo === true;
                // Cargo vinculado a una operación de deuda/financiación
                const esFinanciacion = !!cargo.operacion_financiacion_id;
                // Marcado como vinculado (p. ej. abono a financiación) sin factura ni gasto directo
                const esVinculadoSinFactura = cargo.vinculado === true && !tieneFactura && !esGastoDirecto;
                const estaProcesado = tieneFactura || esGastoDirecto || esFinanciacion || esVinculadoSinFactura;
                const esVinculacionAuto = cargo.vinculacion_automatica === true;
                const esTarjetaConsolidada = cargo.es_consolidado_tarjeta === true;
                const numSubcargos = (cargo.subcargos || []).length;
                const { proveedor: conceptoProveedor, tarjeta, detalle: conceptoDetalle } = limpiarConcepto(cargo.concepto, cargo.tarjeta);
                const catInfo = CATEGORIAS_GASTO.find(c => c.value === (cargo.factura?.categoria || cargo.categoria)) || 
                               { value: 'desconocido', label: 'Desconocido', color: 'bg-gray-100 text-gray-800' };
                
                return (
                  <React.Fragment key={cargo.id}>
                  <tr className={`hover:bg-gray-50 ${estaProcesado ? (esGastoDirecto ? 'bg-blue-50' : (esFinanciacion || esVinculadoSinFactura ? 'bg-purple-50' : (esVinculacionAuto ? 'bg-green-50' : 'bg-white'))) : 'bg-yellow-50'}`}>
                    {/* SELECCIÓN a3doc */}
                    <td className="px-2 py-1.5 text-center">
                      {tieneFactura && (cargo.facturas || (cargo.factura ? [cargo.factura] : [])).some(f => !f.a3doc_document_id) ? (
                        <input
                          type="checkbox"
                          aria-label="Seleccionar para a3doc"
                          checked={cargosSelA3doc.has(cargo.id)}
                          onChange={(e) => setCargosSelA3doc(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(cargo.id); else next.delete(cargo.id);
                            return next;
                          })}
                        />
                      ) : null}
                    </td>
                    {/* PROVEEDOR */}
                    <td className="px-2 py-1.5">
                      {esTarjetaConsolidada && numSubcargos > 0 && (
                        <button
                          onClick={() => toggleCargoExpandido(cargo.id)}
                          className="mr-1 text-gray-400 hover:text-gray-700 align-middle"
                          title={expandedCargos.has(cargo.id) ? 'Ocultar compras' : 'Ver compras'}
                          data-testid={`toggle-subcargos-${cargo.id}`}
                        >
                          {expandedCargos.has(cargo.id) ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}
                        </button>
                      )}
                      {tieneFactura ? (
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-gray-900 text-xs">{cargo.factura.proveedor_nombre}</span>
                          {facturasVinculadas.length > 1 && (
                            <Badge
                              className="bg-purple-100 text-purple-700 text-[10px] px-1 py-0"
                              title={facturasVinculadas.map(f => `${f.numero_factura || 's/n'} · ${formatNumber(f.importe_total)}`).join('\n')}
                            >
                              {facturasVinculadas.length} fact.
                            </Badge>
                          )}
                          {esVinculacionAuto && (
                            <span title={`Vinculado automáticamente (${cargo.match_score || '-'}% match)`} className="text-green-500">
                              <Zap className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      ) : esGastoDirecto ? (
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-blue-700 text-xs truncate max-w-[120px]">
                            {conceptoProveedor || 'Gasto Directo'}
                          </span>
                          <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1 py-0">S/F</Badge>
                        </div>
                      ) : esTarjetaConsolidada ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <Badge className="bg-slate-100 text-slate-700 text-[10px] px-1 py-0">
                            Tarjeta{cargo.tarjeta ? ' ·' + cargo.tarjeta : ''}
                          </Badge>
                          {numSubcargos === 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1 py-0">Sin desglosar</Badge>
                          ) : cargo.subcargos_cuadrados !== true ? (
                            <Badge className="bg-orange-100 text-orange-700 text-[10px] px-1 py-0">
                              No cuadra ({formatNumber(cargo.subcargos_diferencia)})
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">{numSubcargos} compras</Badge>
                          )}
                        </div>
                      ) : esFinanciacion ? (
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-purple-700 text-xs truncate max-w-[120px]">
                            {conceptoProveedor || 'Financiación'}
                          </span>
                          <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1 py-0">Financiación</Badge>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic text-xs">-</span>
                      )}
                    </td>
                    
                    {/* FECHA CARGO */}
                    <td className="px-2 py-1.5 text-center text-gray-600 whitespace-nowrap">
                      {formatFecha(cargo.fecha)}
                    </td>
                    
                    {/* FECHA FACTURA */}
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {tieneFactura ? (
                        <span className="text-gray-900">{formatFecha(cargo.factura.fecha_factura)}</span>
                      ) : esGastoDirecto ? (
                        <span className="text-blue-600">{formatFecha(cargo.fecha)}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    
                    {/* CATEGORÍA */}
                    <td className="px-2 py-1.5 text-center">
                      {tieneFactura ? (
                        <Badge className={`${catInfo.color} text-[10px] px-1.5 py-0.5`}>{catInfo.label}</Badge>
                      ) : esGastoDirecto ? (
                        <Badge className={`${catInfo.color} text-[10px] px-1.5 py-0.5`}>{catInfo.label}</Badge>
                      ) : cargo.categoria ? (
                        <button
                          onClick={() => {
                            setCargoSeleccionado(cargo);
                            setShowEditarCategoria(true);
                          }}
                          className="cursor-pointer"
                        >
                          <Badge className={`${catInfo.color} text-[10px] px-1.5 py-0.5 cursor-pointer hover:opacity-80`}>
                            {catInfo.label}
                            <Edit className="w-2.5 h-2.5 ml-0.5 opacity-50" />
                          </Badge>
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            setCargoSeleccionado(cargo);
                            setShowEditarCategoria(true);
                          }}
                          className="cursor-pointer"
                        >
                          <Badge className="bg-gray-200 text-gray-600 cursor-pointer hover:bg-gray-300 text-[10px] px-1.5 py-0.5">
                            Sin cat.
                            <Edit className="w-2.5 h-2.5 ml-0.5 opacity-50" />
                          </Badge>
                        </button>
                      )}
                    </td>
                    
                    {/* IMPORTE CARGO */}
                    <td className="px-2 py-1.5 text-right">
                      <span className="font-semibold text-red-600">{formatNumber(Math.abs(cargo.importe))}</span>
                    </td>
                    
                    {/* CONCEPTO */}
                    <td className="px-2 py-1.5" style={{maxWidth: '180px'}}>
                      <div className="truncate text-gray-800 font-medium" title={cargo.concepto}>
                        {conceptoProveedor || conceptoDetalle}
                      </div>
                      {conceptoProveedor && conceptoDetalle && (
                        <div className="truncate text-[10px] text-gray-500" title={cargo.concepto}>{conceptoDetalle}</div>
                      )}
                      {(tarjeta || cargo.empleado_tarjeta) && (
                        <div className="text-[10px] text-gray-400">
                          {tarjeta && <span>****{tarjeta}</span>}
                          {cargo.empleado_tarjeta && (
                            <span className="ml-1 text-gray-500">({cargo.empleado_tarjeta})</span>
                          )}
                        </div>
                      )}
                    </td>
                    
                    {/* DATOS FACTURA (si vinculada) */}
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {tieneFactura ? (
                        <div className="text-[10px]">
                          <span className="text-gray-600">{formatNumber(facturasVinculadas.reduce((s, f) => s + (f.importe_base || 0), 0))}</span>
                          <span className="text-red-500 ml-1">+{formatNumber(facturasVinculadas.reduce((s, f) => s + (f.importe_iva || 0), 0))}</span>
                        </div>
                      ) : esGastoDirecto ? (
                        <div className="text-[10px]">
                          <span className="text-blue-600">{formatNumber(Math.abs(cargo.importe))}</span>
                          <span className="text-gray-400 ml-1">+0€</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    
                    {/* a3doc (estado de subida) */}
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {tieneFactura && facturasVinculadas.every(f => f.a3doc_document_id) ? (
                        <span
                          title={cargo.factura.a3doc_subido_en ? `Subido a a3doc el ${formatFechaHora(cargo.factura.a3doc_subido_en)}` : 'Subido a a3doc'}
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded px-1 py-0.5"
                        >
                          <CheckCircle2 className="w-3 h-3" /> a3doc{facturasVinculadas.length > 1 ? ` ${facturasVinculadas.length}/${facturasVinculadas.length}` : ''}
                        </span>
                      ) : tieneFactura && facturasVinculadas.length > 1 && facturasVinculadas.some(f => f.a3doc_document_id) ? (
                        <span
                          title="Algunas facturas del cargo aún no están en a3doc"
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 rounded px-1 py-0.5"
                        >
                          <Cloud className="w-3 h-3" /> {facturasVinculadas.filter(f => f.a3doc_document_id).length}/{facturasVinculadas.length}
                        </span>
                      ) : tieneFactura ? (
                        <span title="Aún no subido a a3doc" className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
                          <Cloud className="w-3 h-3" /> –
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    
                    {/* ACCIONES */}
                    <td className="px-2 py-1.5 text-center">
                      {tieneFactura ? (
                        <div className="flex justify-center gap-0.5">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setCargoSeleccionado(cargo);
                              setShowEditarFacturaVinculada(true);
                            }}
                            title="Editar factura vinculada"
                          >
                            <Edit className="w-3.5 h-3.5 text-gray-600" />
                          </Button>
                          {cargo.factura.drive_link && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => window.open(cargo.factura.drive_link, '_blank')}
                              title="Ver factura en Drive"
                            >
                              <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                          )}
                        </div>
                      ) : esGastoDirecto ? (
                        <span className="text-green-600 text-[10px]">✓</span>
                      ) : (
                        <div className="flex justify-center gap-0.5">
                          {(esTarjetaConsolidada || pareceTarjeta(cargo)) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              title="Desglosar compras de tarjeta"
                              data-testid={'btn-desglose-' + cargo.id}
                              onClick={() => abrirDesglose(cargo)}
                            >
                              <CreditCard className="w-3.5 h-3.5 text-slate-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            title="Vincular factura"
                            onClick={() => {
                              setCargoSeleccionado(cargo);
                              setShowVincularFactura(true);
                            }}
                          >
                            <Link2 className="w-3.5 h-3.5 text-yellow-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 w-6 p-0"
                            title="Gasto directo (sin factura)"
                            onClick={() => {
                              setCargoSeleccionado(cargo);
                              setShowMarcarGastoDirecto(true);
                            }}
                          >
                            <Receipt className="w-3.5 h-3.5 text-purple-500" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-red-100"
                            title="Eliminar cargo"
                            onClick={() => eliminarCargo(cargo.id, cargo)}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedCargos.has(cargo.id) && (cargo.subcargos || []).map((sub) => {
                    const vinculadoSub = !!sub.factura_drive_id;
                    const catSub = CATEGORIAS_GASTO.find(c => c.value === sub.categoria);
                    const facSub = sub.factura || null;  // datos frescos de la factura enlazada
                    const numSub = facSub?.numero_factura || sub.factura_numero;
                    const baseSub = facSub?.importe_base ?? sub.importe_base;
                    const ivaSub = facSub?.importe_iva ?? sub.importe_iva;
                    const enA3docSub = !!facSub?.a3doc_document_id;
                    return (
                      <tr key={`${cargo.id}-${sub.id}`} className="bg-slate-50/70 text-xs border-l-2 border-slate-300">
                        <td className="px-2 py-1"></td>
                        <td className="px-2 py-1 pl-6">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-gray-700 truncate max-w-[140px]">{vinculadoSub ? (sub.factura_proveedor || sub.proveedor || sub.concepto) : (sub.proveedor || sub.concepto)}</span>
                            {vinculadoSub
                              ? <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">Vinculado</Badge>
                              : <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1 py-0">Sin factura</Badge>}
                            {vinculadoSub && numSub && <span className="text-[10px] text-gray-400">nº {numSub}</span>}
                          </div>
                        </td>
                        <td className="px-2 py-1 text-center text-gray-500 whitespace-nowrap">{sub.fecha_compra ? formatFecha(sub.fecha_compra) : '-'}</td>
                        <td className="px-2 py-1 text-center text-gray-500 whitespace-nowrap">{facSub?.fecha_factura ? formatFecha(facSub.fecha_factura) : <span className="text-gray-300">-</span>}</td>
                        <td className="px-2 py-1 text-center">{catSub ? <Badge className={`${catSub.color} text-[10px] px-1 py-0`}>{catSub.label}</Badge> : <span className="text-gray-300">-</span>}</td>
                        <td className="px-2 py-1 text-right font-medium text-red-600 whitespace-nowrap">{formatNumber(sub.importe)}</td>
                        <td className="px-2 py-1 text-gray-600 truncate" style={{maxWidth: '180px'}}>{sub.concepto}</td>
                        <td className="px-2 py-1 text-right whitespace-nowrap">
                          {vinculadoSub && (baseSub != null || ivaSub != null) ? (
                            <div className="text-[10px]">
                              <span className="text-gray-600">{formatNumber(baseSub || 0)}</span>
                              <span className="text-red-500 ml-1">+{formatNumber(ivaSub || 0)}</span>
                            </div>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-2 py-1 text-center whitespace-nowrap">
                          {vinculadoSub ? (
                            enA3docSub ? (
                              <span title={facSub?.a3doc_subido_en ? `Subido a a3doc el ${formatFechaHora(facSub.a3doc_subido_en)}` : 'Subido a a3doc'} className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded px-1 py-0.5">
                                <CheckCircle2 className="w-3 h-3" /> a3doc
                              </span>
                            ) : (
                              <span title="Aún no subido a a3doc" className="inline-flex items-center gap-0.5 text-[10px] text-gray-400">
                                <Cloud className="w-3 h-3" /> –
                              </span>
                            )
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {facSub?.drive_link && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => window.open(facSub.drive_link, '_blank')} title="Ver factura en Drive">
                              <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {gastosFiltrados.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No hay gastos que coincidan con los filtros</p>
          </div>
        )}
        
        {/* Totales al final */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total filtrado: <span className="font-bold">{formatNumber(totalFiltrado)}</span></span>
            <span className="text-sm text-gray-600">Base total: <span className="font-bold">{formatNumber(totalBase)}</span></span>
            <span className="text-sm text-red-600">IVA total: <span className="font-bold">{formatNumber(totalIVA)}</span></span>
          </div>
        </div>
        
        {/* Paginación */}
        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Mostrar:</span>
            <select
              value={cargosLimit}
              onChange={(e) => {
                setCargosLimit(Number(e.target.value));
                setCargosPage(0);
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-500">
              de {cargosTotalCount} registros
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={cargosPage === 0}
              onClick={() => setCargosPage(p => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-gray-600">
              Página {cargosPage + 1} de {Math.ceil(cargosTotalCount / cargosLimit) || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={(cargosPage + 1) * cargosLimit >= cargosTotalCount}
              onClick={() => setCargosPage(p => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar resúmenes mensuales/trimestrales
  const renderResumenes = (tipo) => {
    const mensual = tipo === 'emitidas' ? resumenMensualEmitidas : resumenMensualSoportadas;
    const trimestral = tipo === 'emitidas' ? resumenTrimestralEmitidas : resumenTrimestralSoportadas;
    const colorBase = tipo === 'emitidas' ? 'green' : 'red';
    
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Resumen Mensual */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Resumen Mensual</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {mensual.map((mes) => (
                <div key={mes.mes} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">{mes.nombre_mes?.substring(0, 3)}</span>
                  <span className={`font-medium ${mes.subtotal > 0 ? `text-${colorBase}-600` : 'text-gray-400'}`}>
                    {formatNumber(mes.subtotal)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        {/* Resumen Trimestral */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Resumen Trimestral (Modelo 303)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trimestral.map((trim) => (
                <div key={trim.trimestre} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{trim.nombre}</span>
                    <span className="text-xs text-gray-500">{trim.meses}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Base: </span>
                      <span className="font-medium">{formatNumber(trim.subtotal)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">IVA: </span>
                      <span className={`font-medium text-${colorBase}-600`}>{formatNumber(trim.total_iva)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Renderizar gráfico de gastos por categoría
  const renderGastosPorCategoria = () => {
    const total = gastosPorCategoria.reduce((sum, cat) => sum + cat.total, 0);
    const colors = ['#f97316', '#8b5cf6', '#3b82f6', '#22c55e', '#eab308', '#ef4444', '#6366f1', '#06b6d4', '#ec4899', '#6b7280'];
    
    return (
      <Card className="mt-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Gastos por Categoría</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-8">
            {/* Gráfico de barras horizontal simple */}
            <div className="flex-1 space-y-2">
              {gastosPorCategoria.map((cat, idx) => {
                const percentage = total > 0 ? (cat.total / total) * 100 : 0;
                return (
                  <div key={cat.categoria} className="flex items-center gap-3">
                    <span className="w-24 text-sm text-gray-600 truncate">{cat.nombre}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-4">
                      <div 
                        className="h-4 rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: colors[idx % colors.length]
                        }}
                      />
                    </div>
                    <span className="w-24 text-sm font-medium text-right">{formatNumber(cat.total)}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {gastosPorCategoria.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              No hay gastos registrados para el año {añoSeleccionado}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finanzas</h1>
          <p className="text-gray-500">Gestión de facturas emitidas y soportadas</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Selector de año */}
          <Select value={String(añoSeleccionado)} onValueChange={(v) => setAñoSeleccionado(Number(v))}>
            <SelectTrigger className="w-32">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2023, 2024, 2025, 2026].map((año) => (
                <SelectItem key={año} value={String(año)}>{año}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Resumen Global */}
      {renderResumenGlobal()}

      {/* Banner de facturas posible-duplicado pendientes de revisar */}
      {duplicadosPendientes.length > 0 && (
        <div
          className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => setShowDuplicadosModal(true)}
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">
                {duplicadosPendientes.length} factura{duplicadosPendientes.length === 1 ? '' : 's'} marcada{duplicadosPendientes.length === 1 ? '' : 's'} como posible duplicado
              </p>
              <p className="text-amber-700 text-xs mt-0.5">
                No están contando en el IVA hasta que confirmes si son duplicadas o facturas distintas. Clic para revisar.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-200">
            Revisar
          </Button>
        </div>
      )}

      {/* Tabs - Estilo moderno pill */}
      <div>
        <div className="w-full overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex flex-nowrap gap-1 p-1 bg-slate-100 rounded-xl min-w-max">
            {[
              { id: 'rentabilidad', icon: <Target className="w-3.5 h-3.5" />, label: 'Rentabilidad' },
              { id: 'emitidas', icon: <TrendingUp className="w-3.5 h-3.5" />, label: 'Emitidas' },
              { id: 'banco', icon: <Building2 className="w-3.5 h-3.5" />, label: 'Banco' },
              { id: 'pendientes', icon: <AlertCircle className="w-3.5 h-3.5" />, label: 'Pendientes' },
              { id: 'fiscal', icon: <Calculator className="w-3.5 h-3.5" />, label: 'Fiscal' },
              { id: 'gastos-fijos', icon: <Repeat className="w-3.5 h-3.5" />, label: 'Fijos' },
              { id: 'nominas', icon: <Banknote className="w-3.5 h-3.5" />, label: 'Nóminas' },
              { id: 'prestamos', icon: <CreditCard className="w-3.5 h-3.5" />, label: 'Deudas' },
              { id: 'automatizacion', icon: <FolderSync className="w-3.5 h-3.5" />, label: 'Drive' },
              { id: 'a3doc', icon: <FileText className="w-3.5 h-3.5" />, label: 'a3doc' },
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  (tab.id === 'banco' ? ['banco', 'soportadas', 'ingresos'].includes(activeTab) : activeTab === tab.id)
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Subtabs de Banco: Movimientos / Cargos / Ingresos */}
        {['banco', 'soportadas', 'ingresos'].includes(activeTab) && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {[
              { id: 'banco', label: 'Movimientos' },
              { id: 'soportadas', label: 'Cargos' },
              { id: 'ingresos', label: 'Ingresos' },
            ].map(st => (
              <button
                key={st.id}
                onClick={() => setActiveTab(st.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  activeTab === st.id
                    ? 'bg-duetto-primary text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        )}


        {/* Tab Resumen (vista por defecto) */}
        {activeTab === 'resumen' && (
          <div className="space-y-6 mt-6" data-testid="resumen-panel">
            {cargandoResumen && !resumenDashboard && (
              <div className="text-center py-12 text-slate-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Cargando resumen…
              </div>
            )}
            {resumenDashboard && (
              <>
                {/* Header con año y actualización */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Resumen financiero {resumenDashboard.anio}</h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Una vista rápida de todo · Actualizado {resumenDashboard.actualizado_en ? new Date(resumenDashboard.actualizado_en).toLocaleTimeString('es-ES', {hour:'2-digit', minute:'2-digit'}) : '—'}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cargarResumenDashboard}
                    disabled={cargandoResumen}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${cargandoResumen ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </div>

                {/* Fila 1: Rentabilidad (mes + año) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const m = resumenDashboard.rentabilidad?.mes || {};
                    const a = resumenDashboard.rentabilidad?.anio || {};
                    const resultadoMes = m.resultado || 0;
                    const resultadoAnio = a.resultado || 0;
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => setActiveTab('rentabilidad')}
                          className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Resultado mes en curso</p>
                            <Calendar className="w-4 h-4 text-slate-400" />
                          </div>
                          <p className={`text-3xl font-bold ${resultadoMes >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {resultadoMes >= 0 ? '+' : ''}{formatCurrency(resultadoMes)}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Ingresos <b className="text-slate-700">{formatCurrency(m.ingresos || 0)}</b></span>
                            <span>Gastos <b className="text-slate-700">{formatCurrency(m.gastos || 0)}</b></span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveTab('rentabilidad')}
                          className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Resultado acumulado {resumenDashboard.anio}</p>
                            <Target className="w-4 h-4 text-slate-400" />
                          </div>
                          <p className={`text-3xl font-bold ${resultadoAnio >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {resultadoAnio >= 0 ? '+' : ''}{formatCurrency(resultadoAnio)}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Ingresos <b className="text-slate-700">{formatCurrency(a.ingresos || 0)}</b></span>
                            <span>Gastos <b className="text-slate-700">{formatCurrency(a.gastos || 0)}</b></span>
                            {(a.gasto_financiero || 0) > 0 && (
                              <span>Coste financ. <b className="text-slate-700">{formatCurrency(a.gasto_financiero || 0)}</b></span>
                            )}
                          </div>
                        </button>
                      </>
                    );
                  })()}
                </div>

                {/* Fila 2: Cobros */}
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Cobros</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('emitidas')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Facturas por cobrar</p>
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                      </div>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(resumenDashboard.cobros?.pendiente_cobro_anio || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.cobros?.num_facturas_por_cobrar || 0} facturas pendientes</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('pendientes')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Pendientes de emitir</p>
                        <FileWarning className="w-4 h-4 text-amber-500" />
                      </div>
                      <p className="text-2xl font-bold text-amber-600">{formatCurrency(resumenDashboard.facturacion?.importe_pendientes_emitir || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.facturacion?.num_pendientes_emitir || 0} presupuestos sin facturar</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('ingresos')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Ingresos sin vincular</p>
                        <Link2 className="w-4 h-4 text-blue-500" />
                      </div>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(resumenDashboard.cobros?.abonos_sin_vincular_importe || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.cobros?.num_abonos_sin_vincular || 0} abonos bancarios</p>
                    </button>
                  </div>
                </div>

                {/* Fila 3: Pagos & Drive */}
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Pagos y documentos</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('soportadas')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Cargos sin vincular</p>
                        <Unlink className="w-4 h-4 text-red-500" />
                      </div>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(resumenDashboard.pagos?.cargos_sin_vincular_importe || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.pagos?.num_cargos_sin_vincular || 0} cargos {resumenDashboard.anio} · no deducibles</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('automatizacion')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Facturas en Drive</p>
                        <FolderSync className="w-4 h-4 text-indigo-500" />
                      </div>
                      <p className="text-2xl font-bold text-indigo-600">{resumenDashboard.facturacion?.drive_sin_vincular || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        sin vincular
                        {(resumenDashboard.facturacion?.drive_pendientes_revision || 0) > 0 && (
                          <span className="ml-2">· {resumenDashboard.facturacion.drive_pendientes_revision} pendientes revisión</span>
                        )}
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('soportadas')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Cargos estructurales</p>
                        <Building2 className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className="text-2xl font-bold text-slate-700">{formatCurrency(resumenDashboard.pagos?.cargos_estructurales_importe || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.pagos?.num_cargos_estructurales || 0} nóminas/SS/impuestos</p>
                    </button>
                  </div>
                </div>

                {/* Fila 4: Financiación */}
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Financiación</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('prestamos')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Deuda total</p>
                        <CreditCard className="w-4 h-4 text-rose-500" />
                      </div>
                      <p className="text-2xl font-bold text-rose-600">{formatCurrency(resumenDashboard.financiacion?.deuda_total || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">{resumenDashboard.financiacion?.num_operaciones_activas || 0} operaciones activas</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('prestamos')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Línea crédito disponible</p>
                        <TrendingUp className="w-4 h-4 text-emerald-500" />
                      </div>
                      <p className="text-2xl font-bold text-emerald-600">{formatCurrency(resumenDashboard.financiacion?.linea_credito_disponible || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">disponible para disponer</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('prestamos')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Coste financiero {resumenDashboard.anio}</p>
                        <TrendingDown className="w-4 h-4 text-orange-500" />
                      </div>
                      <p className="text-2xl font-bold text-orange-600">{formatCurrency(resumenDashboard.financiacion?.coste_financiero_anio || 0)}</p>
                      <p className="text-xs text-slate-500 mt-1">intereses + comisiones</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('prestamos')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Próxima cuota</p>
                        <Clock className="w-4 h-4 text-blue-500" />
                      </div>
                      {resumenDashboard.financiacion?.proxima_cuota ? (
                        <>
                          <p className="text-2xl font-bold text-blue-600">{formatCurrency(resumenDashboard.financiacion.proxima_cuota.importe || 0)}</p>
                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {formatFecha(resumenDashboard.financiacion.proxima_cuota.fecha)} · {resumenDashboard.financiacion.proxima_cuota.operacion_nombre || '—'}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-2xl font-bold text-slate-400">—</p>
                          <p className="text-xs text-slate-500 mt-1">sin cuotas pendientes</p>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Fila 5: Fiscal */}
                <div>
                  <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Fiscal</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setActiveTab('fiscal')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">IVA a pagar · {resumenDashboard.fiscal?.trimestre_label}</p>
                        <Calculator className="w-4 h-4 text-purple-500" />
                      </div>
                      <p className={`text-2xl font-bold ${(resumenDashboard.fiscal?.iva_a_pagar_trimestre || 0) >= 0 ? 'text-purple-600' : 'text-emerald-600'}`}>
                        {formatCurrency(resumenDashboard.fiscal?.iva_a_pagar_trimestre || 0)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Repercutido {formatCurrency(resumenDashboard.fiscal?.iva_repercutido_trimestre || 0)} − Soportado {formatCurrency(resumenDashboard.fiscal?.iva_soportado_trimestre || 0)}
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('fiscal')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">IVA acumulado {resumenDashboard.anio}</p>
                        <Calculator className="w-4 h-4 text-purple-400" />
                      </div>
                      <p className={`text-2xl font-bold ${(resumenDashboard.fiscal?.iva_a_pagar_anio || 0) >= 0 ? 'text-purple-600' : 'text-emerald-600'}`}>
                        {formatCurrency(resumenDashboard.fiscal?.iva_a_pagar_anio || 0)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">liquidación anual estimada</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActiveTab('emitidas')}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-slate-500">Facturas emitidas {resumenDashboard.anio}</p>
                        <Receipt className="w-4 h-4 text-slate-500" />
                      </div>
                      <p className="text-2xl font-bold text-slate-700">{resumenDashboard.rentabilidad?.anio?.num_facturas || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Cobrado {formatCurrency(resumenDashboard.cobros?.cobrado_anio || 0)}</p>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab Rentabilidad */}
        {activeTab === 'rentabilidad' && rentabilidadData && (
          <div className="space-y-6" data-testid="rentabilidad-panel">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Facturado YTD</p>
                <p className="text-xl font-bold text-slate-900" data-testid="kpi-facturado">{formatCurrency(rentabilidadData.resumen?.total_ingresos_facturados_ytd || 0)}</p>
                <p className="text-xs text-slate-400 mt-1">Previsto: {formatCurrency(rentabilidadData.resumen?.total_ingresos_previstos_ytd || 0)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Gastos Fijos YTD</p>
                <p className="text-xl font-bold text-red-600" data-testid="kpi-fijos">{formatCurrency(rentabilidadData.resumen?.total_gastos_fijos_ytd || 0)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Personal: {formatCurrency(rentabilidadData.resumen?.personal_mensual || 0)} + Otros: {formatCurrency(rentabilidadData.resumen?.otros_fijos_mensual || 0)}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Gastos Variables YTD</p>
                <p className="text-xl font-bold text-orange-600" data-testid="kpi-variables">{formatCurrency(rentabilidadData.resumen?.total_gastos_variables_ytd || 0)}</p>
                <p className="text-xs text-slate-400 mt-1">{rentabilidadData.resumen?.pct_costes_variables || 0}% sobre ingresos</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 mb-1">Resultado YTD</p>
                <p className={`text-xl font-bold ${(rentabilidadData.resumen?.resultado_ytd || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="kpi-resultado">
                  {formatCurrency(rentabilidadData.resumen?.resultado_ytd || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-1">Margen contrib: {rentabilidadData.resumen?.margen_contribucion_pct || 0}%</p>
              </div>
            </div>

            {/* Breakeven Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-slate-700" />
                <h3 className="font-semibold text-slate-800 text-base">Punto de Equilibrio (Breakeven)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-slate-500">Necesitas facturar al mes</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="breakeven-amount">{formatCurrency(rentabilidadData.resumen?.breakeven_mensual || 0)}</p>
                  <p className="text-xs text-slate-400 mt-1">Fijos ({formatCurrency(rentabilidadData.resumen?.gastos_fijos_mensual || 0)}) + Variables ({rentabilidadData.resumen?.pct_costes_variables || 0}%)</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-slate-500 mb-2">Resultado mensual</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {(rentabilidadData.mensual || []).map(m => {
                      const resultado = m.resultado;
                      const esGanancia = resultado >= 0;
                      const maxAbs = Math.max(...(rentabilidadData.mensual || []).map(x => Math.abs(x.resultado)), 1);
                      const pct = Math.min(100, (Math.abs(resultado) / maxAbs) * 100);
                      return (
                        <div key={m.mes} className={`flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${m.es_futuro ? 'opacity-40' : ''} ${esGanancia ? 'bg-emerald-50' : 'bg-red-50'}`} data-testid={`breakeven-month-${m.mes}`}>
                          <span className="font-medium text-slate-600">{m.nombre_mes}</span>
                          <div className="w-10 h-16 bg-slate-100 rounded-sm relative overflow-hidden flex items-end">
                            <div className={`w-full rounded-sm transition-all ${esGanancia ? 'bg-emerald-400' : 'bg-red-400'}`} style={{ height: `${Math.max(4, pct)}%` }} />
                          </div>
                          <span className={`font-bold ${esGanancia ? 'text-emerald-700' : 'text-red-700'}`}>
                            {esGanancia ? '+' : ''}{(resultado / 1000).toFixed(1)}k
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly P&L Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-200">
                <h3 className="font-semibold text-slate-800">P&L Mensual</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-xs text-slate-500">
                      <th className="px-4 py-3 text-left font-medium">Mes</th>
                      <th className="px-4 py-3 text-right font-medium">Facturado</th>
                      <th className="px-4 py-3 text-right font-medium">G. Fijos</th>
                      <th className="px-4 py-3 text-right font-medium">G. Variables</th>
                      <th className="px-4 py-3 text-right font-medium">Resultado</th>
                      <th className="px-4 py-3 text-right font-medium">Acumulado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rentabilidadData.mensual || []).map(m => {
                      const isExpanded = expandedMonths[m.mes];
                      const hasEvents = m.detalle_eventos && m.detalle_eventos.length > 0;
                      return (
                        <React.Fragment key={m.mes}>
                          <tr 
                            className={`border-b border-slate-100 ${hasEvents ? 'cursor-pointer hover:bg-slate-50' : ''} ${m.es_futuro ? 'opacity-50' : ''} ${isExpanded ? 'bg-slate-50' : ''}`}
                            onClick={() => hasEvents && setExpandedMonths(prev => ({ ...prev, [m.mes]: !prev[m.mes] }))}
                            data-testid={`pl-row-${m.mes}`}
                          >
                            <td className="px-4 py-3 font-medium text-slate-700">
                              <span className="flex items-center gap-2">
                                {hasEvents && (
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                )}
                                {m.nombre_mes}
                                {hasEvents && <span className="text-xs text-slate-400">({m.num_eventos})</span>}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">{formatCurrency(m.ingresos_facturados)}</td>
                            <td className="px-4 py-3 text-right text-red-600">{formatCurrency(m.gastos_fijos)}</td>
                            <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(m.gastos_variables)}</td>
                            <td className={`px-4 py-3 text-right font-semibold ${m.resultado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(m.resultado)}
                            </td>
                            <td className={`px-4 py-3 text-right font-medium ${m.acumulado >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(m.acumulado)}
                            </td>
                          </tr>
                          {isExpanded && hasEvents && (
                            <>
                              <tr className="bg-slate-100/60">
                                <td className="pl-10 pr-4 py-2 text-xs font-medium text-slate-500">Evento</td>
                                <td className="px-4 py-2 text-xs font-medium text-slate-500 text-right">Facturado</td>
                                <td className="px-4 py-2 text-xs font-medium text-slate-500 text-right" colSpan="2">G. Variable</td>
                                <td className="px-4 py-2 text-xs font-medium text-slate-500 text-right">Beneficio</td>
                                <td className="px-4 py-2 text-xs font-medium text-slate-500 text-right">Margen</td>
                              </tr>
                              {m.detalle_eventos.map((ev, idx) => {
                                const margen = ev.facturado > 0 ? ((ev.beneficio / ev.facturado) * 100).toFixed(1) : '0.0';
                                const fechaCorta = ev.fecha_evento ? new Date(ev.fecha_evento).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
                                const evKey = `${m.mes}-${idx}`;
                                const isEvExpanded = expandedEvents[evKey];
                                const desglose = ev.desglose_costes || {};
                                const hasDesglose = ev.coste_variable > 0;
                                return (
                                  <React.Fragment key={idx}>
                                    <tr 
                                      className={`bg-slate-50/80 border-b border-slate-100/50 text-xs ${hasDesglose ? 'cursor-pointer hover:bg-slate-100/50' : ''}`}
                                      onClick={() => hasDesglose && setExpandedEvents(prev => ({ ...prev, [evKey]: !prev[evKey] }))}
                                    >
                                      <td className="pl-10 pr-4 py-2">
                                        {hasDesglose && (
                                          <ChevronDown className={`w-3 h-3 text-slate-400 inline mr-1 transition-transform ${isEvExpanded ? 'rotate-180' : ''}`} />
                                        )}
                                        <span className="text-slate-500">{fechaCorta}</span>
                                        <span className="mx-1.5 text-slate-300">|</span>
                                        <span className="font-medium text-slate-700">{ev.cliente_nombre || 'Sin cliente'}</span>
                                        <span className="ml-1 text-slate-400">#{ev.numero}</span>
                                      </td>
                                      <td className="px-4 py-2 text-right text-slate-700">{formatCurrency(ev.facturado)}</td>
                                      <td className="px-4 py-2 text-right text-orange-500" colSpan="2">{formatCurrency(ev.coste_variable)}</td>
                                      <td className={`px-4 py-2 text-right font-medium ${ev.beneficio >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(ev.beneficio)}
                                      </td>
                                      <td className={`px-4 py-2 text-right ${parseFloat(margen) >= 30 ? 'text-emerald-600' : parseFloat(margen) >= 15 ? 'text-amber-600' : 'text-red-600'}`}>
                                        {margen}%
                                      </td>
                                    </tr>
                                    {isEvExpanded && hasDesglose && (
                                      <tr className="bg-slate-100/40 text-xs">
                                        <td colSpan="6" className="pl-14 pr-4 py-2">
                                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-slate-500">
                                            {desglose.ingredientes > 0 && <span>Ingredientes: <b className="text-slate-700">{formatCurrency(desglose.ingredientes)}</b></span>}
                                            {desglose.personal_cocina > 0 && <span>Personal cocina: <b className="text-slate-700">{formatCurrency(desglose.personal_cocina)}</b></span>}
                                            {desglose.personal_evento > 0 && <span>Personal evento: <b className="text-slate-700">{formatCurrency(desglose.personal_evento)}</b></span>}
                                            {desglose.materiales > 0 && <span>Materiales: <b className="text-slate-700">{formatCurrency(desglose.materiales)}</b></span>}
                                            {desglose.transporte > 0 && <span>Transporte: <b className="text-slate-700">{formatCurrency(desglose.transporte)}</b></span>}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold text-sm">
                      <td className="px-4 py-3">Total</td>
                      <td className="px-4 py-3 text-right">{formatCurrency((rentabilidadData.mensual || []).reduce((s, m) => s + m.ingresos_facturados, 0))}</td>
                      <td className="px-4 py-3 text-right text-red-600">{formatCurrency((rentabilidadData.mensual || []).reduce((s, m) => s + m.gastos_fijos, 0))}</td>
                      <td className="px-4 py-3 text-right text-orange-600">{formatCurrency((rentabilidadData.mensual || []).reduce((s, m) => s + m.gastos_variables, 0))}</td>
                      <td className={`px-4 py-3 text-right ${(rentabilidadData.mensual || []).reduce((s, m) => s + m.resultado, 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency((rentabilidadData.mensual || []).reduce((s, m) => s + m.resultado, 0))}
                      </td>
                      <td className="px-4 py-3 text-right"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tab Emitidas */}
        {activeTab === 'emitidas' && (<>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg">Facturas Emitidas ({facturasEmitidas.length})</CardTitle>
              {facturasEmitidas.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() => setMostrarSelectorFacturas(v => !v)}
                >
                  {facturasEmitidas.every(f => facturasEmitidasSeleccionadas.has(f.id))
                    ? 'Deseleccionar todas'
                    : `Seleccionar (${facturasEmitidas.length})`}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mostrarSelectorFacturas ? 'rotate-180' : ''}`} />
                </Button>
              )}
            </CardHeader>
            {facturasEmitidas.length > 0 && mostrarSelectorFacturas && (() => {
              const todasSeleccionadas = facturasEmitidas.every(f => facturasEmitidasSeleccionadas.has(f.id));
              const getIdsTrimestre = (t) => {
                const mesesT = [[0,1,2],[3,4,5],[6,7,8],[9,10,11]][t-1];
                return facturasEmitidas
                  .filter(f => {
                    const fecha = f.fecha_factura || f.fecha_emision;
                    if (!fecha) return false;
                    return mesesT.includes(new Date(fecha).getMonth());
                  })
                  .map(f => f.id);
              };
              const getIdsMes = (m) => facturasEmitidas
                .filter(f => {
                  const fecha = f.fecha_factura || f.fecha_emision;
                  if (!fecha) return false;
                  return new Date(fecha).getMonth() === m;
                })
                .map(f => f.id);
              const toggleIds = (ids) => {
                const todos = ids.length > 0 && ids.every(id => facturasEmitidasSeleccionadas.has(id));
                setFacturasEmitidasSeleccionadas(prev => {
                  const next = new Set(prev);
                  if (todos) ids.forEach(id => next.delete(id));
                  else ids.forEach(id => next.add(id));
                  return next;
                });
              };
              const mesesAbr = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
              const pillBase = 'inline-flex items-center gap-1 px-3 py-1 text-xs rounded-full border transition-colors cursor-pointer select-none';
              return (
                <div className="px-6 pb-3 -mt-2 border-b border-slate-200 bg-white">
                  <div className="flex items-center gap-2 flex-wrap py-2">
                    <span className="text-xs text-slate-500 font-medium mr-1">Trimestres:</span>
                    <button
                      type="button"
                      onClick={() => {
                        const allIds = facturasEmitidas.map(f => f.id);
                        setFacturasEmitidasSeleccionadas(todasSeleccionadas ? new Set() : new Set(allIds));
                      }}
                      className={`${pillBase} ${todasSeleccionadas ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`}
                    >
                      Todas ({facturasEmitidas.length})
                    </button>
                    {[1,2,3,4].map(t => {
                      const ids = getIdsTrimestre(t);
                      const todos = ids.length > 0 && ids.every(id => facturasEmitidasSeleccionadas.has(id));
                      return (
                        <button
                          type="button"
                          key={`t${t}`}
                          disabled={ids.length === 0}
                          onClick={() => toggleIds(ids)}
                          className={`${pillBase} ${
                            ids.length === 0 ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                            : todos ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {t}T ({ids.length})
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap py-2">
                    <span className="text-xs text-slate-500 font-medium mr-1">Meses:</span>
                    {mesesAbr.map((nombre, m) => {
                      const ids = getIdsMes(m);
                      if (ids.length === 0) return null;
                      const todos = ids.every(id => facturasEmitidasSeleccionadas.has(id));
                      return (
                        <button
                          type="button"
                          key={`m${m}`}
                          onClick={() => toggleIds(ids)}
                          className={`${pillBase} ${todos ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`}
                        >
                          {nombre} ({ids.length})
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Cargando...</div>
              ) : (
                renderTablaEmitidas()
              )}
            </CardContent>
          </Card>
          
          {renderResumenes('emitidas')}
        </>)}

        {/* Tab Banco: Movimientos (extracto de cuenta) */}
        {activeTab === 'banco' && (
          <div className="space-y-4 mt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white border rounded-xl p-4">
                <p className="text-xs text-gray-500">Saldo en cuenta</p>
                <p className="text-2xl font-bold text-slate-900">
                  {saldoBanco?.saldo != null
                    ? Number(saldoBanco.saldo).toLocaleString('es-ES', { style: 'currency', currency: saldoBanco.saldo_moneda || 'EUR' })
                    : '—'}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {saldoBanco?.saldo_iban ? `${saldoBanco.saldo_iban} · ` : ''}
                  {saldoBanco?.ultima_sync
                    ? `actualizado ${new Date(saldoBanco.ultima_sync).toLocaleDateString('es-ES')}`
                    : 'pendiente de la próxima sincronización'}
                </p>
              </div>
              <div className="bg-white border rounded-xl p-4">
                <p className="text-xs text-gray-500">Ingresos {trimestreSeleccionado ? `T${trimestreSeleccionado}` : añoSeleccionado}</p>
                <p className="text-xl font-bold text-green-600">
                  {Number(saldoBanco?.total_ingresos || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </p>
              </div>
              <div className="bg-white border rounded-xl p-4">
                <p className="text-xs text-gray-500">Gastos {trimestreSeleccionado ? `T${trimestreSeleccionado}` : añoSeleccionado}</p>
                <p className="text-xl font-bold text-red-600">
                  {Number(saldoBanco?.total_gastos || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </p>
              </div>
            </div>

            <Card>
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Movimientos de la cuenta</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Extracto de {añoSeleccionado} completo, como en el banco. Se actualiza con la sincronización diaria.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={busquedaMovimiento}
                      onChange={(e) => setBusquedaMovimiento(e.target.value)}
                      placeholder="Buscar concepto (ej: carrefour)"
                      className="pl-8 pr-7 py-1.5 text-sm border rounded-md w-56 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    />
                    {busquedaMovimiento && (
                      <button onClick={() => setBusquedaMovimiento('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" title="Limpiar">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={cargarMovimientosBanco} disabled={cargandoMovimientos}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${cargandoMovimientos ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {cargandoMovimientos ? (
                  <p className="text-sm text-gray-500 py-4">Cargando movimientos…</p>
                ) : movimientosBanco.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">Sin movimientos en este periodo.</p>
                ) : (() => {
                  const q = busquedaMovimiento.trim().toLowerCase();
                  const movs = q
                    ? movimientosBanco.filter(m => (m.concepto || '').toLowerCase().includes(q))
                    : movimientosBanco;
                  const sumaFiltro = movs.reduce((s, m) => s + (m.importe || 0), 0);
                  if (movs.length === 0) {
                    return <p className="text-sm text-gray-500 py-4">Ningún movimiento coincide con "{busquedaMovimiento}".</p>;
                  }
                  return (
                  <div className="overflow-x-auto">
                    {q && (
                      <p className="text-xs text-gray-500 mb-2">
                        {movs.length} movimiento{movs.length === 1 ? '' : 's'} · suma {sumaFiltro >= 0 ? '+' : ''}{Number(sumaFiltro).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </p>
                    )}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 border-b">
                          <th className="py-2 pr-3 whitespace-nowrap">Fecha</th>
                          <th className="py-2 pr-3">Concepto</th>
                          <th className="py-2 pr-3 text-right whitespace-nowrap">Importe</th>
                          <th className="py-2 pr-3 text-right whitespace-nowrap">Saldo</th>
                          <th className="py-2 w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {movs.map((m) => (
                          <tr key={m.id} className="border-b last:border-0">
                            <td className="py-2 pr-3 whitespace-nowrap text-gray-600">{m.fecha}</td>
                            <td className="py-2 pr-3">
                              <span className="text-gray-800">{m.concepto}</span>
                              {m.vinculado && <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-green-500 align-middle" title="Vinculado" />}
                            </td>
                            <td className={`py-2 pr-3 text-right whitespace-nowrap font-medium ${m.importe >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {m.importe >= 0 ? '+' : ''}{Number(m.importe).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                            </td>
                            <td className="py-2 pr-3 text-right whitespace-nowrap text-gray-500">
                              {m.saldo_acumulado != null ? Number(m.saldo_acumulado).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '—'}
                            </td>
                            <td className="py-2 text-right">
                              {m.importe < 0 && (
                                <button
                                  onClick={() => pedirFacturaCargo(m)}
                                  className="p-1 text-gray-300 hover:text-duetto-primary"
                                  title="Pedir factura de este cargo por email"
                                  data-testid={`btn-pedir-factura-${m.id}`}
                                >
                                  <Mail className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Soportadas */}
        {activeTab === 'soportadas' && (<>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Gastos y Facturas Soportadas</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {cargosBancarios.filter(c => c.tipo === 'cargo').length} cargos bancarios + {facturasSoportadas.length} facturas | 
                  Total gastos: {formatNumber(cargosBancarios.filter(c => c.tipo === 'cargo').reduce((sum, c) => sum + (c.importe || 0), 0) + facturasSoportadas.reduce((sum, f) => sum + (f.total || 0), 0))}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Cargando...</div>
              ) : (
                renderTablaSoportadas()
              )}
            </CardContent>
          </Card>
          
          {renderResumenes('soportadas')}
          {renderGastosPorCategoria()}
        </>)}

        {/* Tab Automatización Google Drive */}
        {activeTab === 'automatizacion' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Registro del vigilante de Drive/Soportadas (procesamiento con IA) */}
            <RegistroAutomatizacion />
            {/* Visor de Archivos Drive - PRINCIPAL */}
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm sm:text-lg flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-blue-500" />
                    Archivos Drive 
                    <span className="text-xs sm:text-sm font-normal text-gray-500">
                      {driveArchivos.length > 0 && `${driveArchivos.length} archivos`}
                      {driveSubcarpetas.length > 0 && ` · ${driveSubcarpetas.length} carpetas`}
                    </span>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {(cargandoDrive || verificandoDrive) && <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        setCargandoDrive(true);
                        try {
                          const res = await fetch(`${API_URL}/api/finanzas/drive/archivos`, { headers });
                          const data = await res.json();
                          setDriveArchivos(data.archivos || []);
                          setDriveSubcarpetas(data.subcarpetas || []);
                          setDriveCarpetaActual(null);
                          setDriveRutaCarpetas([]);
                          setDriveConectado(true);
                        } catch (e) {
                          console.error(e);
                        } finally {
                          setCargandoDrive(false);
                        }
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Recargar
                    </Button>
                  </div>
                </div>
                
                {/* Breadcrumb */}
                {driveRutaCarpetas.length > 0 && (
                  <div className="flex items-center gap-1 text-xs mt-2 flex-wrap">
                    <button 
                      onClick={async () => {
                        setCargandoDrive(true);
                        try {
                          const res = await fetch(`${API_URL}/api/finanzas/drive/archivos`, { headers });
                          const data = await res.json();
                          setDriveArchivos(data.archivos || []);
                          setDriveSubcarpetas(data.subcarpetas || []);
                          setDriveCarpetaActual(null);
                          setDriveRutaCarpetas([]);
                        } catch (e) {
                          console.error(e);
                        } finally {
                          setCargandoDrive(false);
                        }
                      }}
                      className="text-blue-600 hover:underline"
                    >
                      Soportadas
                    </button>
                    {driveRutaCarpetas.map((carpeta, index) => (
                      <span key={carpeta.id} className="flex items-center gap-1">
                        <span className="text-gray-400">/</span>
                        <button
                          onClick={async () => {
                            setCargandoDrive(true);
                            try {
                              const res = await fetch(`${API_URL}/api/finanzas/drive/archivos?carpeta_id=${carpeta.id}`, { headers });
                              const data = await res.json();
                              setDriveArchivos(data.archivos || []);
                              setDriveSubcarpetas(data.subcarpetas || []);
                              setDriveCarpetaActual(carpeta.id);
                              setDriveRutaCarpetas(prev => prev.slice(0, index + 1));
                            } catch (e) {
                              console.error(e);
                            } finally {
                              setCargandoDrive(false);
                            }
                          }}
                          className={index === driveRutaCarpetas.length - 1 ? 'text-gray-700 font-medium' : 'text-blue-600 hover:underline'}
                        >
                          {carpeta.name}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </CardHeader>
              
              <CardContent className="p-3 sm:p-6 pt-0">
                {/* Subcarpetas */}
                {driveSubcarpetas.length > 0 && (
                  <div className="mb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                      {driveSubcarpetas.map((carpeta) => (
                        <button
                          key={carpeta.id}
                          onClick={async () => {
                            setCargandoDrive(true);
                            try {
                              const res = await fetch(`${API_URL}/api/finanzas/drive/archivos?carpeta_id=${carpeta.id}`, { headers });
                              const data = await res.json();
                              setDriveArchivos(data.archivos || []);
                              setDriveSubcarpetas(data.subcarpetas || []);
                              setDriveCarpetaActual(carpeta.id);
                              setDriveRutaCarpetas(prev => [...prev, { id: carpeta.id, name: carpeta.name }]);
                            } catch (e) {
                              console.error(e);
                            } finally {
                              setCargandoDrive(false);
                            }
                          }}
                          className="flex items-center gap-2 p-2 text-left bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors"
                        >
                          <Folder className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="text-sm text-blue-800 truncate">{carpeta.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Estado de carga */}
                {cargandoDrive && driveArchivos.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    <p>Cargando archivos...</p>
                  </div>
                )}

                {/* Sin archivos */}
                {!cargandoDrive && driveArchivos.length === 0 && driveSubcarpetas.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Folder className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No hay archivos en esta carpeta</p>
                  </div>
                )}

                {/* Tabla de archivos */}
                {driveArchivos.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-[500px] overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left">Archivo</th>
                            <th className="px-3 py-2 text-right hidden sm:table-cell">Importe</th>
                            <th className="px-3 py-2 text-center hidden sm:table-cell">Fecha</th>
                            <th className="px-3 py-2 text-center w-20">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {driveArchivos.map(archivo => {
                            const incidenciaAbierta = archivo.incidencia?.estado === 'abierta';
                            return (
                            <tr key={archivo.id} className={`border-t ${incidenciaAbierta ? 'bg-amber-50 hover:bg-amber-100' : `hover:bg-gray-50 ${archivo.vinculada ? 'bg-green-50' : ''}`}`}>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <FileText className={`w-4 h-4 flex-shrink-0 ${incidenciaAbierta ? 'text-amber-500' : archivo.vinculada ? 'text-green-500' : 'text-gray-400'}`} />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1">
                                      <span className="truncate text-sm" title={archivo.name}>{archivo.name}</span>
                                      {incidenciaAbierta && (
                                        <span
                                          className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-0.5 flex-shrink-0"
                                          title={archivo.incidencia?.texto || 'Incidencia abierta'}
                                        >
                                          <AlertTriangle className="w-3 h-3" /> Incidencia
                                        </span>
                                      )}
                                      {archivo.vinculada && <Link2 className="w-3 h-3 text-green-600 flex-shrink-0" title="Vinculada" />}
                                      {archivo.procesada && !archivo.vinculada && !incidenciaAbierta && <Sparkles className="w-3 h-3 text-blue-500 flex-shrink-0" title="Procesada" />}
                                    </div>
                                    {archivo.proveedor && <div className="text-xs text-gray-500">{archivo.proveedor}</div>}
                                    {incidenciaAbierta && archivo.incidencia?.texto && (
                                      <div className="text-xs text-amber-700 truncate" title={archivo.incidencia.texto}>
                                        {archivo.incidencia.texto}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right hidden sm:table-cell">
                                {archivo.importe ? formatNumber(archivo.importe) : '-'}
                              </td>
                              <td className="px-3 py-2 text-center text-gray-500 hidden sm:table-cell">
                                {archivo.createdTime ? formatFecha(archivo.createdTime.split('T')[0]) : '-'}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex justify-center gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={() => window.open(archivo.webViewLink, '_blank')}
                                    title="Ver en Drive"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    title="Procesar factura"
                                    onClick={async () => {
                                      setArchivoEnProceso(archivo);
                                      setDatosFacturaIA(null);
                                      setCargosMatch([]);
                                      setSubcargosMatch([]);
                                      setCargosSeleccionados([]);
                                      setDatosDesdeCache(false);
                                      setCargoYaVinculado(null);
                                      setShowProcesarFactura(true);
                                      setProcesandoIA(true);
                                      
                                      try {
                                        // Usar safeFetch para evitar error de clone
                                        const { ok, data } = await safeFetch(
                                          `${API_URL}/api/finanzas/drive/extraer-datos/${archivo.id}?incluir_cargos_vinculados=${incluirCargosVinculados}`, 
                                          {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            }
                                          }
                                        );
                                        
                                        if (ok && data.success) {
                                          setDatosFacturaIA(data.datos_extraidos);
                                          setCargosMatch(data.cargos_candidatos || []);
                                          setSubcargosMatch(data.subcargos_candidatos || []);
                                          setAbonosMatch(data.abonos_candidatos || []);
                                          setEsNotaCredito(data.es_nota_credito || false);
                                          setDatosDesdeCache(data.desde_cache || false);
                                          setCargoYaVinculado(data.cargo_vinculado || null);
                                        } else {
                                          setDatosFacturaIA({ error: data.detail || data.error || 'Error extrayendo datos' });
                                        }
                                      } catch (e) {
                                        console.error('Error procesando factura:', e);
                                        setDatosFacturaIA({ error: e.message });
                                      } finally {
                                        setProcesandoIA(false);
                                      }
                                    }}
                                  >
                                    <Play className="w-4 h-4 text-green-600" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    title="Mover archivo"
                                    onClick={async () => {
                                      setArchivoParaMover(archivo);
                                      setCarpetaDestino('');
                                      setShowMoverArchivo(true);
                                      
                                      // Cargar carpetas disponibles
                                      try {
                                        const res = await fetch(`${API_URL}/api/finanzas/drive/carpetas-disponibles`, { headers });
                                        const data = await res.json();
                                        setCarpetasDisponibles(data.carpetas || []);
                                      } catch (e) {
                                        console.error('Error cargando carpetas:', e);
                                        setCarpetasDisponibles([{ valor: 'papelera', nombre: '🗑️ Papelera', tipo: 'especial' }]);
                                      }
                                    }}
                                  >
                                    <FolderInput className="w-4 h-4 text-blue-600" />
                                  </Button>
                                  {archivo.a3doc_document_id ? (
                                    <span
                                      className="h-7 w-7 inline-flex items-center justify-center flex-shrink-0"
                                      title={archivo.a3doc_subido_en ? `Subida a a3doc el ${formatFechaHora(archivo.a3doc_subido_en)}` : 'Subida a a3doc'}
                                    >
                                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                                    </span>
                                  ) : archivo.factura_drive_id ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0"
                                      title="Enviar a a3doc; la factura sigue pendiente de vincular"
                                      disabled={subiendoA3docIncId === archivo.factura_drive_id}
                                      onClick={() => subirA3docPendiente(archivo.factura_drive_id, (data) => {
                                        setDriveArchivos(prev => prev.map(a => a.id === archivo.id
                                          ? { ...a, a3doc_document_id: data.a3doc_document_id, a3doc_subido_en: data.a3doc_subido_en }
                                          : a));
                                      })}
                                    >
                                      <Cloud className={`w-4 h-4 text-blue-600 ${subiendoA3docIncId === archivo.factura_drive_id ? 'animate-pulse' : ''}`} />
                                    </Button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Acciones secundarias */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowConfigTarjetas(true)}>
                <CardContent className="p-3 flex items-center gap-3">
                  <CreditCard className="w-8 h-8 text-purple-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-sm">Asignar Tarjetas</h3>
                    <p className="text-xs text-gray-500">Vincular a empleados</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="cursor-pointer hover:shadow-md transition-shadow" 
                onClick={async () => {
                  if (!window.confirm('¿Limpiar cargos con facturas huérfanas?\n\nEsto arreglará cargos que muestran "ya tiene factura vinculada" pero la factura no existe.')) return;
                  try {
                    const { ok, data } = await safeFetch(`${API_URL}/api/finanzas/cargos-bancarios/limpiar-huerfanos`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    if (ok && data.success) {
                      alert(`✅ ${data.mensaje}`);
                      cargarDatosPestaña();
                    } else {
                      alert('Error: ' + (data.detail || 'No se pudo limpiar'));
                    }
                  } catch (e) {
                    alert('Error: ' + e.message);
                  }
                }}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <Database className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-sm">Limpiar Huérfanos</h3>
                    <p className="text-xs text-gray-500">Arreglar cargos rotos</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Tab Pendientes de Facturar */}
        {activeTab === 'pendientes' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileWarning className="w-5 h-5 text-orange-500" />
                  Presupuestos Pendientes de Facturar
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Eventos realizados que aún no tienen factura emitida
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(diasMaxPendientes)} onValueChange={(v) => setDiasMaxPendientes(Number(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="90">90 días</SelectItem>
                    <SelectItem value="180">6 meses</SelectItem>
                    <SelectItem value="365">1 año</SelectItem>
                    <SelectItem value="730">2 años</SelectItem>
                    <SelectItem value="1825">5 años</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    setCargandoPendientes(true);
                    try {
                      const res = await fetch(`${API_URL}/api/finanzas/pendientes-facturar?dias_max=${diasMaxPendientes}`, { headers });
                      const data = await res.json();
                      setPendientesFacturar(data.presupuestos || []);
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setCargandoPendientes(false);
                    }
                  }}
                >
                  {cargandoPendientes ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Actualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendientesFacturar.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-2" />
                  <p className="text-gray-600">¡Perfecto! No hay facturas pendientes de emitir</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={async () => {
                      setCargandoPendientes(true);
                      try {
                        const res = await fetch(`${API_URL}/api/finanzas/pendientes-facturar?dias_max=${diasMaxPendientes}`, { headers });
                        const data = await res.json();
                        setPendientesFacturar(data.presupuestos || []);
                      } catch (e) {
                        console.error(e);
                      } finally {
                        setCargandoPendientes(false);
                      }
                    }}
                  >
                    Buscar pendientes
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4 mb-4">
                    <div className="bg-orange-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-xl sm:text-2xl font-bold text-orange-600">{pendientesFacturar.length}</p>
                      <p className="text-xs sm:text-sm text-orange-700">Pendientes</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-lg sm:text-2xl font-bold text-blue-600">
                        €{pendientesFacturar.reduce((sum, p) => sum + (p.precio_total || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 0})}
                      </p>
                      <p className="text-xs sm:text-sm text-blue-700">Total</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-lg sm:text-2xl font-bold text-green-600">
                        €{pendientesFacturar.reduce((sum, p) => sum + (p.total_pagado || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 0})}
                      </p>
                      <p className="text-xs sm:text-sm text-green-700">Cobrado</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                      <p className="text-lg sm:text-2xl font-bold text-yellow-600">
                        €{pendientesFacturar.reduce((sum, p) => sum + (p.pendiente_cobro || 0), 0).toLocaleString('es-ES', {minimumFractionDigits: 0})}
                      </p>
                      <p className="text-xs sm:text-sm text-yellow-700">Pendiente</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2 sm:p-3 text-center col-span-2 sm:col-span-1">
                      <p className="text-xl sm:text-2xl font-bold text-red-600">
                        {pendientesFacturar.length > 0 ? Math.max(...pendientesFacturar.map(p => p.dias_desde_evento || 0)) : 0} días
                      </p>
                      <p className="text-xs sm:text-sm text-red-700">Más antiguo</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-2 sm:p-3 text-center col-span-2 sm:col-span-1">
                      <p className="text-xl sm:text-2xl font-bold text-amber-600">
                        {pendientesFacturar.filter(p => p.validacion_comercial !== 'validado').length}
                      </p>
                      <p className="text-xs sm:text-sm text-amber-700">Sin validar comercial</p>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Presup.</th>
                          <th className="px-3 py-2 text-left">Fecha Evento</th>
                          <th className="px-3 py-2 text-left">Días</th>
                          <th className="px-3 py-2 text-left">Cliente</th>
                          <th className="px-3 py-2 text-right">Importe</th>
                          <th className="px-3 py-2 text-center">Cobrado</th>
                          <th className="px-3 py-2 text-center">Validación</th>
                          <th className="px-3 py-2 text-center">Dir. Fiscal</th>
                          <th className="px-3 py-2 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientesFacturar.map(p => (
                          <tr key={p.es_jornada_individual ? `${p.id}-${p.jornada_id}` : p.id} className={`border-b hover:bg-gray-50 ${p.dias_desde_evento > 30 ? 'bg-red-50' : p.dias_desde_evento > 14 ? 'bg-orange-50' : ''}`}>
                            <td className="px-3 py-2 font-medium">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span>#{p.numero}</span>
                                {p.es_jornada_individual ? (
                                  <span
                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap"
                                    title="Jornada individual de un multi-evento facturado por jornada"
                                  >
                                    JORNADA · {p.jornada_fecha}
                                  </span>
                                ) : p.es_multi_evento && (
                                  <span
                                    className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200 whitespace-nowrap"
                                    title={`Presupuesto multi-evento${p.total_jornadas ? ` (${p.total_jornadas} jornadas)` : ''}`}
                                  >
                                    MULTI{p.total_jornadas ? ` · ${p.total_jornadas}j` : ''}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              {p.es_multi_evento && p.fecha_primera_jornada && p.fecha_ultima_jornada ? (
                                <span title="Rango de jornadas (multi-evento)" className="whitespace-nowrap">
                                  {p.fecha_primera_jornada}
                                  <span className="mx-1 text-gray-400">→</span>
                                  {p.fecha_ultima_jornada}
                                </span>
                              ) : (
                                p.fecha_evento
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded text-xs ${p.dias_desde_evento > 30 ? 'bg-red-100 text-red-700' : p.dias_desde_evento > 14 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100'}`}>
                                {p.dias_desde_evento}d
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{p.cliente_nombre}</div>
                              {p.cliente_empresa && <div className="text-xs text-gray-500">{p.cliente_empresa}</div>}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">€{(p.precio_total || 0).toLocaleString('es-ES', {minimumFractionDigits: 2})}</td>
                            <td className="px-3 py-2 text-center">
                              {p.cobrado ? (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                  ✓ Cobrado
                                </span>
                              ) : p.total_pagado > 0 ? (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs" title={`Pagado: €${p.total_pagado?.toFixed(2)} / Pendiente: €${p.pendiente_cobro?.toFixed(2)}`}>
                                  Parcial ({Math.round((p.total_pagado / p.precio_total) * 100)}%)
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                  Pendiente
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {p.validacion_comercial === 'validado' ? (
                                <span
                                  className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium whitespace-nowrap"
                                  title={`El comercial ha revisado el evento: se puede emitir sin sorpresas${p.validacion_comercial_por ? ` (${p.validacion_comercial_por})` : ''}`}
                                >
                                  ✓ Validado
                                </span>
                              ) : (
                                <span
                                  className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium whitespace-nowrap"
                                  title="El comercial aún no ha confirmado que el evento esté completo (ampliaciones del día, horas extra, bebidas...). Se puede emitir igualmente."
                                >
                                  Sin validar
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {p.tiene_direccion_fiscal ? (
                                <Check className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-orange-500 mx-auto" title="Sin dirección fiscal" />
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => window.open(`/presupuestos/${p.id}`, '_blank')}
                                  title="Ver presupuesto"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-duetto-primary hover:bg-duetto-primary-dark text-white"
                                  onClick={() => window.open(
                                    p.es_jornada_individual
                                      ? `/presupuestos/${p.id}`
                                      : `/presupuestos/${p.id}?openFactura=true`,
                                    '_blank'
                                  )}
                                  title={p.es_jornada_individual ? "Abrir presupuesto para emitir esta jornada" : "Emitir factura"}
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-gray-400 hover:text-red-600"
                                  onClick={() => handleDescartarFactura(p.id)}
                                  title="Descartar / No quiere factura"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}        {/* Tab Resumen Fiscal - Componente Extraído */}
        {activeTab === 'fiscal' && (
          <FiscalTab headers={headers} añoSeleccionado={añoSeleccionado} />
        )}

        {/* Tab Gastos Fijos */}
        {activeTab === 'gastos-fijos' && (
          <div className="space-y-4">
            {/* Header con acciones */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Repeat className="w-5 h-5 text-purple-500" />
                    Gastos Fijos Mensuales
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Controla tus gastos recurrentes y detecta anomalías
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={async () => {
                      setCargandoGastosFijos(true);
                      try {
                        const [gfRes, patRes, personalRes] = await Promise.all([
                          fetch(`${API_URL}/api/finanzas/gastos-fijos`, { headers }),
                          fetch(`${API_URL}/api/finanzas/gastos-fijos/detectar-patrones?meses_minimos=3`, { headers }),
                          fetch(`${API_URL}/api/finanzas/gastos-fijos/personal`, { headers })
                        ]);
                        const gfData = await gfRes.json();
                        const patData = await patRes.json();
                        const personalData = await personalRes.json();
                        setGastosFijos(gfData.gastos_fijos || []);
                        setPatronesDetectados(patData.sugerencias?.filter(s => !s.ya_existe_como_fijo) || []);
                        setGastosPersonal(personalData);
                      } catch (e) {
                        console.error(e);
                      } finally {
                        setCargandoGastosFijos(false);
                      }
                    }}
                  >
                    {cargandoGastosFijos ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    <span className="ml-1 hidden sm:inline">Actualizar</span>
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowNuevoGastoFijo(true)}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="ml-1 hidden sm:inline">Añadir</span>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Resumen de totales incluyendo personal */}
                {(gastosFijos.length > 0 || gastosPersonal) && (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-purple-600">Gastos Fijos</p>
                      <p className="text-xl font-bold text-purple-700">{gastosFijos.filter(g => g.activo).length}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-600">Otros Fijos</p>
                      <p className="text-xl font-bold text-blue-700">
                        {formatNumber(gastosFijos.filter(g => g.activo).reduce((sum, g) => sum + (g.importe_mensual || g.importe_estimado || 0), 0))}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-600">Personal</p>
                      <p className="text-xl font-bold text-green-700">
                        {formatNumber(gastosPersonal?.resumen?.total_personal || 0)}
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-orange-600">Total Mensual</p>
                      <p className="text-xl font-bold text-orange-700">
                        {formatNumber(
                          gastosFijos.filter(g => g.activo).reduce((sum, g) => sum + (g.importe_mensual || g.importe_estimado || 0), 0) +
                          (gastosPersonal?.resumen?.total_personal || 0)
                        )}
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-600">Total Anual</p>
                      <p className="text-xl font-bold text-red-700">
                        {formatNumber(
                          (gastosFijos.filter(g => g.activo).reduce((sum, g) => sum + (g.importe_mensual || g.importe_estimado || 0), 0) +
                          (gastosPersonal?.resumen?.total_personal || 0)) * 12
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Panel de Gastos de Personal */}
                {gastosPersonal && gastosPersonal.gastos_personal?.length > 0 && (
                  <div className="mb-4 border rounded-lg overflow-hidden">
                    <div className="bg-green-50 px-4 py-2 flex items-center justify-between">
                      <h4 className="font-medium text-green-800 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Gastos de Personal ({gastosPersonal.resumen?.num_empleados} empleados)
                      </h4>
                      <div className="text-sm text-green-700">
                        <span className="font-semibold">{formatNumber(gastosPersonal.resumen?.total_personal || 0)}</span>/mes
                      </div>
                    </div>
                    <div className="divide-y">
                      {gastosPersonal.gastos_personal.map((gasto, idx) => (
                        <div key={idx} className={`px-4 py-2 flex justify-between items-center text-sm ${gasto.tipo === 'nomina' ? 'bg-white' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            {gasto.tipo === 'nomina' ? (
                              <span className="text-green-600">💰</span>
                            ) : (
                              <span className="text-blue-600">🏛️</span>
                            )}
                            <div>
                              <span className={gasto.tipo === 'nomina' ? 'font-medium' : 'text-gray-600 text-xs'}>
                                {gasto.concepto}
                              </span>
                              {gasto.fecha_baja && gasto.tipo === 'nomina' && (
                                <p className="text-[10px] text-amber-600 font-medium">
                                  Baja: {new Date(gasto.fecha_baja).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                          </div>
                          <span className={`font-medium ${gasto.tipo === 'nomina' ? 'text-green-700' : 'text-blue-700 text-xs'}`}>
                            {formatNumber(gasto.importe)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-green-100 px-4 py-2 flex justify-between text-sm">
                      <span className="text-green-800">
                        Nóminas: {formatNumber(gastosPersonal.resumen?.total_nominas || 0)} | 
                        SS Empresa: {formatNumber(gastosPersonal.resumen?.total_ss_empresa || 0)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Lista de Gastos Fijos */}
                {gastosFijos.length === 0 && !cargandoGastosFijos ? (
                  <div className="text-center py-8">
                    <Repeat className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No hay gastos fijos configurados</p>
                    <p className="text-sm text-gray-500 mb-4">Haz clic en &quot;Actualizar&quot; para detectar patrones automáticamente</p>
                    <Button 
                      onClick={async () => {
                        setCargandoGastosFijos(true);
                        try {
                          const [gfRes, patRes, personalRes] = await Promise.all([
                            fetch(`${API_URL}/api/finanzas/gastos-fijos`, { headers }),
                            fetch(`${API_URL}/api/finanzas/gastos-fijos/detectar-patrones?meses_minimos=3`, { headers }),
                            fetch(`${API_URL}/api/finanzas/gastos-fijos/personal`, { headers })
                          ]);
                          const gfData = await gfRes.json();
                          const patData = await patRes.json();
                          const personalData = await personalRes.json();
                          setGastosFijos(gfData.gastos_fijos || []);
                          setPatronesDetectados(patData.sugerencias?.filter(s => !s.ya_existe_como_fijo) || []);
                          setGastosPersonal(personalData);
                        } catch (e) {
                          console.error(e);
                        } finally {
                          setCargandoGastosFijos(false);
                        }
                      }}
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Detectar patrones automáticamente
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {gastosFijos.map(gasto => (
                      <div 
                        key={gasto.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${gasto.fecha_baja ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            CATEGORIAS_GASTO.find(c => c.value === gasto.categoria)?.color || 'bg-gray-100'
                          }`}>
                            <Receipt className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{gasto.nombre}</p>
                            <p className="text-xs text-gray-500">
                              {CATEGORIAS_GASTO.find(c => c.value === gasto.categoria)?.label || gasto.categoria}
                              {gasto.periodicidad && gasto.periodicidad !== 'mensual' && (
                                <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">
                                  {gasto.periodicidad.toUpperCase()}
                                </span>
                              )}
                              {gasto.dia_estimado && ` • Día ~${gasto.dia_estimado}`}
                              {gasto.proveedor && ` • ${gasto.proveedor}`}
                            </p>
                            {gasto.fecha_baja && (
                              <p className="text-xs text-amber-600 font-medium mt-0.5">
                                Baja desde {new Date(gasto.fecha_baja).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className="font-bold text-lg">{formatNumber(gasto.importe_mensual || gasto.importe_estimado)}<span className="text-xs font-normal text-gray-500">/mes</span></p>
                            {gasto.periodicidad && gasto.periodicidad !== 'mensual' && (
                              <p className="text-xs text-gray-400">
                                ({formatNumber(gasto.importe_estimado)} {gasto.periodicidad === 'anual' ? 'al año' : gasto.periodicidad === 'semestral' ? 'cada 6 meses' : 'trimestral'})
                              </p>
                            )}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => setEditandoGastoFijo(gasto)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {gasto.fecha_baja ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-emerald-500 hover:text-emerald-700"
                              onClick={async () => {
                                try {
                                  await fetch(`${API_URL}/api/finanzas/gastos-fijos/${gasto.id}/reactivar`, {
                                    method: 'POST',
                                    headers
                                  });
                                  setGastosFijos(prev => prev.map(g => g.id === gasto.id ? { ...g, fecha_baja: null } : g));
                                } catch (e) {
                                  alert('Error: ' + e.message);
                                }
                              }}
                              title="Reactivar"
                              data-testid={`reactivar-gasto-${gasto.id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={async () => {
                                const fecha = window.prompt(
                                  `Fecha de baja para "${gasto.nombre}" (YYYY-MM-DD):\nEj: 2026-04-01 = deja de contar desde abril`,
                                  new Date().toISOString().slice(0, 10)
                                );
                                if (!fecha) return;
                                try {
                                  await fetch(`${API_URL}/api/finanzas/gastos-fijos/${gasto.id}?fecha_baja=${fecha}`, {
                                    method: 'DELETE',
                                    headers
                                  });
                                  setGastosFijos(prev => prev.map(g => g.id === gasto.id ? { ...g, fecha_baja: fecha } : g));
                                } catch (e) {
                                  alert('Error: ' + e.message);
                                }
                              }}
                              title="Dar de baja"
                              data-testid={`baja-gasto-${gasto.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Patrones detectados - Sugerencias */}
            {patronesDetectados.length > 0 && (
              <Card className="border-2 border-dashed border-purple-200">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-500" />
                        Patrones Detectados ({patronesDetectados.length})
                      </CardTitle>
                      <p className="text-xs text-gray-500">
                        Gastos que se repiten mensualmente. Añádelos o descártalos.
                      </p>
                    </div>
                    {patronesDetectados.length > 9 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setMostrarTodosPatrones(!mostrarTodosPatrones)}
                      >
                        {mostrarTodosPatrones ? 'Ver menos' : `Ver todos (${patronesDetectados.length})`}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {(mostrarTodosPatrones ? patronesDetectados : patronesDetectados.slice(0, 9)).map((patron, idx) => (
                      <div 
                        key={idx}
                        className="p-3 bg-purple-50 rounded-lg border border-purple-100 hover:border-purple-300 transition-colors relative group"
                      >
                        {/* Botón descartar (X) */}
                        <button
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                          title="No es gasto fijo (descartar)"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await fetch(`${API_URL}/api/finanzas/gastos-fijos/descartar-patron?patron=${encodeURIComponent(patron.patron)}`, {
                                method: 'POST',
                                headers
                              });
                              setPatronesDetectados(prev => prev.filter((_, i) => i !== idx));
                            } catch (e) {
                              alert('Error: ' + e.message);
                            }
                          }}
                        >
                          <X className="w-4 h-4" />
                        </button>
                        
                        {/* Contenido clickeable para añadir */}
                        <div 
                          className="cursor-pointer"
                          onClick={async () => {
                            if (!window.confirm(`¿Añadir "${patron.nombre_sugerido}" como gasto fijo?\n\nImporte estimado: ${formatNumber(patron.importe_medio)}\nDía estimado: ${patron.dia_estimado || 'Variable'}`)) return;
                            try {
                              const res = await fetch(`${API_URL}/api/finanzas/gastos-fijos?nombre=${encodeURIComponent(patron.nombre_sugerido)}&categoria=${patron.categoria_sugerida || 'otros'}&importe_estimado=${patron.importe_medio}&dia_estimado=${patron.dia_estimado || ''}`, {
                                method: 'POST',
                                headers
                              });
                              const data = await res.json();
                              if (data.success) {
                                setGastosFijos(prev => [...prev, data.gasto_fijo]);
                                setPatronesDetectados(prev => prev.filter((_, i) => i !== idx));
                                alert('✅ Gasto fijo añadido');
                              }
                            } catch (e) {
                              alert('Error: ' + e.message);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{patron.nombre_sugerido}</p>
                              <p className="text-xs text-gray-500 truncate">{patron.ejemplo_concepto}</p>
                            </div>
                            <Plus className="w-5 h-5 text-purple-500 flex-shrink-0" />
                          </div>
                          <div className="mt-2 flex items-center justify-between text-xs">
                            <span className="text-purple-600">{patron.meses_diferentes} meses</span>
                            <span className="font-bold">{formatNumber(patron.importe_medio)}</span>
                          </div>
                          {patron.variacion_porcentaje > 20 && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-orange-600">
                              <AlertTriangle className="w-3 h-3" />
                              Variación: ±{patron.variacion_porcentaje.toFixed(0)}%
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Modal Editar Gasto Fijo */}
            {editandoGastoFijo && (
              <Dialog open={!!editandoGastoFijo} onOpenChange={() => setEditandoGastoFijo(null)}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Editar Gasto Fijo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm text-gray-600">Nombre</label>
                      <Input 
                        value={editandoGastoFijo.nombre || ''}
                        onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, nombre: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">Categoría</label>
                        <select
                          className="w-full h-10 px-3 border rounded-md"
                          value={editandoGastoFijo.categoria || ''}
                          onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, categoria: e.target.value})}
                        >
                          <option value="">Seleccionar...</option>
                          {CATEGORIAS_GASTO.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Importe Estimado</label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={editandoGastoFijo.importe_estimado || ''}
                          onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, importe_estimado: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">Día del mes</label>
                        <Input 
                          type="number"
                          min="1"
                          max="31"
                          value={editandoGastoFijo.dia_estimado || ''}
                          onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, dia_estimado: parseInt(e.target.value) || null})}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Proveedor</label>
                        <Input 
                          value={editandoGastoFijo.proveedor || ''}
                          onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, proveedor: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Fecha de baja (opcional)</label>
                      <Input 
                        type="date"
                        value={editandoGastoFijo.fecha_baja || ''}
                        onChange={(e) => setEditandoGastoFijo({...editandoGastoFijo, fecha_baja: e.target.value || null})}
                        data-testid="edit-gasto-fecha-baja"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {editandoGastoFijo.fecha_baja 
                          ? `Este gasto dejará de contar desde ${new Date(editandoGastoFijo.fecha_baja).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`
                          : 'Deja vacío si el gasto sigue activo indefinidamente'
                        }
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setEditandoGastoFijo(null)}>
                      Cancelar
                    </Button>
                    <Button 
                      onClick={async () => {
                        try {
                          const params = new URLSearchParams();
                          if (editandoGastoFijo.nombre) params.append('nombre', editandoGastoFijo.nombre);
                          if (editandoGastoFijo.categoria) params.append('categoria', editandoGastoFijo.categoria);
                          if (editandoGastoFijo.importe_estimado) params.append('importe_estimado', editandoGastoFijo.importe_estimado);
                          if (editandoGastoFijo.dia_estimado) params.append('dia_estimado', editandoGastoFijo.dia_estimado);
                          if (editandoGastoFijo.proveedor !== undefined) params.append('proveedor', editandoGastoFijo.proveedor || '');
                          if (editandoGastoFijo.fecha_baja) params.append('fecha_baja', editandoGastoFijo.fecha_baja);
                          if (editandoGastoFijo.fecha_baja === null) params.append('fecha_baja', '');
                          
                          const res = await fetch(`${API_URL}/api/finanzas/gastos-fijos/${editandoGastoFijo.id}?${params.toString()}`, {
                            method: 'PUT',
                            headers
                          });
                          const data = await res.json();
                          if (data.success) {
                            setGastosFijos(prev => prev.map(g => g.id === editandoGastoFijo.id ? editandoGastoFijo : g));
                            setEditandoGastoFijo(null);
                            alert('✅ Gasto fijo actualizado');
                          } else {
                            alert(data.detail || 'Error actualizando');
                          }
                        } catch (e) {
                          alert('Error: ' + e.message);
                        }
                      }}
                    >
                      Guardar cambios
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Modal Nuevo Gasto Fijo */}
            {showNuevoGastoFijo && (
              <Dialog open={showNuevoGastoFijo} onOpenChange={setShowNuevoGastoFijo}>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Nuevo Gasto Fijo</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <label className="text-sm text-gray-600">Nombre *</label>
                      <Input 
                        placeholder="Ej: Alquiler nave, Luz, Internet..."
                        value={nuevoGastoFijo.nombre}
                        onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, nombre: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">Categoría *</label>
                        <select
                          className="w-full h-10 px-3 border rounded-md"
                          value={nuevoGastoFijo.categoria}
                          onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, categoria: e.target.value})}
                        >
                          <option value="">Seleccionar...</option>
                          {CATEGORIAS_GASTO.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Periodicidad *</label>
                        <select 
                          className="w-full px-3 py-2 border rounded-md"
                          value={nuevoGastoFijo.periodicidad}
                          onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, periodicidad: e.target.value})}
                        >
                          <option value="mensual">Mensual</option>
                          <option value="trimestral">Trimestral</option>
                          <option value="semestral">Semestral</option>
                          <option value="anual">Anual</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-600">
                          Importe {nuevoGastoFijo.periodicidad === 'mensual' ? '' : `(${nuevoGastoFijo.periodicidad})`} *
                        </label>
                        <Input 
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={nuevoGastoFijo.importe_estimado}
                          onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, importe_estimado: e.target.value})}
                        />
                        {nuevoGastoFijo.periodicidad !== 'mensual' && nuevoGastoFijo.importe_estimado && (
                          <p className="text-xs text-blue-600 mt-1">
                            ≈ {formatNumber(nuevoGastoFijo.importe_estimado / {trimestral: 3, semestral: 6, anual: 12}[nuevoGastoFijo.periodicidad])}/mes
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm text-gray-600">Día del mes (estimado)</label>
                        <Input 
                          type="number"
                          min="1"
                          max="31"
                          placeholder="Ej: 15"
                          value={nuevoGastoFijo.dia_estimado}
                          onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, dia_estimado: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Proveedor</label>
                      <Input 
                        placeholder="Ej: Iberdrola, Movistar..."
                        value={nuevoGastoFijo.proveedor}
                        onChange={(e) => setNuevoGastoFijo({...nuevoGastoFijo, proveedor: e.target.value})}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowNuevoGastoFijo(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      disabled={!nuevoGastoFijo.nombre || !nuevoGastoFijo.categoria || !nuevoGastoFijo.importe_estimado}
                      onClick={async () => {
                        try {
                          const params = new URLSearchParams({
                            nombre: nuevoGastoFijo.nombre,
                            categoria: nuevoGastoFijo.categoria,
                            importe_estimado: nuevoGastoFijo.importe_estimado,
                            periodicidad: nuevoGastoFijo.periodicidad
                          });
                          if (nuevoGastoFijo.dia_estimado) params.append('dia_estimado', nuevoGastoFijo.dia_estimado);
                          if (nuevoGastoFijo.proveedor) params.append('proveedor', nuevoGastoFijo.proveedor);
                          
                          const res = await fetch(`${API_URL}/api/finanzas/gastos-fijos?${params.toString()}`, {
                            method: 'POST',
                            headers
                          });
                          const data = await res.json();
                          if (data.success) {
                            setGastosFijos(prev => [...prev, data.gasto_fijo]);
                            setShowNuevoGastoFijo(false);
                            setNuevoGastoFijo({ nombre: '', categoria: '', importe_estimado: '', dia_estimado: '', proveedor: '', periodicidad: 'mensual' });
                            alert('✅ Gasto fijo creado');
                          } else {
                            alert(data.detail || 'Error creando gasto fijo');
                          }
                        } catch (e) {
                          alert('Error: ' + e.message);
                        }
                      }}
                    >
                      Guardar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}        {/* Tab Préstamos/Deudas - Componente Extraído */}
        {activeTab === 'prestamos' && (
          <PrestamosTab headers={headers} />
        )}

        {/* Tab Ingresos/Abonos */}
        {activeTab === 'ingresos' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Ingresos y Abonos Bancarios
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Gestiona cobros de clientes, devoluciones y fianzas recuperadas
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Total {añoSeleccionado}</p>
                  <p className="text-lg font-bold text-green-600">{formatNumber(abonosTotalImporte)}</p>
                </div>
                {abonosPendientes > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Pendientes vincular</p>
                    <p className="text-lg font-bold text-orange-600">{formatNumber(abonosPendientes)}</p>
                  </div>
                )}
                <ConexionBancaria onSyncCompleta={() => cargarDatosPestaña()} />
                <div className="flex flex-col items-end gap-1">
                  <Button
                    variant="outline"
                    onClick={() => setShowImportarCargos(true)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importar CSV Banco
                  </Button>
                  {ultimaImportacion?.fecha && (
                    <p
                      className="text-[11px] text-gray-400"
                      title={ultimaImportacion.filename ? `Archivo: ${ultimaImportacion.filename}${ultimaImportacion.total_importados !== undefined ? ` (${ultimaImportacion.total_importados} importados)` : ''}` : undefined}
                    >
                      Ultima subida: {new Date(ultimaImportacion.fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filtros */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Select value={filtroTipoAbono || 'all'} onValueChange={(v) => setFiltroTipoAbono(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Todos los tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="pago_cliente">Pago de cliente</SelectItem>
                    <SelectItem value="devolucion_proveedor">Devolución proveedor</SelectItem>
                    <SelectItem value="fianza_recuperada">Fianza recuperada</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroAbonoVinculado === null ? 'all' : String(filtroAbonoVinculado)} onValueChange={(v) => setFiltroAbonoVinculado(v === 'all' ? null : v === 'true')}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="true">Vinculados</SelectItem>
                    <SelectItem value="false">Sin vincular</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={cargarAbonos} disabled={cargandoAbonos}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${cargandoAbonos ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
              </div>

              {/* Leyenda */}
              <div className="flex gap-4 mb-4 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-green-100 rounded"></span> Pago cliente
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-blue-100 rounded"></span> Devolución
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-purple-100 rounded"></span> Fianza
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-gray-100 rounded"></span> Sin vincular
                </span>
              </div>

              {cargandoAbonos ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                  Cargando abonos...
                </div>
              ) : abonos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No hay abonos para mostrar</p>
                  <p className="text-sm">Importa movimientos bancarios para ver los ingresos</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Fecha</th>
                        <th className="px-3 py-2 text-left">Concepto</th>
                        <th className="px-3 py-2 text-right">Importe</th>
                        <th className="px-3 py-2 text-center">Tipo</th>
                        <th className="px-3 py-2 text-left">Vinculado a</th>
                        <th className="px-3 py-2 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {abonos.map(abono => {
                        const tipoColor = abono.tipo_abono === 'pago_cliente' ? 'bg-green-100 text-green-800' :
                                         abono.tipo_abono === 'devolucion_proveedor' ? 'bg-blue-100 text-blue-800' :
                                         abono.tipo_abono === 'fianza_recuperada' ? 'bg-purple-100 text-purple-800' :
                                         'bg-gray-100 text-gray-800';
                        const vinculado = abono.factura_emitida_id || abono.presupuesto_id || abono.devolucion_de_cargo_id || abono.vinculado === true || abono.proveedor_devolucion || abono.factura_soportada_id;
                        
                        return (
                          <tr key={abono.id} className={`border-t hover:bg-gray-50 ${!vinculado ? 'bg-orange-50' : ''}`}>
                            <td className="px-3 py-2 whitespace-nowrap">{formatFecha(abono.fecha)}</td>
                            <td className="px-3 py-2">
                              <span className="line-clamp-1" title={abono.concepto}>
                                {abono.concepto?.slice(0, 60)}{abono.concepto?.length > 60 ? '...' : ''}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-green-600">
                              +{formatNumber(abono.importe)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {abono.tipo_abono ? (
                                <span className={`px-2 py-0.5 rounded text-xs ${tipoColor}`}>
                                  {abono.tipo_abono === 'pago_cliente' ? 'Cobro' :
                                   abono.tipo_abono === 'devolucion_proveedor' ? 'Devolución' :
                                   abono.tipo_abono === 'fianza_recuperada' ? 'Fianza' : 'Otro'}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              {abono.factura_emitida ? (
                                <span className="text-green-700">
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  Fact. {abono.factura_emitida.numero_factura} ({abono.factura_emitida.cliente_nombre?.slice(0, 20)})
                                </span>
                              ) : abono.presupuesto ? (
                                <span className="text-blue-700">
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  Presup. #{abono.presupuesto.numero} ({abono.presupuesto.cliente_nombre?.slice(0, 20)})
                                </span>
                              ) : abono.cargo_relacionado ? (
                                <span className="text-blue-700">
                                  <Link2 className="w-3 h-3 inline mr-1" />
                                  {abono.cargo_relacionado.concepto?.slice(0, 30)}
                                </span>
                              ) : abono.proveedor_devolucion ? (
                                <span className="text-purple-700">
                                  <Link2 className="w-3 h-3 inline mr-1" />
                                  Dev. {abono.proveedor_devolucion?.slice(0, 25)}
                                </span>
                              ) : abono.factura_soportada_id ? (
                                <span className="text-blue-700">
                                  <Link2 className="w-3 h-3 inline mr-1" />
                                  {abono.tipo_abono === 'fianza_recuperada' ? 'Fianza' : 'Dev.'} {abono.factura_soportada?.proveedor_nombre?.slice(0, 25) || abono.factura_soportada?.numero_factura || 'vinculada'}
                                </span>
                              ) : abono.vinculado && abono.facturas_vinculadas?.length > 0 ? (
                                <span className="text-green-700">
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  {abono.facturas_vinculadas.length} factura(s)
                                </span>
                              ) : abono.vinculado && abono.presupuestos_vinculados?.length > 0 ? (
                                <span className="text-blue-700">
                                  <FileText className="w-3 h-3 inline mr-1" />
                                  {abono.presupuestos_vinculados.length} presupuesto(s)
                                </span>
                              ) : (
                                <span className="text-orange-500 text-xs">Sin vincular</span>
                              )}
                              {abono.a3doc_document_id && (
                                <span
                                  title={abono.a3doc_subido_en ? `Subido a a3doc el ${formatFechaHora(abono.a3doc_subido_en)}` : 'Subido a a3doc'}
                                  className="inline-flex items-center gap-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded px-1 py-0.5 ml-2"
                                >
                                  <CheckCircle2 className="w-3 h-3" /> a3doc
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex justify-center gap-1">
                                {!vinculado ? (
                                  <>
                                    {/* Botón Vincular V3 - Usa el sistema V3 directamente */}
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="h-7 text-xs border-green-500 text-green-600 hover:bg-green-50"
                                      onClick={() => {
                                        setAbonoSeleccionado(abono);
                                        cargarSugerenciasV3(abono.id);
                                        setShowVincularV3(true);
                                      }}
                                      title="Vincular ingreso"
                                    >
                                      <Link2 className="w-3 h-3 mr-1" />
                                      Vincular
                                    </Button>
                                  </>
                                ) : (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 text-xs text-red-600"
                                    onClick={() => desvincularAbono(abono.id)}
                                  >
                                    <Unlink className="w-3 h-3 mr-1" />
                                    Desvincular
                                  </Button>
                                )}
                                {/* Botón eliminar - solo si no está vinculado */}
                                {!abono.vinculado && !abono.factura_emitida_id && !abono.presupuesto_id && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => eliminarAbono(abono.id, abono)}
                                    title="Eliminar ingreso"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              <div className="mt-4 text-sm text-gray-500">
                Mostrando {abonos.length} de {abonosTotal} abonos
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab Migraciones */}
        {activeTab === 'a3doc' && (
          <A3docTab />
        )}

        {/* Tab Nóminas: pago con la remesa del BBVA */}
        {activeTab === 'nominas' && (
          <NominasTab />
        )}
      </div>

      {/* Modal Subir Factura con IA */}
      <Dialog open={showSubirFactura} onOpenChange={setShowSubirFactura}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Subir Factura (Extracción con IA)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setArchivoSeleccionado(e.target.files[0])}
                className="hidden"
                id="factura-upload"
              />
              <label htmlFor="factura-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  {archivoSeleccionado ? archivoSeleccionado.name : 'Arrastra una factura o haz clic para seleccionar'}
                </p>
                <p className="text-xs text-gray-400 mt-2">PDF, PNG, JPG (máx. 10MB)</p>
              </label>
            </div>
            
            {archivoSeleccionado && (
              <div className="bg-green-50 p-3 rounded-lg flex items-center justify-between">
                <span className="text-green-700">{archivoSeleccionado.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setArchivoSeleccionado(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubirFactura(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={extraerFacturaConIA} 
              disabled={!archivoSeleccionado || extracting}
            >
              {extracting ? 'Extrayendo...' : 'Extraer Datos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nueva Factura Soportada */}
      <Dialog open={showNuevaFactura} onOpenChange={setShowNuevaFactura}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {datosExtraidos ? 'Revisar Datos Extraídos' : 'Nueva Factura Soportada'}
            </DialogTitle>
          </DialogHeader>
          
          {datosExtraidos && (
            <div className="bg-blue-50 p-3 rounded-lg mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-blue-700">
                Datos extraídos automáticamente. Confianza: {Math.round((datosExtraidos.confianza || 0) * 100)}%. Revisa y corrige si es necesario.
              </span>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 md:col-span-1">
              <label className="text-sm font-medium">Proveedor *</label>
              <Input 
                value={formFactura.proveedor_nombre}
                onChange={(e) => setFormFactura({...formFactura, proveedor_nombre: e.target.value})}
                placeholder="Nombre del proveedor"
              />
            </div>
            <div>
              <label className="text-sm font-medium">CIF</label>
              <Input 
                value={formFactura.proveedor_cif}
                onChange={(e) => setFormFactura({...formFactura, proveedor_cif: e.target.value})}
                placeholder="B12345678"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nº Factura *</label>
              <Input 
                value={formFactura.numero_factura}
                onChange={(e) => setFormFactura({...formFactura, numero_factura: e.target.value})}
                placeholder="FAC-2025-001"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Fecha Factura *</label>
              <Input 
                type="date"
                value={formFactura.fecha_factura}
                onChange={(e) => setFormFactura({...formFactura, fecha_factura: e.target.value})}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Categoría</label>
              <Select 
                value={formFactura.categoria} 
                onValueChange={(v) => setFormFactura({...formFactura, categoria: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS_GASTO.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Base Imponible *</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.base_imponible}
                onChange={(e) => {
                  const base = parseFloat(e.target.value) || 0;
                  const iva = formFactura.iva_4 + formFactura.iva_10 + formFactura.iva_21;
                  setFormFactura({
                    ...formFactura, 
                    base_imponible: base,
                    total_iva: iva,
                    total: base + iva
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium">IVA 4%</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.iva_4}
                onChange={(e) => {
                  const iva4 = parseFloat(e.target.value) || 0;
                  const totalIva = iva4 + formFactura.iva_10 + formFactura.iva_21;
                  setFormFactura({
                    ...formFactura, 
                    iva_4: iva4,
                    total_iva: totalIva,
                    total: formFactura.base_imponible + totalIva
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium">IVA 10%</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.iva_10}
                onChange={(e) => {
                  const iva10 = parseFloat(e.target.value) || 0;
                  const totalIva = formFactura.iva_4 + iva10 + formFactura.iva_21;
                  setFormFactura({
                    ...formFactura, 
                    iva_10: iva10,
                    total_iva: totalIva,
                    total: formFactura.base_imponible + totalIva
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium">IVA 21%</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.iva_21}
                onChange={(e) => {
                  const iva21 = parseFloat(e.target.value) || 0;
                  const totalIva = formFactura.iva_4 + formFactura.iva_10 + iva21;
                  setFormFactura({
                    ...formFactura, 
                    iva_21: iva21,
                    total_iva: totalIva,
                    total: formFactura.base_imponible + totalIva
                  });
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Total IVA</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.total_iva}
                readOnly
                className="bg-gray-50"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Total Factura</label>
              <Input 
                type="number"
                step="0.01"
                value={formFactura.total}
                readOnly
                className="bg-gray-50 font-bold"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium">Notas</label>
              <Input 
                value={formFactura.notas}
                onChange={(e) => setFormFactura({...formFactura, notas: e.target.value})}
                placeholder="Observaciones..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowNuevaFactura(false);
              setDatosExtraidos(null);
            }}>
              Cancelar
            </Button>
            <Button onClick={guardarFacturaSoportada}>
              Guardar Factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Importar Movimientos Bancarios */}
      <Dialog open={showImportarCargos} onOpenChange={(open) => { if (!importandoCargos) setShowImportarCargos(open); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Importar Movimientos Bancarios</DialogTitle>
          </DialogHeader>
          {importandoCargos ? (
            <div className="py-12 text-center space-y-4">
              <div className="w-12 h-12 border-4 border-duetto-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-lg font-medium text-gray-800">Procesando archivo...</p>
              <p className="text-sm text-gray-500">Esto puede tardar unos segundos dependiendo del tamaño del archivo.</p>
              <p className="text-xs text-gray-400">Detectando duplicados y categorizando movimientos</p>
            </div>
          ) : (
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Sube un archivo CSV del banco con los movimientos. Se importarán automáticamente los <strong>cargos</strong> (gastos) y los <strong>abonos</strong> (ingresos).
            </p>
            <div className="bg-blue-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">Formato esperado (BBVA):</p>
              <p className="text-blue-700">Columnas: Fecha, Importe, Concepto/Observaciones</p>
              <p className="text-blue-600 text-xs mt-1">Los importes negativos se guardan como cargos, los positivos como abonos.</p>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    importarCargosBancarios(e.target.files[0]);
                  }
                }}
                className="hidden"
                id="cargos-upload"
              />
              <label htmlFor="cargos-upload" className="cursor-pointer">
                <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Haz clic para seleccionar archivo</p>
                <p className="text-xs text-gray-400 mt-2">CSV, Excel (máx. 5MB)</p>
              </label>
            </div>
          </div>
          )}
          {!importandoCargos && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportarCargos(false)}>
              Cancelar
            </Button>
          </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Editar Categoría */}
      <Dialog open={showEditarCategoria} onOpenChange={setShowEditarCategoria}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Editar Categoría</DialogTitle>
          </DialogHeader>
          {cargoSeleccionado && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{limpiarConcepto(cargoSeleccionado.concepto).proveedor}</p>
                <p className="text-sm text-gray-500">{formatFecha(cargoSeleccionado.fecha)} - {formatNumber(cargoSeleccionado.importe)}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Seleccionar Categoría</label>
                <Select
                  value={cargoSeleccionado.categoria || ''}
                  onValueChange={async (value) => {
                    try {
                      const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${cargoSeleccionado.id}`, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ categoria: value })
                      });
                      if (response.ok) {
                        setCargosBancarios(prev => prev.map(c => 
                          c.id === cargoSeleccionado.id ? {...c, categoria: value} : c
                        ));
                        setShowEditarCategoria(false);
                        setCargoSeleccionado(null);
                      }
                    } catch (error) {
                      console.error('Error actualizando categoría:', error);
                    }
                  }}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border shadow-lg z-[100] max-h-60 overflow-y-auto">
                    {CATEGORIAS_GASTO.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value || 'empty'}>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${cat.color}`}>{cat.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Crear nueva categoría */}
              <div className="border-t pt-4">
                <label className="text-sm font-medium mb-2 block">O crear nueva categoría</label>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Nombre de la categoría..."
                    value={nuevaCategoria}
                    onChange={(e) => setNuevaCategoria(e.target.value)}
                    className="flex-1"
                  />
                  <Button 
                    onClick={async () => {
                      if (!nuevaCategoria.trim()) return;
                      
                      const nuevaCatValue = nuevaCategoria.toLowerCase().replace(/\s+/g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                      const nuevaCatObj = {
                        value: nuevaCatValue,
                        label: nuevaCategoria.trim(),
                        color: 'bg-gray-100 text-gray-800'
                      };
                      
                      // Añadir a la lista de categorías
                      setCategoriasGasto(prev => [...prev, nuevaCatObj]);
                      
                      // Asignar al cargo actual
                      try {
                        const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${cargoSeleccionado.id}`, {
                          method: 'PUT',
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({ categoria: nuevaCatValue })
                        });
                        if (response.ok) {
                          setCargosBancarios(prev => prev.map(c => 
                            c.id === cargoSeleccionado.id ? {...c, categoria: nuevaCatValue} : c
                          ));
                          setNuevaCategoria('');
                          setShowEditarCategoria(false);
                          setCargoSeleccionado(null);
                        }
                      } catch (error) {
                        console.error('Error:', error);
                      }
                    }}
                    disabled={!nuevaCategoria.trim()}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crear
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditarCategoria(false);
              setCargoSeleccionado(null);
              setNuevaCategoria('');
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Marcar como Gasto Directo / Vincular a Financiación */}
      <Dialog open={showMarcarGastoDirecto} onOpenChange={(open) => {
        setShowMarcarGastoDirecto(open);
        if (!open) {
          setCategoriaGastoDirecto('');
          setDescripcionGastoDirecto('');
          setModoGastoDirecto('gasto');
          setOperacionFinanciacionId('');
          setTipoMovimientoFinanciacion('');
        }
        // El fetch de operacionesFinanciacion se dispara en el useEffect
        // que escucha showMarcarGastoDirecto/showVincularV3 (ver arriba).
      }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-500" />
              Gasto Directo o Financiación
            </DialogTitle>
          </DialogHeader>
          {cargoSeleccionado && (
            <div className="space-y-4 py-4">
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="font-medium text-purple-900">{limpiarConcepto(cargoSeleccionado.concepto).proveedor}</p>
                <p className="text-sm text-purple-700">{formatFecha(cargoSeleccionado.fecha)} - {formatNumber(cargoSeleccionado.importe)}</p>
              </div>

              {/* Toggle modo */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${modoGastoDirecto === 'gasto' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                  onClick={() => setModoGastoDirecto('gasto')}
                >
                  Gasto Directo
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${modoGastoDirecto === 'financiacion' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                  onClick={() => setModoGastoDirecto('financiacion')}
                >
                  Vincular a Financiación
                </button>
              </div>

              {modoGastoDirecto === 'gasto' && (
                <>
                  <div className="bg-yellow-50 p-3 rounded-lg text-sm">
                    <p className="font-medium text-yellow-800">⚠️ ¿Qué es un gasto directo?</p>
                    <p className="text-yellow-700 mt-1">
                      Gastos como nóminas, Seguridad Social o impuestos que <strong>no tienen factura con IVA</strong>.
                      Se contabilizan como gasto pero <strong>no generan IVA deducible</strong>.
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Categoría *</label>
                    <select
                      className="w-full h-10 mt-1 px-3 border rounded-md"
                      value={categoriaGastoDirecto}
                      onChange={(e) => setCategoriaGastoDirecto(e.target.value)}
                    >
                      <option value="">Seleccionar categoría...</option>
                      <option value="nominas">Nóminas</option>
                      <option value="seguridad_social">Seguridad Social</option>
                      <option value="impuestos">Impuestos (IVA, IRPF...)</option>
                      <option value="comisiones_bancarias">Comisiones bancarias</option>
                      <option value="seguros">Seguros</option>
                      <option value="alquileres">Alquileres</option>
                      <option value="otros_sin_factura">Otros sin factura</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Descripción (opcional)</label>
                    <Input
                      className="mt-1"
                      placeholder="Ej: Nómina enero 2024, TC1 febrero..."
                      value={descripcionGastoDirecto}
                      onChange={(e) => setDescripcionGastoDirecto(e.target.value)}
                    />
                  </div>
                </>
              )}

              {modoGastoDirecto === 'financiacion' && (
                <>
                  <div className="bg-red-50 p-3 rounded-lg text-sm border border-red-100">
                    <p className="font-medium text-red-800">Vincular a operación de financiación</p>
                    <p className="text-red-700 mt-1">
                      Elige primero el préstamo, después el tipo de movimiento.
                      Capital (cuota) reduce la deuda. Comisión e interés son gastos financieros.
                    </p>
                  </div>

                  {/* Selector 1: el préstamo */}
                  <div>
                    <label className="text-sm font-medium text-gray-700">Préstamo / operación *</label>
                    <select
                      className="w-full h-10 mt-1 px-3 border rounded-md"
                      value={operacionFinanciacionId}
                      onChange={(e) => {
                        setOperacionFinanciacionId(e.target.value);
                        // Reset del tipo al cambiar de operación: las opciones
                        // dependen del tipo de operación, así que forzamos
                        // elección consciente del usuario.
                        setTipoMovimientoFinanciacion('');
                      }}
                    >
                      <option value="">Selecciona préstamo...</option>
                      {operacionesFinanciacion.map(op => {
                        const prefijo = {
                          click_pay: 'C&C',
                          prestamo: 'Prest',
                          prestamo_socio: 'Socio',
                          linea_credito: 'Linea Cred',
                        }[op.tipo] || (op.tipo || '').toUpperCase();
                        const nombre = op.tipo === 'prestamo_socio' && op.socio_nombre
                          ? `${op.nombre} (${op.socio_nombre})`
                          : op.nombre;
                        const sufijoCerrada = op.estado === 'cerrada' ? ' [cerrada]' : '';
                        const r = op.resumen || {};
                        const partes = [`${prefijo} · ${nombre}${sufijoCerrada}`];
                        if (op.tipo === 'linea_credito') {
                          partes.push(`pendiente ${formatNumber(r.deuda_capital || 0)}`);
                          if (r.disponible != null) {
                            partes.push(`disponible ${formatNumber(r.disponible)}`);
                          }
                        } else if (op.num_cuotas) {
                          const cuotaN = r.proxima_cuota_numero
                            || ((op.cuotas_pagadas_real || op.cuotas_pagadas || 0) + 1);
                          partes.push(`cuota ${cuotaN}/${op.num_cuotas}`);
                          partes.push(`pendiente ${formatNumber(r.deuda_capital || 0)}`);
                          if (r.proxima_cuota_fecha) {
                            partes.push(
                              `próx. ${r.proxima_cuota_fecha} (${formatNumber(r.proxima_cuota_importe || 0)})`
                            );
                          }
                        }
                        return (
                          <option key={op.id} value={op.id}>
                            {partes.join(' · ')}
                          </option>
                        );
                      })}
                    </select>
                    {operacionesFinanciacion.length === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        No hay operaciones activas. Créalas desde la pestaña Deudas.
                      </p>
                    )}
                  </div>

                  {/* Selector 2: tipo de movimiento — solo cuando hay préstamo elegido */}
                  {operacionFinanciacionId && (() => {
                    const op = operacionesFinanciacion.find(o => o.id === operacionFinanciacionId);
                    if (!op) return null;
                    // Este modal vincula CARGOS (salidas de banco) a operaciones,
                    // así que solo tiene sentido tipos que son salida de dinero:
                    // cuota_capital, interés y comisión. El desembolso (entrada de
                    // capital del banco al socio o del banco a la empresa) se vincula
                    // desde el modal de Abonos, no aquí.
                    const tipos = [];
                    if (op.tipo === 'linea_credito') {
                      tipos.push({ value: 'cuota_capital', label: 'Devolución de capital' });
                      tipos.push({ value: 'interes',       label: 'Interés' });
                      tipos.push({ value: 'comision',      label: 'Comisión' });
                    } else if (op.tipo === 'prestamo') {
                      tipos.push({ value: 'cuota_capital', label: 'Pago de cuota (o amortización extra)' });
                      tipos.push({ value: 'interes',       label: 'Interés' });
                      tipos.push({ value: 'comision',      label: 'Comisión' });
                    } else if (op.tipo === 'prestamo_socio') {
                      tipos.push({ value: 'cuota_capital', label: 'Devolución al socio' });
                    } else {
                      // click_pay
                      tipos.push({ value: 'cuota_capital', label: 'Pago de cuota (o amortización extra)' });
                      tipos.push({ value: 'comision',      label: 'Comisión' });
                    }
                    const explicaciones = {
                      cuota_capital: 'Pago al banco para devolver capital. Reduce la deuda pendiente. No es gasto.',
                      interes: 'Coste financiero del préstamo. Cuenta como gasto financiero.',
                      comision: 'Comisión bancaria (apertura, mantenimiento). Cuenta como gasto financiero.',
                    };
                    return (
                      <div>
                        <label className="text-sm font-medium text-gray-700">Tipo de movimiento *</label>
                        <select
                          className="w-full h-10 mt-1 px-3 border rounded-md"
                          value={tipoMovimientoFinanciacion}
                          onChange={(e) => setTipoMovimientoFinanciacion(e.target.value)}
                        >
                          <option value="">Selecciona tipo...</option>
                          {tipos.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        {tipoMovimientoFinanciacion && explicaciones[tipoMovimientoFinanciacion] && (
                          <p className="text-xs text-gray-600 mt-2 bg-gray-50 rounded p-2 border border-gray-100">
                            {explicaciones[tipoMovimientoFinanciacion]}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarcarGastoDirecto(false)}>
              Cancelar
            </Button>
            {modoGastoDirecto === 'gasto' ? (
              <Button
                disabled={!categoriaGastoDirecto}
                onClick={async () => {
                  try {
                    const params = new URLSearchParams({
                      categoria: categoriaGastoDirecto
                    });
                    if (descripcionGastoDirecto) {
                      params.append('descripcion', descripcionGastoDirecto);
                    }

                    const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${cargoSeleccionado.id}/marcar-gasto-directo?${params.toString()}`, {
                      method: 'POST',
                      headers
                    });
                    const data = await res.json();

                    if (data.success) {
                      alert(`✅ Gasto marcado como "${categoriaGastoDirecto}"\n\nImporte: ${formatNumber(data.importe)}\nIVA deducible: 0€`);
                      setShowMarcarGastoDirecto(false);
                      setCategoriaGastoDirecto('');
                      setDescripcionGastoDirecto('');
                      cargarDatosPestaña();
                    } else {
                      alert(data.detail || 'Error marcando gasto');
                    }
                  } catch (e) {
                    alert('Error: ' + e.message);
                  }
                }}
              >
                Marcar como Gasto Directo
              </Button>
            ) : (
              <Button
                disabled={!operacionFinanciacionId || !tipoMovimientoFinanciacion}
                onClick={async () => {
                  try {
                    const params = new URLSearchParams({
                      cargo_id: cargoSeleccionado.id,
                      tipo_movimiento: tipoMovimientoFinanciacion,
                    });
                    const res = await fetch(`${API_URL}/api/finanzas/operaciones-financiacion/${operacionFinanciacionId}/vincular-cargo?${params.toString()}`, {
                      method: 'POST',
                      headers
                    });
                    const data = await res.json();
                    if (data.success) {
                      const msg = `✅ Cargo vinculado a la operación como ${tipoMovimientoFinanciacion}`;
                      alert(data.warning ? `${msg}\n\n⚠️ ${data.warning}` : msg);
                      setShowMarcarGastoDirecto(false);
                      setModoGastoDirecto('gasto');
                      setOperacionFinanciacionId('');
                      setTipoMovimientoFinanciacion('');
                      // Refrescar el listado de operaciones para que el próximo
                      // abrir del modal vea la deuda actualizada (cuota_capital
                      // baja el saldo en el backend).
                      recargarOperacionesFinanciacion();
                      cargarDatosPestaña();
                    } else {
                      alert(data.detail || 'Error vinculando');
                    }
                  } catch (e) {
                    alert('Error: ' + e.message);
                  }
                }}
              >
                Vincular a Operación
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Vincular Factura */}
      <Dialog open={showVincularFactura} onOpenChange={setShowVincularFactura}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Vincular Factura al Gasto</DialogTitle>
          </DialogHeader>
          {cargoSeleccionado && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-900">{limpiarConcepto(cargoSeleccionado.concepto).proveedor}</p>
                <p className="text-sm text-blue-700">{formatFecha(cargoSeleccionado.fecha)} - {formatNumber(cargoSeleccionado.importe)}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Opción 1: Subir Factura con IA */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  onClick={() => {
                    setShowVincularFactura(false);
                    setShowSubirFactura(true);
                  }}
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="font-medium text-gray-700">Subir Factura</p>
                  <p className="text-xs text-gray-500">Extracción automática con IA</p>
                </div>
                
                {/* Opción 2: Entrada Manual */}
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                  onClick={() => {
                    // Pre-rellenar el formulario con datos del cargo
                    const { proveedor } = limpiarConcepto(cargoSeleccionado.concepto);
                    setFormFactura({
                      ...formFactura,
                      proveedor_nombre: proveedor,
                      fecha_factura: cargoSeleccionado.fecha,
                      total: cargoSeleccionado.importe,
                      base_imponible: cargoSeleccionado.importe / 1.10,  // Asumir 10% IVA
                      total_iva: cargoSeleccionado.importe - (cargoSeleccionado.importe / 1.10),
                      iva_10: cargoSeleccionado.importe - (cargoSeleccionado.importe / 1.10),
                      categoria: cargoSeleccionado.categoria
                    });
                    setShowVincularFactura(false);
                    setShowNuevaFactura(true);
                  }}
                >
                  <Edit className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="font-medium text-gray-700">Entrada Manual</p>
                  <p className="text-xs text-gray-500">Completar datos a mano</p>
                </div>
              </div>
              
              <div className="text-center text-xs text-gray-400 pt-2">
                La factura quedará vinculada automáticamente a este cargo bancario
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowVincularFactura(false);
              setCargoSeleccionado(null);
            }}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Procesar Factura desde Drive */}
      <Dialog open={showProcesarFactura} onOpenChange={(open) => {
        if (!open) {
          setShowProcesarFactura(false);
          setArchivoEnProceso(null);
          setDatosFacturaIA(null);
          setCargosMatch([]);
          setSubcargosMatch([]);
          setCargosSeleccionados([]);
          setFiltroConceptoCargos('');
          setFiltroFechaCargos('');
          setFiltroImporteCargos('');
          setFiltroAnioCargos(anioActualCargos);
          setFiltroMesCargos('');
          setDatosDesdeCache(false);
          setCargoYaVinculado(null);
        }
      }}>
        <DialogContent className="bg-white w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-3 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="truncate">Procesar: {archivoEnProceso?.name?.substring(0, 30)}...</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {/* Columna izquierda: Vista previa - Oculta en móvil, solo botón */}
              <div className="space-y-3 sm:space-y-4">
                {/* En móvil solo mostrar botón para abrir en Drive */}
                <div className="lg:hidden">
                  {archivoEnProceso?.webViewLink && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => window.open(archivoEnProceso.webViewLink, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Ver factura en Drive
                    </Button>
                  )}
                </div>
                
                {/* Vista previa iframe - Solo desktop */}
                <div className="hidden lg:block border rounded-lg overflow-hidden bg-gray-50">
                  <div className="p-2 bg-gray-100 border-b flex items-center justify-between">
                    <span className="text-sm font-medium">Vista Previa</span>
                    {archivoEnProceso?.webViewLink && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => window.open(archivoEnProceso.webViewLink, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Abrir
                      </Button>
                    )}
                  </div>
                  <div className="h-[350px]">
                    {archivoEnProceso?.webViewLink ? (
                      <iframe
                        src={`https://drive.google.com/file/d/${archivoEnProceso.id}/preview`}
                        className="w-full h-full"
                        title="Vista previa"
                        allow="autoplay"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400">
                        <FileText className="w-12 h-12" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Incidencia / Observaciones (debajo de la vista previa) */}
                {datosFacturaIA && !datosFacturaIA.error && (
                  <div className="hidden lg:block border rounded-lg p-3 bg-amber-50/40">
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Incidencia / Observaciones
                      </label>
                      {datosFacturaIA.incidencia?.estado && (
                        <Badge className={datosFacturaIA.incidencia.estado === 'resuelta' ? 'bg-green-100 text-green-700 font-normal' : 'bg-amber-100 text-amber-800 font-normal'}>
                          {datosFacturaIA.incidencia.estado === 'resuelta' ? 'Resuelta' : 'Abierta'}
                        </Badge>
                      )}
                    </div>
                    <textarea
                      className="w-full text-xs sm:text-sm border rounded-md px-2 py-1.5 h-16 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                      placeholder="Observaciones: importe que no cuadra, falta base/IVA, factura duplicada, etc."
                      value={datosFacturaIA.incidencia?.texto || ''}
                      onChange={(e) => setDatosFacturaIA({ ...datosFacturaIA, incidencia: { ...(datosFacturaIA.incidencia || {}), texto: e.target.value } })}
                    />
                    {(() => {
                      const fid = datosFacturaIA.factura_drive_id;
                      const inc = datosFacturaIA.incidencia;
                      const onSaved = (nueva) => setDatosFacturaIA(prev => ({ ...prev, incidencia: nueva }));
                      return (
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => guardarIncidencia('abierta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                            Guardar
                          </Button>
                          {inc?.texto && inc?.estado !== 'resuelta' && (
                            <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => guardarIncidencia('resuelta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Resuelta
                            </Button>
                          )}
                          {inc?.estado === 'resuelta' && (
                            <Button size="sm" variant="outline" onClick={() => guardarIncidencia('abierta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reabrir
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => abrirAvisarEquipo(fid)}>
                            <Users className="w-3.5 h-3.5 mr-1" /> Avisar
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => abrirEmailProveedor(fid)}>
                            <Mail className="w-3.5 h-3.5 mr-1" /> Proveedor
                          </Button>
                        </div>
                      );
                    })()}
                    {datosFacturaIA.incidencia?.estado === 'abierta' && (
                      <p className="text-[11px] text-amber-700 mt-1">Con la incidencia abierta no se puede vincular la factura.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Columna derecha: Datos extraídos y matches */}
              <div className="space-y-3 sm:space-y-4">
                {/* Estado de procesamiento */}
                {procesandoIA && (
                  <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                    <RefreshCw className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-800 text-sm">Extrayendo datos con IA...</p>
                      <p className="text-xs text-blue-600">Analizando factura</p>
                    </div>
                  </div>
                )}

                {/* Indicador de datos desde caché */}
                {datosDesdeCache && datosFacturaIA && !datosFacturaIA.error && (
                  <div className="p-2 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
                    <Zap className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-xs text-green-800">Datos cargados instantáneamente (ya procesado)</span>
                  </div>
                )}

                {/* Alerta si ya está vinculada */}
                {cargoYaVinculado && (
                  <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">Esta factura ya está vinculada</span>
                    </div>
                    <div className="bg-white rounded p-2 text-xs">
                      <p><strong>Cargo:</strong> {cargoYaVinculado.concepto}</p>
                      <p><strong>Importe:</strong> {cargoYaVinculado.importe?.toLocaleString('es-ES', {minimumFractionDigits: 2})}€</p>
                      <p><strong>Fecha:</strong> {cargoYaVinculado.fecha}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full text-xs"
                      onClick={() => {
                        // Abrir modal de ver cargo o redirigir a soportadas
                        window.alert(`Ver cargo vinculado: ${cargoYaVinculado.id}\n\nPuedes ver este cargo en la pestaña "Soportadas"`);
                      }}
                    >
                      <Eye className="w-3 h-3 mr-2" />
                      Ver detalles del cargo
                    </Button>
                  </div>
                )}

                {/* Error */}
                {datosFacturaIA?.error && (
                  <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium text-red-800 text-sm">Error extrayendo datos</p>
                    <p className="text-xs text-red-600">{datosFacturaIA.error}</p>
                  </div>
                )}

                {/* Datos extraídos - EDITABLES */}
                {datosFacturaIA && !datosFacturaIA.error && (
                  <div className="border rounded-lg">
                    <div className="p-2 bg-green-50 border-b flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-green-800 truncate">
                          Extraído ({Math.round((datosFacturaIA.confianza || 0) * 100)}%)
                        </span>
                        {esNotaCredito && (
                          <Badge className="bg-orange-100 text-orange-800 text-[10px] px-1">
                            Nota de Crédito
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 hidden sm:inline">Editable</span>
                    </div>
                    <div className="p-2 sm:p-3 space-y-2 text-sm">
                      {/* Toggle Cargo/Abono */}
                      <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg border" data-testid="tipo-documento-toggle">
                        <span className="text-xs font-medium text-gray-600">Tipo:</span>
                        <div className="flex bg-white rounded-md border overflow-hidden">
                          <button
                            type="button"
                            data-testid="tipo-cargo-btn"
                            className={`px-3 py-1 text-xs font-medium transition-colors ${!esNotaCredito ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                            onClick={() => {
                              if (esNotaCredito) {
                                setEsNotaCredito(false);
                              }
                            }}
                          >
                            Cargo
                          </button>
                          <button
                            type="button"
                            data-testid="tipo-abono-btn"
                            className={`px-3 py-1 text-xs font-medium transition-colors ${esNotaCredito ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                            onClick={() => {
                              if (!esNotaCredito) {
                                setEsNotaCredito(true);
                              }
                            }}
                          >
                            Abono
                          </button>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="reanalizar-btn"
                          className="ml-auto h-7 text-xs"
                          disabled={procesandoIA}
                          onClick={async () => {
                            const tipo = esNotaCredito ? 'abono' : 'cargo';
                            setProcesandoIA(true);
                            try {
                              const { ok, data } = await safeFetch(
                                `${API_URL}/api/finanzas/drive/extraer-datos/${archivoEnProceso.id}?forzar_reprocesar=true&tipo_documento=${tipo}&incluir_cargos_vinculados=${incluirCargosVinculados}`,
                                {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  }
                                }
                              );
                              if (ok && data.success) {
                                setDatosFacturaIA(data.datos_extraidos);
                                setCargosMatch(data.cargos_candidatos || []);
                                setSubcargosMatch(data.subcargos_candidatos || []);
                                setAbonosMatch(data.abonos_candidatos || []);
                                setDatosDesdeCache(false);
                                setCargoYaVinculado(data.cargo_vinculado || null);
                              } else {
                                setDatosFacturaIA({ error: data.detail || data.error || 'Error re-analizando' });
                              }
                            } catch (e) {
                              console.error('Error re-analizando:', e);
                            } finally {
                              setProcesandoIA(false);
                            }
                          }}
                        >
                          <RefreshCw className={`w-3 h-3 mr-1 ${procesandoIA ? 'animate-spin' : ''}`} />
                          Re-analizar
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-gray-500 text-xs">Fecha</label>
                          <Input 
                            type="date"
                            className="h-8 text-xs sm:text-sm"
                            value={datosFacturaIA.fecha_factura || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, fecha_factura: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs">Total</label>
                          <Input 
                            type="number"
                            step="0.01"
                            className="h-8 text-xs sm:text-sm font-medium"
                            value={datosFacturaIA.importe_total || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, importe_total: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs">Proveedor</label>
                        <Input 
                          className="h-8 text-xs sm:text-sm"
                          value={datosFacturaIA.proveedor_nombre || ''}
                          onChange={(e) => {
                            const nuevoProveedor = e.target.value;
                            setDatosFacturaIA({...datosFacturaIA, proveedor_nombre: nuevoProveedor});
                            // Cargar categoría sugerida con debounce
                            if (nuevoProveedor.length >= 3) {
                              setTimeout(() => cargarCategoriaSugerida(nuevoProveedor), 500);
                            }
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-gray-500 text-xs">CIF/NIF</label>
                          <Input 
                            className="h-8 text-xs sm:text-sm"
                            value={datosFacturaIA.proveedor_cif || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, proveedor_cif: e.target.value})}
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs">Nº Factura</label>
                          <Input 
                            className="h-8 text-xs sm:text-sm"
                            value={datosFacturaIA.numero_factura || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, numero_factura: e.target.value})}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-gray-500 text-xs">Concepto</label>
                        <Input 
                          className="h-8 text-xs sm:text-sm"
                          value={datosFacturaIA.concepto || ''}
                          onChange={(e) => setDatosFacturaIA({...datosFacturaIA, concepto: e.target.value})}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-gray-500 text-xs">Categoría</label>
                          {categoriaSugerida && !datosFacturaIA.categoria && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              onClick={() => setDatosFacturaIA({...datosFacturaIA, categoria: categoriaSugerida.categoria_sugerida})}
                            >
                              💡 Usar &quot;{CATEGORIAS_GASTO.find(c => c.value === categoriaSugerida.categoria_sugerida)?.label || categoriaSugerida.categoria_sugerida}&quot; ({categoriaSugerida.confianza}%)
                            </button>
                          )}
                        </div>
                        <select
                          className="w-full h-8 text-sm px-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          value={datosFacturaIA.categoria || ''}
                          onChange={(e) => setDatosFacturaIA({...datosFacturaIA, categoria: e.target.value})}
                        >
                          <option value="">-- Seleccionar categoría --</option>
                          {CATEGORIAS_GASTO.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div>
                          <label className="text-gray-500 text-xs">Base</label>
                          <Input 
                            type="number"
                            step="0.01"
                            className="h-8 text-sm"
                            value={datosFacturaIA.importe_base || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, importe_base: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs">IVA</label>
                          <Input 
                            type="number"
                            step="0.01"
                            className="h-8 text-sm"
                            value={datosFacturaIA.importe_iva || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, importe_iva: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs">% IVA</label>
                          <Input 
                            type="number"
                            className="h-8 text-sm"
                            value={datosFacturaIA.tipo_iva || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, tipo_iva: parseInt(e.target.value) || 0})}
                          />
                        </div>
                        <div>
                          <label className="text-gray-500 text-xs">Ret. IRPF</label>
                          <Input 
                            type="number"
                            step="0.01"
                            className="h-8 text-sm"
                            placeholder="0.00"
                            value={datosFacturaIA.retencion_irpf || ''}
                            onChange={(e) => setDatosFacturaIA({...datosFacturaIA, retencion_irpf: parseFloat(e.target.value) || 0})}
                          />
                        </div>
                      </div>
                      {datosFacturaIA.retencion_irpf > 0 && (
                        <div className="p-2 bg-orange-50 rounded text-xs text-orange-700">
                          ⚠️ Factura con retención IRPF: {formatNumber(datosFacturaIA.retencion_irpf)}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Lista de cargos candidatos */}
                {cargosMatch.length > 0 && (
                  <div className="border rounded-lg">
                    <div className="p-2 bg-purple-50 border-b">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                        <span className="text-xs sm:text-sm font-medium text-purple-800">
                          Cargos Candidatos ({cargosMatch.filter(filtrarCargoCandidato).length})
                        </span>
                        {cargosSeleccionados.length > 0 && (
                          <Badge className="bg-blue-100 text-blue-800 text-xs w-fit">
                            {cargosSeleccionados.length} sel. • {formatNumber(cargosSeleccionados.reduce((sum, c) => sum + (c.importe || 0), 0))}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2 items-center mb-2">
                        <Input
                          placeholder="Filtrar por concepto..."
                          className="h-7 text-xs flex-1"
                          value={filtroConceptoCargos}
                          onChange={(e) => setFiltroConceptoCargos(e.target.value)}
                        />
                        <Input
                          type="date"
                          placeholder="Fecha"
                          className="h-7 text-xs w-32"
                          value={filtroFechaCargos}
                          onChange={(e) => setFiltroFechaCargos(e.target.value)}
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Importe"
                          className="h-7 text-xs w-24"
                          value={filtroImporteCargos}
                          onFocus={() => {
                            // Al activarlo vacío, proponer el total de la factura
                            if (!filtroImporteCargos && datosFacturaIA?.importe_total) {
                              setFiltroImporteCargos(String(datosFacturaIA.importe_total));
                            }
                          }}
                          onChange={(e) => setFiltroImporteCargos(e.target.value)}
                          title="Filtrar por importe (±0,50€, también contra el restante del cargo)"
                        />
                        {(filtroConceptoCargos || filtroFechaCargos || filtroImporteCargos || filtroMesCargos || filtroAnioCargos !== anioActualCargos) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setFiltroConceptoCargos('');
                              setFiltroFechaCargos('');
                              setFiltroImporteCargos('');
                              setFiltroAnioCargos(anioActualCargos);
                              setFiltroMesCargos('');
                            }}
                            title="Limpiar filtros"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      {/* Filtro por año y mes */}
                      <div className="flex flex-wrap items-center gap-1 mb-2">
                        {aniosCargosCandidatos.map(a => (
                          <button
                            key={a}
                            onClick={() => setFiltroAnioCargos(prev => prev === a ? '' : a)}
                            className={`text-[11px] px-2 py-0.5 rounded border font-medium transition-colors ${
                              filtroAnioCargos === a
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                            }`}
                            title={filtroAnioCargos === a ? 'Quitar filtro de año' : `Solo cargos de ${a}`}
                          >
                            {a}
                          </button>
                        ))}
                        <span className="w-px h-4 bg-gray-300 mx-1" />
                        {['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'].map((nombre, i) => {
                          const mes = String(i + 1).padStart(2, '0');
                          return (
                            <button
                              key={mes}
                              onClick={() => setFiltroMesCargos(prev => prev === mes ? '' : mes)}
                              className={`text-[11px] px-1.5 py-0.5 rounded border transition-colors ${
                                filtroMesCargos === mes
                                  ? 'bg-purple-600 text-white border-purple-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                              }`}
                              title={filtroMesCargos === mes ? 'Quitar filtro de mes' : `Solo ${nombre}`}
                            >
                              {nombre}
                            </button>
                          );
                        })}
                      </div>
                      {/* Checkbox para incluir cargos vinculados */}
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={incluirCargosVinculados}
                            onChange={async (e) => {
                              const checked = e.target.checked;
                              setIncluirCargosVinculados(checked);
                              if (!checked) return;
                              // Traer al momento los cargos ya vinculados, sin
                              // re-procesar la factura, para poder añadir esta
                              // factura a un cargo que agrupa varias
                              try {
                                const params = new URLSearchParams();
                                if (filtroConceptoCargos) params.append('q', filtroConceptoCargos);
                                const rango = rangoFechasCargos();
                                if (rango) {
                                  params.append('fecha_desde', rango.desde);
                                  params.append('fecha_hasta', rango.hasta);
                                }
                                const rangoImp = rangoImporteCargos();
                                if (rangoImp) {
                                  params.append('importe_min', rangoImp.min);
                                  params.append('importe_max', rangoImp.max);
                                }
                                params.append('solo_sin_vincular', 'false');
                                params.append('limit', '500');
                                const res = await fetch(`${API_URL}/api/finanzas/cargos-buscar?${params}`, { headers });
                                const data = await res.json();
                                if (data.success && data.cargos?.length > 0) {
                                  const idsExistentes = new Set(cargosMatch.map(c => c.id));
                                  const nuevos = data.cargos.filter(c =>
                                    !idsExistentes.has(c.id) && (c.facturas_vinculadas?.length > 0 || c.factura_id)
                                  );
                                  if (nuevos.length > 0) {
                                    setCargosMatch(prev => [...prev, ...nuevos.map(c => ({ ...c, match_score: 0, match_reasons: ['Cargo ya vinculado'] }))]);
                                  }
                                }
                              } catch (err) {
                                console.error('Error cargando cargos vinculados:', err);
                              }
                            }}
                            className="rounded"
                          />
                          Incluir también cargos ya cubiertos (los que aún tienen importe por cubrir salen siempre)
                        </label>
                        <span className="text-xs text-gray-400">
                          {cargosMatch.filter(filtrarCargoCandidato).length} de {cargosMatch.length} cargos
                        </span>
                      </div>
                      {/* Botón Ampliar búsqueda cuando hay filtros activos pero pocos resultados */}
                      {(filtroConceptoCargos || filtroFechaCargos || filtroMesCargos || filtroImporteCargos) && cargosMatch.filter(filtrarCargoCandidato).length < 5 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 text-xs"
                          disabled={buscandoCargos}
                          onClick={async () => {
                            setBuscandoCargos(true);
                            try {
                              const params = new URLSearchParams();
                              if (filtroConceptoCargos) params.append('q', filtroConceptoCargos);
                              const rango = rangoFechasCargos();
                              if (rango) {
                                params.append('fecha_desde', rango.desde);
                                params.append('fecha_hasta', rango.hasta);
                              }
                              const rangoImp = rangoImporteCargos();
                              if (rangoImp) {
                                params.append('importe_min', rangoImp.min);
                                params.append('importe_max', rangoImp.max);
                              }
                              params.append('solo_sin_vincular', !incluirCargosVinculados);
                              params.append('limit', '500');
                              
                              const res = await fetch(`${API_URL}/api/finanzas/cargos-buscar?${params}`, { headers });
                              const data = await res.json();
                              if (data.success && data.cargos?.length > 0) {
                                // Combinar con los existentes sin duplicados
                                const idsExistentes = new Set(cargosMatch.map(c => c.id));
                                const nuevos = data.cargos.filter(c => !idsExistentes.has(c.id));
                                setCargosMatch(prev => [...nuevos.map(c => ({...c, match_score: 0, match_reasons: ['Búsqueda ampliada']})), ...prev]);
                              }
                            } catch (e) {
                              console.error('Error ampliando búsqueda:', e);
                            } finally {
                              setBuscandoCargos(false);
                            }
                          }}
                        >
                          {buscandoCargos ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                          Ampliar búsqueda en todos los cargos
                        </Button>
                      )}
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {cargosMatch
                        .filter(filtrarCargoCandidato)
                        .slice(0, 200)  // Mostrar hasta 200 después del filtrado
                        .map((cargo, idx) => {
                        const isSelected = cargosSeleccionados.some(c => c.id === cargo.id);
                        const conSustitucion = cargosSeleccionados.find(c => c.id === cargo.id)?._sustituir === true;
                        return (
                          <div 
                            key={cargo.id || idx} 
                            className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                              isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                            }`}
                            onClick={() => {
                              if (isSelected) {
                                setCargosSeleccionados(prev => prev.filter(c => c.id !== cargo.id));
                              } else {
                                setCargosSeleccionados(prev => [...prev, cargo]);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-2">
                                <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center ${
                                  isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
                                }`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge className={
                                      cargo.match_score >= 80 ? 'bg-green-100 text-green-800' :
                                      cargo.match_score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }>
                                      {cargo.match_score}% match
                                    </Badge>
                                    {cargo.match_score >= 80 && (
                                      <span className="text-xs text-green-600 font-medium">✓ Recomendado</span>
                                    )}
                                  </div>
                                  <p className="text-sm font-medium mt-1">
                                    {limpiarConcepto(cargo.concepto).proveedor}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFecha(cargo.fecha)} • {cargo.ultimos_4_digitos ? `****${cargo.ultimos_4_digitos}` : ''}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {cargo.match_reasons?.join(' • ')}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-lg">{formatNumber(cargo.importe)}</p>
                                {cargo.facturas_vinculadas?.length > 0 ? (
                                  <div className="mt-0.5">
                                    <Badge
                                      className="bg-purple-100 text-purple-700 text-[10px]"
                                      title={cargo.facturas_vinculadas.map(f => `${f.numero_factura || 's/n'} · ${formatNumber(f.importe_total)}`).join('\n')}
                                    >
                                      {cargo.facturas_vinculadas.length} fact. ya vinculada(s)
                                    </Badge>
                                    {typeof cargo.importe_restante === 'number' && (
                                      <p className="text-[10px] text-purple-600 mt-0.5">
                                        restante {formatNumber(cargo.importe_restante)}
                                      </p>
                                    )}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (conSustitucion) {
                                          setCargosSeleccionados(prev => prev.filter(c => c.id !== cargo.id));
                                        } else {
                                          setCargosSeleccionados(prev => [...prev.filter(c => c.id !== cargo.id), { ...cargo, _sustituir: true }]);
                                        }
                                      }}
                                      className={`mt-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                                        conSustitucion
                                          ? 'bg-red-600 text-white border-red-600'
                                          : 'bg-white text-red-600 border-red-300 hover:bg-red-50'
                                      }`}
                                      title="Desvincula la(s) factura(s) actual(es) del cargo (vuelven a Pendientes) y vincula esta en su lugar"
                                    >
                                      {conSustitucion ? 'Sustituirá la actual' : 'Sustituir factura'}
                                    </button>
                                  </div>
                                ) : cargo.factura_drive_link ? (
                                  <Badge variant="outline" className="text-xs">Ya vinculado</Badge>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sección de Compras de tarjeta candidatas (subcargos) */}
                {subcargosMatch.length > 0 && (
                  <div className="border rounded-lg mt-3">
                    <div className="p-2 bg-amber-50 border-b">
                      <span className="text-xs sm:text-sm font-medium text-amber-800">
                        Compras de tarjeta candidatas ({subcargosMatch.length})
                      </span>
                      <p className="text-[11px] text-amber-700 mt-0.5">
                        Esta factura puede corresponder a una compra dentro del cargo mensual de la tarjeta.
                      </p>
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y">
                      {subcargosMatch.map((cand) => (
                        <div key={`${cand.cargo_id}-${cand.subcargo_id}`} className="flex items-center justify-between gap-2 p-2 hover:bg-amber-50/40">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-gray-800 truncate">{cand.concepto || cand.proveedor || 'Compra'}</span>
                              {cand.tarjeta && <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1 py-0">Tarjeta {cand.tarjeta}</Badge>}
                              {cand.match_score >= 60 && <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">cuadra</Badge>}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {cand.fecha_compra ? formatFecha(cand.fecha_compra) : ''} · {formatNumber(cand.importe)}
                              {cand.match_reasons?.length ? ` · ${cand.match_reasons.join(', ')}` : ''}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="shrink-0 h-7 text-xs"
                            disabled={vinculandoSubcargo === cand.subcargo_id}
                            onClick={() => vincularFacturaASubcargo(cand)}
                          >
                            {vinculandoSubcargo === cand.subcargo_id ? 'Vinculando...' : 'Vincular'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sección de Abonos Candidatos para Notas de Crédito/Devoluciones */}
                {esNotaCredito && abonosMatch.length > 0 && (
                  <div className="border-2 border-green-200 rounded-lg bg-green-50">
                    <div className="p-2 bg-green-100 border-b border-green-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-800">
                          📥 Abonos Bancarios Candidatos ({abonosMatch.length})
                        </span>
                        <Badge className="bg-green-200 text-green-800 text-xs">
                          Nota de Crédito / Devolución
                        </Badge>
                      </div>
                      <p className="text-xs text-green-600 mt-1">
                        Esta factura es una devolución. Selecciona el ingreso bancario para vincular.
                      </p>
                    </div>
                    <div className="max-h-[250px] overflow-y-auto">
                      {abonosMatch.map((abono, idx) => (
                        <div 
                          key={abono.id}
                          className={`p-2 border-b last:border-0 cursor-pointer transition-colors ${
                            abonoSeleccionadoDevolucion?.id === abono.id 
                              ? 'bg-green-100 ring-2 ring-green-400' 
                              : 'hover:bg-green-50'
                          }`}
                          onClick={() => setAbonoSeleccionadoDevolucion(abono)}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{abono.fecha}</span>
                                {abono.match_score >= 50 && (
                                  <Badge className="bg-green-200 text-green-800 text-[10px] px-1">
                                    {abono.match_score}% match
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm font-medium truncate">{abono.concepto}</p>
                              {abono.match_reasons?.length > 0 && (
                                <p className="text-xs text-green-600">{abono.match_reasons.join(' • ')}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="font-bold text-green-700">+{formatNumber(abono.importe)}</span>
                              {abonoSeleccionadoDevolucion?.id === abono.id && (
                                <div className="text-green-600 mt-1">
                                  <CheckCircle className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Botón para vincular devolución */}
                    {abonoSeleccionadoDevolucion && (
                      <div className="p-3 bg-green-100 border-t border-green-200">
                        <Button
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                          disabled={vinculandoDevolucion}
                          onClick={async () => {
                            if (!window.confirm(`¿Vincular esta nota de crédito con el ingreso de ${formatNumber(abonoSeleccionadoDevolucion.importe)}?`)) return;
                            
                            setVinculandoDevolucion(true);
                            try {
                              const url = `${API_URL}/api/finanzas/drive/vincular-devolucion?file_id=${archivoEnProceso.id}&abono_id=${abonoSeleccionadoDevolucion.id}`;
                              const { ok, data } = await safeFetch(url, {
                                method: 'POST',
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify(datosFacturaIA)
                              });
                              
                              if (ok && data.success) {
                                alert(`✅ ${data.mensaje}`);
                                // Limpiar y recargar
                                setShowProcesarFactura(false);
                                setArchivoEnProceso(null);
                                setDatosFacturaIA(null);
                                setAbonosMatch([]);
                                setAbonoSeleccionadoDevolucion(null);
                                setEsNotaCredito(false);
                                // Recargar archivos de Drive
                                const res = await fetch(`${API_URL}/api/finanzas/drive/archivos`, { 
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                const driveData = await res.json();
                                setDriveArchivos(driveData.archivos || []);
                                // Recargar abonos
                                cargarAbonos();
                              } else {
                                alert(`Error: ${data.detail || data.error || 'Error vinculando'}`);
                              }
                            } catch (e) {
                              console.error('Error:', e);
                              alert('Error vinculando la devolución');
                            } finally {
                              setVinculandoDevolucion(false);
                            }
                          }}
                        >
                          {vinculandoDevolucion ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Link className="w-4 h-4 mr-2" />
                          )}
                          Vincular Nota de Crédito con Ingreso
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Sin matches - modificado para manejar notas de crédito */}
                {!procesandoIA && datosFacturaIA && !datosFacturaIA.error && cargosMatch.length === 0 && abonosMatch.length === 0 && !mostrarBusquedaManual && !esNotaCredito && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                    <p className="font-medium text-yellow-800">No se encontraron cargos bancarios coincidentes</p>
                    <p className="text-sm text-yellow-600 mb-3">Puede ser una factura en otra moneda, nómina o impuesto</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setMostrarBusquedaManual(true)}
                    >
                      <Search className="w-4 h-4 mr-1" />
                      Buscar cargo manualmente
                    </Button>
                  </div>
                )}

                {/* Sin abonos para nota de crédito */}
                {!procesandoIA && datosFacturaIA && !datosFacturaIA.error && esNotaCredito && abonosMatch.length === 0 && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-center">
                    <AlertCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className="font-medium text-orange-800">Nota de crédito sin ingresos coincidentes</p>
                    <p className="text-sm text-orange-600 mb-3">
                      No se encontraron ingresos bancarios que coincidan con esta devolución de {formatNumber(Math.abs(datosFacturaIA.importe_total || 0))}
                    </p>
                    <p className="text-xs text-orange-500">
                      Importa los movimientos bancarios o vincúlala manualmente desde Ingresos
                    </p>
                  </div>
                )}

                {/* Búsqueda manual de cargos */}
                {mostrarBusquedaManual && (
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        <Search className="w-4 h-4 inline mr-1" />
                        Búsqueda Manual de Cargos
                      </span>
                      <button 
                        className="text-gray-400 hover:text-gray-600"
                        onClick={() => {
                          setMostrarBusquedaManual(false);
                          setBusquedaManual('');
                        }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <Input
                        placeholder="Buscar por proveedor, concepto, nómina..."
                        value={busquedaManual}
                        onChange={(e) => setBusquedaManual(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={async (e) => {
                          if (e.key === 'Enter' && busquedaManual.length >= 2) {
                            setBuscandoCargos(true);
                            try {
                              const res = await fetch(`${API_URL}/api/finanzas/cargos-buscar?q=${encodeURIComponent(busquedaManual)}&solo_sin_vincular=true&limit=30`, { headers });
                              const data = await res.json();
                              if (data.success) {
                                setCargosMatch(data.cargos.map(c => ({...c, match_score: 0, match_reasons: ['Búsqueda manual']})));
                              }
                            } catch (e) {
                              console.error('Error buscando:', e);
                            } finally {
                              setBuscandoCargos(false);
                            }
                          }
                        }}
                      />
                      <Button 
                        size="sm"
                        disabled={busquedaManual.length < 2 || buscandoCargos}
                        onClick={async () => {
                          setBuscandoCargos(true);
                          try {
                            const res = await fetch(`${API_URL}/api/finanzas/cargos-buscar?q=${encodeURIComponent(busquedaManual)}&solo_sin_vincular=true&limit=30`, { headers });
                            const data = await res.json();
                            if (data.success) {
                              setCargosMatch(data.cargos.map(c => ({...c, match_score: 0, match_reasons: ['Búsqueda manual']})));
                            }
                          } catch (e) {
                            console.error('Error buscando:', e);
                          } finally {
                            setBuscandoCargos(false);
                          }
                        }}
                      >
                        {buscandoCargos ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      💡 Escribe: nómina, autónomo, TGSS, hacienda, nombre de empleado, proveedor...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Aviso: ya existe otra factura con mismo CIF+nº */}
          {avisoDuplicadoVincular && (
            <div className="mx-1 mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <p className="font-medium text-amber-900">
                    Ya existe una factura con mismo CIF y nº en el sistema
                  </p>
                  {avisoDuplicadoVincular.original && (
                    <p className="text-amber-800 text-xs mt-1">
                      {avisoDuplicadoVincular.original.proveedor_nombre || '—'} · Nº {avisoDuplicadoVincular.original.numero_factura || '—'} · {avisoDuplicadoVincular.original.fecha_factura || '—'} · Total {formatNumber(avisoDuplicadoVincular.original.importe_total || 0)}
                      <br />
                      <span className="text-amber-700">Archivo: {avisoDuplicadoVincular.original.drive_file_name || avisoDuplicadoVincular.original.drive_file_id}</span>
                    </p>
                  )}
                  <p className="text-amber-800 text-xs mt-2">
                    Si es la misma factura, cancela y elimínala de Drive. Si son distintas (p.ej. Carrefour agrupa tickets con mismo nº), pulsa de nuevo "Vincular" para confirmar que son diferentes.
                  </p>
                </div>
                <button
                  className="text-amber-600 hover:text-amber-800 shrink-0"
                  onClick={() => setAvisoDuplicadoVincular(null)}
                  title="Descartar aviso"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <DialogFooter className="border-t pt-3 sm:pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-3">
              <div className="order-2 sm:order-1 flex items-center gap-2">
                {/* Botón eliminar factura */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  disabled={eliminandoFactura}
                  onClick={() => {
                    if (archivoEnProceso?.id) {
                      eliminarFacturaDrive(archivoEnProceso.id);
                    }
                  }}
                  title="Eliminar factura del sistema"
                >
                  {eliminandoFactura ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  <span className="ml-1 hidden sm:inline">Eliminar</span>
                </Button>
                
                {cargosSeleccionados.length > 0 && (() => {
                  // Para cargos con facturas ya vinculadas se compara contra
                  // el importe restante, no contra el total del cargo
                  const disponible = cargosSeleccionados.reduce((sum, c) =>
                    sum + (!c._sustituir && c.facturas_vinculadas?.length > 0 && typeof c.importe_restante === 'number'
                      ? c.importe_restante
                      : (c.importe || 0)), 0);
                  const hayParciales = cargosSeleccionados.some(c => !c._sustituir && c.facturas_vinculadas?.length > 0);
                  const numSustituciones = cargosSeleccionados.filter(c => c._sustituir).length;
                  return (
                    <div className="text-xs sm:text-sm text-center sm:text-left">
                      <span className="font-medium text-blue-600">{cargosSeleccionados.length} cargo(s)</span>
                      <span className="text-gray-400 mx-1 sm:mx-2">•</span>
                      <span className="font-medium">{hayParciales ? `restante ${formatNumber(disponible)}` : formatNumber(disponible)}</span>
                      {numSustituciones > 0 && (
                        <span className="ml-1 sm:ml-2 text-xs text-red-600 font-medium">
                          sustituye {numSustituciones} vinculación(es)
                        </span>
                      )}
                      {datosFacturaIA?.importe_total && (
                        <span className={`ml-1 sm:ml-2 text-xs ${
                          Math.abs(disponible - datosFacturaIA.importe_total) < 1
                            ? 'text-green-600'
                            : 'text-orange-600'
                        }`}>
                          (Fact: {formatNumber(datosFacturaIA.importe_total)})
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div className="flex flex-col-reverse sm:flex-row gap-2 order-1 sm:order-2">
                <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
                  setShowProcesarFactura(false);
                  setArchivoEnProceso(null);
                  setDatosFacturaIA(null);
                  setCargosMatch([]);
                  setSubcargosMatch([]);
                  setCargosSeleccionados([]);
                  setAvisoDuplicadoVincular(null);
                }}>
                  Cancelar
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  disabled={!datosFacturaIA || datosFacturaIA.error || cargosSeleccionados.length === 0 || vinculandoFactura || datosFacturaIA?.incidencia?.estado === 'abierta'}
                  title={datosFacturaIA?.incidencia?.estado === 'abierta' ? 'Resuelve la incidencia antes de vincular la factura' : ''}
                  onClick={async () => {
                    if (cargosSeleccionados.length === 0 || !datosFacturaIA || vinculandoFactura) return;
                    if (datosFacturaIA?.incidencia?.estado === 'abierta') { alert('Esta factura tiene una incidencia abierta. Resuélvela antes de vincularla.'); return; }

                    setVinculandoFactura(true);
                    try {
                      const cargoIds = cargosSeleccionados.map(c => c.id).join(',');
                      const forzar = avisoDuplicadoVincular ? '&forzar_distinta=true' : '';
                      const sustituirIds = cargosSeleccionados.filter(c => c._sustituir).map(c => c.id).join(',');
                      if (sustituirIds && !window.confirm('Vas a SUSTITUIR la(s) factura(s) ya vinculada(s) de algún cargo: las anteriores se desvincularán y volverán a Pendientes de Drive. ¿Continuar?')) {
                        setVinculandoFactura(false);
                        return;
                      }
                      const sustituir = sustituirIds ? `&sustituir_cargo_ids=${sustituirIds}` : '';
                      const url = `${API_URL}/api/finanzas/drive/vincular-factura?file_id=${archivoEnProceso.id}&cargo_ids=${cargoIds}${forzar}${sustituir}`;

                      const { ok, status, data } = await safeFetch(url, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(datosFacturaIA)
                      });

                      if (ok && data.success) {
                        const lineaA3doc = data.a3doc_subido
                          ? '\n\nCopiada a a3doc (Facturas recibidas)'
                          : data.a3doc_ya_estaba
                            ? '\n\nYa estaba en a3doc'
                            : '\n\nNO se pudo copiar a a3doc' + (data.a3doc_error ? ` (${data.a3doc_error})` : '') + '. Súbela luego desde el botón «Subir a a3doc» del cargo.';
                        const lineaSustitucion = data.facturas_sustituidas > 0
                          ? `\nSustituida(s) ${data.facturas_sustituidas} factura(s) anterior(es) (devueltas a Pendientes)`
                          : '';
                        alert(`✅ Factura vinculada a ${data.cargos_vinculados} cargo(s)${lineaSustitucion}\n\nImporte factura: ${formatNumber(data.importe_factura)}\nImporte cargos: ${formatNumber(data.importe_cargos)}${lineaA3doc}`);
                        setAvisoDuplicadoVincular(null);
                        setShowProcesarFactura(false);
                        setArchivoEnProceso(null);
                        setDatosFacturaIA(null);
                        setCargosMatch([]);
                        setSubcargosMatch([]);
                        setCargosSeleccionados([]);
                        cargarDatosPestaña();
                        recargarContenidoDrive();
                      } else if (status === 409 && data.detail?.code === 'duplicado_detectado') {
                        // Duplicado detectado: mostrar aviso en el modal y dejar que el usuario decida
                        setAvisoDuplicadoVincular({ original: data.detail.original || null });
                      } else {
                        const det = data.detail;
                        alert((typeof det === 'string' ? det : det?.mensaje) || data.message || 'Error vinculando factura');
                      }
                    } catch (e) {
                      console.error('Error en vinculación:', e);
                      alert('Error: ' + e.message);
                    } finally {
                      setVinculandoFactura(false);
                    }
                  }}
                >
                  {vinculandoFactura ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Vinculando...
                    </>
                  ) : avisoDuplicadoVincular ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Son distintas, vincular igual
                    </>
                  ) : (
                    <>
                      <Link2 className="w-4 h-4 mr-2" />
                      Vincular ({cargosSeleccionados.length})
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Configurar Tarjetas de Empleados */}
      <Dialog open={showConfigTarjetas} onOpenChange={setShowConfigTarjetas}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Asignar Tarjetas a Empleados
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-500">
              Asigna los últimos 4 dígitos de cada tarjeta a su empleado correspondiente.
              Esto permite identificar automáticamente quién debe subir cada factura.
            </p>
            
            {/* Cargar tarjetas y empleados al abrir */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={async () => {
                try {
                  const [tarRes, empRes] = await Promise.all([
                    fetch(`${API_URL}/api/finanzas/tarjetas-disponibles`, { headers }),
                    fetch(`${API_URL}/api/empleados`, { headers })
                  ]);
                  const tarData = await tarRes.json();
                  const empData = await empRes.json();
                  setTarjetasDisponibles(tarData.tarjetas || []);
                  setEmpleadosTarjetas(Array.isArray(empData) ? empData.filter(e => e.activo) : []);
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Cargar Tarjetas y Empleados
            </Button>

            {tarjetasDisponibles.length > 0 && (
              <div className="border rounded-lg divide-y">
                {tarjetasDisponibles.map(tarjeta => (
                  <div key={tarjeta.ultimos_4} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <CreditCard className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <div className="font-mono font-medium">**** **** **** {tarjeta.ultimos_4}</div>
                        <div className="text-xs text-gray-500">
                          {tarjeta.total_cargos} cargos | Último uso: {tarjeta.ultimo_uso ? formatFecha(tarjeta.ultimo_uso) : 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {tarjeta.asignada ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-100 text-green-800">
                            <User className="w-3 h-3 mr-1" />
                            {tarjeta.empleado?.nombre}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={async () => {
                              try {
                                await fetch(`${API_URL}/api/finanzas/empleados/${tarjeta.empleado?.id}/tarjeta`, {
                                  method: 'DELETE',
                                  headers
                                });
                                // Recargar
                                const tarRes = await fetch(`${API_URL}/api/finanzas/tarjetas-disponibles`, { headers });
                                const tarData = await tarRes.json();
                                setTarjetasDisponibles(tarData.tarjetas || []);
                              } catch (e) {
                                alert('Error: ' + e.message);
                              }
                            }}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ) : (
                        <Select
                          onValueChange={async (empleadoId) => {
                            try {
                              const res = await fetch(`${API_URL}/api/finanzas/empleados/${empleadoId}/tarjeta?ultimos_4=${tarjeta.ultimos_4}`, {
                                method: 'PUT',
                                headers
                              });
                              const data = await res.json();
                              if (data.success) {
                                // Recargar
                                const tarRes = await fetch(`${API_URL}/api/finanzas/tarjetas-disponibles`, { headers });
                                const tarData = await tarRes.json();
                                setTarjetasDisponibles(tarData.tarjetas || []);
                              } else {
                                alert(data.detail || 'Error asignando tarjeta');
                              }
                            } catch (e) {
                              alert('Error: ' + e.message);
                            }
                          }}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Seleccionar empleado..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white border shadow-lg z-[100]">
                            {empleadosTarjetas.map(emp => (
                              <SelectItem key={emp.id} value={emp.id}>
                                {emp.nombre} {emp.apellidos}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tarjetasDisponibles.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Haz clic en &quot;Cargar&quot; para ver las tarjetas detectadas</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConfigTarjetas(false);
              setTarjetasDisponibles([]);
              setEmpleadosTarjetas([]);
            }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Factura Vinculada */}
      <Dialog open={showEditarFacturaVinculada} onOpenChange={setShowEditarFacturaVinculada}>
        <DialogContent className="bg-white max-w-7xl max-h-[95vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Editar Factura Vinculada
            </DialogTitle>
          </DialogHeader>
          {cargoSeleccionado && cargoSeleccionado.factura && (
            <>
            {(cargoSeleccionado.facturas?.length || 0) > 1 && (
              <div className="flex flex-wrap items-center gap-2 border-b pb-2">
                <span className="text-xs font-medium text-gray-500">
                  Este cargo tiene {cargoSeleccionado.facturas.length} facturas:
                </span>
                {cargoSeleccionado.facturas.map((f, i) => (
                  <button
                    key={f.id || i}
                    onClick={() => setCargoSeleccionado({ ...cargoSeleccionado, factura: f })}
                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                      f.id === cargoSeleccionado.factura?.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    title={f.concepto || ''}
                  >
                    {f.numero_factura || 's/n'} · {formatNumber(f.importe_total)}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-6 py-4 max-h-[75vh] overflow-hidden">
              {/* Columna izquierda: Vista previa del documento */}
              <div className="flex flex-col h-full">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Vista Previa del Documento</h4>
                {cargoSeleccionado.factura.drive_link ? (
                  <div className="flex-1 border rounded-lg overflow-hidden bg-gray-100 min-h-[500px]">
                    <iframe
                      src={cargoSeleccionado.factura.drive_link.replace('/view', '/preview')}
                      className="w-full h-full min-h-[500px]"
                      title="Vista previa de factura"
                      allow="autoplay"
                    />
                  </div>
                ) : (
                  <div className="flex-1 border rounded-lg flex items-center justify-center bg-gray-50 min-h-[500px]">
                    <div className="text-center text-gray-400">
                      <FileText className="w-16 h-16 mx-auto mb-2" />
                      <p>Sin documento vinculado</p>
                    </div>
                  </div>
                )}
                {cargoSeleccionado.factura.drive_link && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => window.open(cargoSeleccionado.factura.drive_link, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Abrir en nueva pestaña
                  </Button>
                )}
              </div>

              {/* Columna derecha: Formulario de edición */}
              <div className="flex flex-col h-full overflow-y-auto pr-2">
                {/* Estado / reintento de subida a a3doc (considera TODAS las facturas del cargo) */}
                {(() => {
                  const facturasCargo = cargoSeleccionado.facturas?.length ? cargoSeleccionado.facturas : (cargoSeleccionado.factura ? [cargoSeleccionado.factura] : []);
                  const enA3doc = facturasCargo.filter(f => f.a3doc_document_id).length;
                  return (
                <div className="mb-4">
                  {(facturasCargo.length > 0 && enA3doc === facturasCargo.length) ? (
                    <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <CheckCircle2 className="w-4 h-4" />
                      {facturasCargo.length > 1
                        ? `Subidas a a3doc (${enA3doc}/${facturasCargo.length})`
                        : `Subido a a3doc${cargoSeleccionado.factura?.a3doc_subido_en ? ` el ${formatFechaHora(cargoSeleccionado.factura.a3doc_subido_en)}` : ''}`}
                    </div>
                  ) : (
                    <>
                      {enA3doc > 0 && (
                        <div className="text-[11px] text-amber-700 mb-1">
                          En a3doc: {enA3doc}/{facturasCargo.length} facturas del cargo
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReintentarA3doc}
                        disabled={reintentandoA3doc}
                        className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      >
                        {reintentandoA3doc
                          ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Subiendo a a3doc…</>
                          : <><Cloud className="w-4 h-4 mr-1" /> Subir a a3doc</>}
                      </Button>
                    </>
                  )}
                </div>
                  );
                })()}
                {/* Info del cargo */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                  <h4 className="font-medium text-blue-800 mb-2 text-sm">Cargo Bancario</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Fecha:</span>
                      <span className="ml-1 font-medium">{formatFecha(cargoSeleccionado.fecha)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Importe:</span>
                      <span className="ml-1 font-medium text-red-600">{formatNumber(cargoSeleccionado.importe)} €</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Concepto:</span>
                      <span className="ml-1">{limpiarConcepto(cargoSeleccionado.concepto).proveedor}</span>
                    </div>
                  </div>
                </div>

                {/* Formulario de edición */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">Proveedor</label>
                    <Input 
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.proveedor_nombre || ''}
                      onChange={(e) => setCargoSeleccionado({
                        ...cargoSeleccionado,
                        factura: {...cargoSeleccionado.factura, proveedor_nombre: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">CIF/NIF</label>
                    <Input 
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.proveedor_cif || ''}
                      onChange={(e) => setCargoSeleccionado({
                        ...cargoSeleccionado,
                        factura: {...cargoSeleccionado.factura, proveedor_cif: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Nº Factura</label>
                    <Input 
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.numero_factura || ''}
                      onChange={(e) => setCargoSeleccionado({
                        ...cargoSeleccionado,
                        factura: {...cargoSeleccionado.factura, numero_factura: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Fecha Factura</label>
                    <Input 
                      type="date"
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.fecha_factura || ''}
                      onChange={(e) => setCargoSeleccionado({
                        ...cargoSeleccionado,
                        factura: {...cargoSeleccionado.factura, fecha_factura: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Base Imponible</label>
                    <Input 
                      type="number"
                      step="0.01"
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.importe_base || ''}
                      onChange={(e) => {
                        const base = parseFloat(e.target.value) || 0;
                        const iva = cargoSeleccionado.factura.importe_iva || 0;
                        setCargoSeleccionado({
                          ...cargoSeleccionado,
                          factura: {
                            ...cargoSeleccionado.factura, 
                            importe_base: base,
                            importe_total: base + iva
                          }
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">IVA</label>
                    <Input 
                      type="number"
                      step="0.01"
                      className="h-8 text-sm"
                      value={cargoSeleccionado.factura.importe_iva || ''}
                      onChange={(e) => {
                        const iva = parseFloat(e.target.value) || 0;
                        const base = cargoSeleccionado.factura.importe_base || 0;
                        setCargoSeleccionado({
                          ...cargoSeleccionado,
                          factura: {
                            ...cargoSeleccionado.factura, 
                            importe_iva: iva,
                            importe_total: base + iva
                          }
                        });
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Total Factura</label>
                    <Input 
                      type="number"
                      step="0.01"
                      className="h-8 text-sm bg-gray-100"
                      value={cargoSeleccionado.factura.importe_total || ''}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">Categoría</label>
                    <Select 
                      value={cargoSeleccionado.factura.categoria || 'otros'} 
                      onValueChange={(v) => setCargoSeleccionado({
                        ...cargoSeleccionado,
                        factura: {...cargoSeleccionado.factura, categoria: v}
                      })}
                    >
                      <SelectTrigger className="h-8 text-sm bg-white">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border shadow-lg z-[100] max-h-60 overflow-y-auto">
                        {CATEGORIAS_GASTO.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* IRPF si existe */}
                {(cargoSeleccionado.factura.retencion_irpf > 0 || cargoSeleccionado.factura.porcentaje_irpf > 0) && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-xs font-medium mb-1 block">% IRPF</label>
                      <Input 
                        type="number"
                        step="0.01"
                        className="h-8 text-sm"
                        value={cargoSeleccionado.factura.porcentaje_irpf || ''}
                        onChange={(e) => setCargoSeleccionado({
                          ...cargoSeleccionado,
                          factura: {...cargoSeleccionado.factura, porcentaje_irpf: parseFloat(e.target.value) || 0}
                        })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Retención IRPF</label>
                      <Input 
                        type="number"
                        step="0.01"
                        className="h-8 text-sm"
                        value={cargoSeleccionado.factura.retencion_irpf || ''}
                        onChange={(e) => setCargoSeleccionado({
                          ...cargoSeleccionado,
                          factura: {...cargoSeleccionado.factura, retencion_irpf: parseFloat(e.target.value) || 0}
                        })}
                      />
                    </div>
                  </div>
                )}

                {/* Incidencia / Observaciones */}
                <div className="mt-4 border-t pt-3">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Incidencia / Observaciones
                    </label>
                    {cargoSeleccionado.factura.incidencia?.estado && (
                      <Badge className={cargoSeleccionado.factura.incidencia.estado === 'resuelta' ? 'bg-green-100 text-green-700 font-normal' : 'bg-amber-100 text-amber-800 font-normal'}>
                        {cargoSeleccionado.factura.incidencia.estado === 'resuelta' ? 'Resuelta' : 'Abierta'}
                      </Badge>
                    )}
                  </div>
                  <textarea
                    className="w-full text-sm border rounded-md px-2 py-1.5 h-20 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    placeholder="Describe la incidencia: importe que no cuadra, falta base/IVA, factura duplicada, etc."
                    value={cargoSeleccionado.factura.incidencia?.texto || ''}
                    onChange={(e) => setCargoSeleccionado({
                      ...cargoSeleccionado,
                      factura: { ...cargoSeleccionado.factura, incidencia: { ...(cargoSeleccionado.factura.incidencia || {}), texto: e.target.value } }
                    })}
                  />
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {(() => {
                      const fid = cargoSeleccionado.factura.id;
                      const inc = cargoSeleccionado.factura.incidencia;
                      const onSaved = (nueva) => setCargoSeleccionado(prev => ({ ...prev, factura: { ...prev.factura, incidencia: nueva } }));
                      return (
                        <>
                          <Button size="sm" variant="outline" onClick={() => guardarIncidencia('abierta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                            Guardar incidencia
                          </Button>
                          {inc?.texto && inc?.estado !== 'resuelta' && (
                            <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => guardarIncidencia('resuelta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                              <Check className="w-3.5 h-3.5 mr-1" /> Marcar resuelta
                            </Button>
                          )}
                          {inc?.estado === 'resuelta' && (
                            <Button size="sm" variant="outline" onClick={() => guardarIncidencia('abierta', fid, inc, onSaved)} disabled={guardandoIncidencia}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reabrir
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => abrirAvisarEquipo(fid)}>
                            <Users className="w-3.5 h-3.5 mr-1" /> Avisar al equipo
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => abrirEmailProveedor(fid)}>
                            <Mail className="w-3.5 h-3.5 mr-1" /> Escribir al proveedor
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                  {cargoSeleccionado.factura.incidencia?.creada_en && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      Creada {new Date(cargoSeleccionado.factura.incidencia.creada_en).toLocaleDateString('es-ES')}
                      {cargoSeleccionado.factura.incidencia.creada_por ? ` por ${cargoSeleccionado.factura.incidencia.creada_por}` : ''}
                      {cargoSeleccionado.factura.incidencia.estado === 'resuelta' && cargoSeleccionado.factura.incidencia.resuelta_en
                        ? ` · resuelta ${new Date(cargoSeleccionado.factura.incidencia.resuelta_en).toLocaleDateString('es-ES')}` : ''}
                    </p>
                  )}
                </div>
              </div>
            </div>
            </>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="destructive"
              size="sm"
              onClick={async () => {
                if (!cargoSeleccionado?.id) return;
                const varias = (cargoSeleccionado.facturas?.length || 0) > 1;
                const facturaActivaId = cargoSeleccionado.factura?.id;
                const msg = varias
                  ? `¿Desvincular la factura ${cargoSeleccionado.factura?.numero_factura || 'seleccionada'} de este cargo? Las otras ${cargoSeleccionado.facturas.length - 1} factura(s) del cargo se mantienen.`
                  : '¿Desvincular esta factura del cargo? La factura no se eliminará pero quedará sin asociar.';
                if (!confirm(msg)) return;
                try {
                  const paramFactura = varias && facturaActivaId ? `?factura_id=${facturaActivaId}` : '';
                  const response = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${cargoSeleccionado.id}/desvincular${paramFactura}`, {
                    method: 'POST',
                    headers
                  });
                  // Siempre leer el body para evitar memory leaks
                  const data = await response.json().catch(() => ({}));

                  if (response.ok) {
                    if (!varias) {
                      setCargosBancarios(prev => prev.map(c =>
                        c.id === cargoSeleccionado.id
                          ? {...c, factura: null, facturas: null, factura_id: null, factura_vinculada_id: null, conciliado: false}
                          : c
                      ));
                    }
                    setShowEditarFacturaVinculada(false);
                    setCargoSeleccionado(null);
                    cargarDatosPestaña();
                    alert(data.message || 'Factura desvinculada correctamente');
                  } else {
                    alert('Error al desvincular: ' + (data.detail || 'Error desconocido'));
                  }
                } catch (error) {
                  console.error('Error:', error);
                  alert('Error al desvincular: ' + error.message);
                }
              }}
              className="mr-auto"
            >
              <Unlink className="w-4 h-4 mr-1" />
              Desvincular
            </Button>
            <Button variant="outline" onClick={() => {
              setShowEditarFacturaVinculada(false);
              setCargoSeleccionado(null);
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={async () => {
                if (!cargoSeleccionado?.factura?.id) return;
                try {
                  const response = await fetch(`${API_URL}/api/finanzas/drive/facturas/${cargoSeleccionado.factura.id}`, {
                    method: 'PUT',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(cargoSeleccionado.factura)
                  });
                  // Siempre leer el body para evitar memory leaks
                  const data = await response.json().catch(() => ({}));
                  
                  if (response.ok) {
                    // Actualizar el cargo en la lista local
                    setCargosBancarios(prev => prev.map(c => 
                      c.id === cargoSeleccionado.id 
                        ? {...c, factura: cargoSeleccionado.factura}
                        : c
                    ));
                    setShowEditarFacturaVinculada(false);
                    setCargoSeleccionado(null);
                    // Recargar resumen global
                    cargarDatosPestaña();
                  } else {
                    alert('Error al guardar los cambios: ' + (data.detail || 'Error desconocido'));
                  }
                } catch (error) {
                  console.error('Error:', error);
                  alert('Error al guardar los cambios: ' + error.message);
                }
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Check className="w-4 h-4 mr-1" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: escribir al proveedor sobre la incidencia */}
      <Dialog open={showEmailProveedor} onOpenChange={setShowEmailProveedor}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-duetto-primary" /> Escribir al proveedor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Para (email del proveedor)</label>
              <Input
                className="h-9 text-sm"
                placeholder="proveedor@ejemplo.com"
                value={emailProveedorData.destinatario}
                onChange={(e) => setEmailProveedorData({ ...emailProveedorData, destinatario: e.target.value })}
              />
              {!emailProveedorData.destinatario && (
                <p className="text-[11px] text-amber-600 mt-1">No hay email en la ficha del proveedor. Escríbelo a mano.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Asunto</label>
              <Input
                className="h-9 text-sm"
                value={emailProveedorData.asunto}
                onChange={(e) => setEmailProveedorData({ ...emailProveedorData, asunto: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Mensaje</label>
              <textarea
                className="w-full text-sm border rounded-md px-2 py-1.5 h-40 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                value={emailProveedorData.mensaje}
                onChange={(e) => setEmailProveedorData({ ...emailProveedorData, mensaje: e.target.value })}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={emailProveedorData.adjuntar_pdf}
                onChange={(e) => setEmailProveedorData({ ...emailProveedorData, adjuntar_pdf: e.target.checked })}
              />
              Adjuntar el PDF de la factura
            </label>
            <p className="text-[11px] text-gray-400">Se envía desde administracion@duettocatering.com</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEmailProveedor(false)}>Cancelar</Button>
            <Button className="bg-duetto-primary hover:bg-duetto-primary/90" onClick={enviarEmailProveedor} disabled={enviandoEmailProv}>
              <Send className="w-4 h-4 mr-1" /> {enviandoEmailProv ? 'Enviando...' : 'Enviar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: avisar a alguien del equipo */}
      <Dialog open={showAvisarEquipo} onOpenChange={setShowAvisarEquipo}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-duetto-primary" /> Avisar al equipo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Enviar aviso a</label>
              <select
                className="w-full border rounded-md px-2 py-2 text-sm"
                value={avisoEquipoData.empleado_id}
                onChange={(e) => setAvisoEquipoData({ ...avisoEquipoData, empleado_id: e.target.value })}
              >
                <option value="">Selecciona un empleado...</option>
                {empleadosLista.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}{emp.email ? '' : ' (sin email)'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Mensaje (opcional)</label>
              <textarea
                className="w-full text-sm border rounded-md px-2 py-1.5 h-24 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                placeholder="Añade contexto para el compañero..."
                value={avisoEquipoData.mensaje}
                onChange={(e) => setAvisoEquipoData({ ...avisoEquipoData, mensaje: e.target.value })}
              />
            </div>
            <p className="text-[11px] text-gray-400">Recibe una notificación interna y, si tiene email, también un correo con la factura adjunta.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAvisarEquipo(false)}>Cancelar</Button>
            <Button className="bg-duetto-primary hover:bg-duetto-primary/90" onClick={enviarAvisoEquipo} disabled={enviandoAviso}>
              <Send className="w-4 h-4 mr-1" /> {enviandoAviso ? 'Enviando...' : 'Enviar aviso'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: panel de incidencias por periodo */}
      <Dialog open={showIncidenciasPanel} onOpenChange={setShowIncidenciasPanel}>
        <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Incidencias de facturas
              <span className="text-sm font-normal text-gray-500">
                · {añoSeleccionado}{trimestreSeleccionado ? ` · T${trimestreSeleccionado}` : ''}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            {['', 'abierta', 'resuelta'].map((est) => (
              <button
                key={est || 'todas'}
                onClick={() => { setIncidenciasEstado(est); cargarIncidenciasPanel(est); }}
                className={`px-3 py-1 rounded-full text-xs font-medium ${incidenciasEstado === est ? 'bg-duetto-primary text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                {est === '' ? 'Todas' : est === 'abierta' ? 'Abiertas' : 'Resueltas'}
              </button>
            ))}
            <span className="text-xs text-gray-400 ml-auto">Filtra por año/trimestre desde la cabecera de Finanzas</span>
          </div>
          <div className="overflow-y-auto max-h-[60vh]">
            {incidenciasCargando ? (
              <p className="text-sm text-gray-500 py-4">Cargando...</p>
            ) : incidenciasLista.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">Sin incidencias en este periodo.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Fecha</th>
                    <th className="py-2 pr-3">Proveedor</th>
                    <th className="py-2 pr-3">Nº factura</th>
                    <th className="py-2 pr-3 text-right">Total</th>
                    <th className="py-2 pr-3">Incidencia</th>
                    <th className="py-2 pr-3">Estado</th>
                    <th className="py-2 pr-3 text-center">a3doc</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {incidenciasLista.map((f) => (
                    <tr key={f.id} className="border-b last:border-0 align-top">
                      <td className="py-2 pr-3 whitespace-nowrap">{f.fecha_factura || '-'}</td>
                      <td className="py-2 pr-3">{f.proveedor_nombre || '-'}</td>
                      <td className="py-2 pr-3">{f.numero_factura || '-'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {f.importe_total != null ? Number(f.importe_total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' }) : '-'}
                      </td>
                      <td className="py-2 pr-3 max-w-[280px] text-xs text-gray-700">{f.incidencia?.texto || '-'}</td>
                      <td className="py-2 pr-3">
                        <Badge className={f.incidencia?.estado === 'resuelta' ? 'bg-green-100 text-green-700 font-normal' : 'bg-amber-100 text-amber-800 font-normal'}>
                          {f.incidencia?.estado === 'resuelta' ? 'Resuelta' : 'Abierta'}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-center whitespace-nowrap">
                        {f.a3doc_document_id ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-green-700"
                            title={f.a3doc_subido_en ? `Subido a a3doc el ${formatFechaHora(f.a3doc_subido_en)}` : 'Subido a a3doc'}
                          >
                            <CheckCircle2 className="w-3 h-3" /> a3doc
                          </span>
                        ) : (
                          <button
                            onClick={() => subirA3docPendiente(f.id)}
                            disabled={subiendoA3docIncId === f.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-medium disabled:opacity-60"
                            title="Enviar a a3doc; la factura sigue pendiente de vincular"
                          >
                            <Cloud className="w-3.5 h-3.5" /> {subiendoA3docIncId === f.id ? 'Subiendo...' : 'a3doc'}
                          </button>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {f.drive_link && (
                          <a href={f.drive_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs">
                            Ver <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Validación de Vinculación */}
      <Dialog open={showValidacionModal} onOpenChange={setShowValidacionModal}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-600" />
              Validar Vinculación Automática
            </DialogTitle>
          </DialogHeader>
          
          {vinculacionParaValidar && (
            <div className="space-y-4">
              {/* Info del Score */}
              <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-gray-50">
                <span className="text-sm text-gray-600">Coincidencia:</span>
                <Badge className={`text-lg px-3 py-1 ${
                  vinculacionParaValidar.cargo?.match_score >= 85 
                    ? 'bg-green-100 text-green-700' 
                    : vinculacionParaValidar.cargo?.match_score >= 70 
                    ? 'bg-yellow-100 text-yellow-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {vinculacionParaValidar.cargo?.match_score}%
                </Badge>
                {vinculacionParaValidar.cargo?.match_reasons?.length > 0 && (
                  <span className="text-xs text-gray-500 ml-2">
                    ({vinculacionParaValidar.cargo.match_reasons.join(', ')})
                  </span>
                )}
              </div>

              {/* Comparación lado a lado */}
              <div className="grid grid-cols-2 gap-4">
                {/* Factura */}
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Factura
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Proveedor:</span>
                      <p className="font-medium">{vinculacionParaValidar.factura?.proveedor || 'Sin datos'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Archivo:</span>
                      <p className="font-medium truncate" title={vinculacionParaValidar.factura?.archivo}>
                        {vinculacionParaValidar.factura?.archivo || 'Sin archivo'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha:</span>
                      <p className="font-medium">{vinculacionParaValidar.factura?.fecha || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Importe:</span>
                      <p className="font-bold text-lg text-blue-700">
                        {formatNumber(vinculacionParaValidar.factura?.importe || 0)}
                      </p>
                    </div>
                    {vinculacionParaValidar.factura?.drive_file_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => window.open(`https://drive.google.com/file/d/${vinculacionParaValidar.factura.drive_file_id}/view`, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-2" />
                        Ver en Drive
                      </Button>
                    )}
                  </div>
                </div>

                {/* Cargo Bancario */}
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Cargo Bancario
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Concepto:</span>
                      <p className="font-medium">{vinculacionParaValidar.cargo?.concepto || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha:</span>
                      <p className="font-medium">{vinculacionParaValidar.cargo?.fecha || '-'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Importe:</span>
                      <p className="font-bold text-lg text-green-700">
                        {formatNumber(Math.abs(vinculacionParaValidar.cargo?.importe || 0))}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Diferencia de importes */}
              {vinculacionParaValidar.factura?.importe && vinculacionParaValidar.cargo?.importe && (
                <div className={`p-3 rounded-lg text-center ${
                  Math.abs(vinculacionParaValidar.factura.importe - Math.abs(vinculacionParaValidar.cargo.importe)) < 1
                    ? 'bg-green-100 text-green-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  <span className="text-sm">
                    Diferencia de importes: <strong>
                      {formatNumber(Math.abs(vinculacionParaValidar.factura.importe - Math.abs(vinculacionParaValidar.cargo.importe)))}
                    </strong>
                  </span>
                </div>
              )}

              {/* Warning si score bajo */}
              {vinculacionParaValidar.cargo?.match_score < 70 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Score bajo:</strong> Esta vinculación tiene una coincidencia baja. 
                    Revisa cuidadosamente que la factura corresponda al cargo bancario.
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowValidacionModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={validandoVinculacion}
              onClick={async () => {
                setValidandoVinculacion(true);
                try {
                  const res = await fetch(`${API_URL}/api/finanzas/auto-procesar/validar-vinculacion/${vinculacionParaValidar.cargo?.id}?accion=rechazar`, {
                    method: 'POST',
                    headers
                  });
                  // Consumir el body
                  await res.json().catch(() => ({}));
                  
                  if (res.ok) {
                    setShowValidacionModal(false);
                    setVinculacionParaValidar(null);
                    const vinculacionesRes = await fetch(`${API_URL}/api/finanzas/auto-procesar/vinculaciones-recientes?limite=20&solo_2025=true`, { headers });
                    if (vinculacionesRes.ok) setVinculacionesRecientes(await vinculacionesRes.json());
                    const estadoRes = await fetch(`${API_URL}/api/finanzas/auto-procesar/estado-drive`, { headers });
                    if (estadoRes.ok) setEstadoDrive2025(await estadoRes.json());
                  } else {
                    alert('Error al rechazar');
                  }
                } catch (e) {
                  console.error('Error rechazando:', e);
                  alert('Error al rechazar: ' + e.message);
                } finally {
                  setValidandoVinculacion(false);
                }
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Rechazar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              disabled={validandoVinculacion}
              onClick={async () => {
                setValidandoVinculacion(true);
                try {
                  const res = await fetch(`${API_URL}/api/finanzas/auto-procesar/validar-vinculacion/${vinculacionParaValidar.cargo?.id}?accion=aprobar`, {
                    method: 'POST',
                    headers
                  });
                  // Consumir el body
                  await res.json().catch(() => ({}));
                  
                  if (res.ok) {
                    setShowValidacionModal(false);
                    setVinculacionParaValidar(null);
                    const vinculacionesRes = await fetch(`${API_URL}/api/finanzas/auto-procesar/vinculaciones-recientes?limite=20&solo_2025=true`, { headers });
                    if (vinculacionesRes.ok) setVinculacionesRecientes(await vinculacionesRes.json());
                    const estadoRes = await fetch(`${API_URL}/api/finanzas/auto-procesar/estado-drive`, { headers });
                    if (estadoRes.ok) setEstadoDrive2025(await estadoRes.json());
                  } else {
                    alert('Error al aprobar');
                  }
                } catch (e) {
                  alert('Error al aprobar');
                } finally {
                  setValidandoVinculacion(false);
                }
              }}
            >
              <Check className="w-4 h-4 mr-2" />
              Aprobar Vinculación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================================ */}
      {/* MODAL VINCULAR V3 - Sistema de integridad relacional total */}
      {/* ============================================================================ */}
      <Dialog open={showVincularV3} onOpenChange={(open) => {
        setShowVincularV3(open);
        if (!open) {
          setBusquedaV3('');
          setResultadosV3([]);
          setDocumentoV3Seleccionado(null);
          setDocumentosSeleccionados([]);
          setSugerenciasV3([]);
        }
        // El fetch de operacionesFinanciacion se dispara en el useEffect
        // que escucha showMarcarGastoDirecto/showVincularV3 (ver arriba).
      }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-green-600" />
              Vincular Ingreso
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">
              Selecciona uno o varios presupuestos para vincular este abono
            </p>
          </DialogHeader>
          
          {abonoSeleccionado && (
            <div className="space-y-4">
              {/* Info del abono */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{abonoSeleccionado.concepto?.slice(0, 60)}</p>
                    <p className="text-sm text-gray-600">{formatFecha(abonoSeleccionado.fecha)}</p>
                  </div>
                  <p className="text-xl font-bold text-green-600">+{formatNumber(abonoSeleccionado.importe)}</p>
                </div>
              </div>

              {/* Selector de tipo de vinculación */}
              <div className="flex flex-wrap gap-2" data-testid="tipo-vinculacion-selector">
                <Button 
                  variant={tipoVinculacionAbono === 'factura' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('factura')}
                  data-testid="tipo-cobro-cliente"
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Cobro de Cliente
                </Button>
                <Button 
                  variant={tipoVinculacionAbono === 'devolucion' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('devolucion')}
                  data-testid="tipo-devolucion"
                >
                  <TrendingDown className="w-4 h-4 mr-1" />
                  Devolución Proveedor
                </Button>
                <Button
                  variant={tipoVinculacionAbono === 'fianza' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('fianza')}
                  data-testid="tipo-fianza"
                >
                  <Receipt className="w-4 h-4 mr-1" />
                  Fianza Recuperada
                </Button>
                <Button
                  variant={tipoVinculacionAbono === 'financiacion' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('financiacion')}
                  data-testid="tipo-financiacion"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Financiación / Préstamo
                </Button>
              </div>

              {/* === Sección: Financiación === */}
              {tipoVinculacionAbono === 'financiacion' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Vincula este ingreso a una operación de financiación (préstamo, Click&Pay, línea de crédito o préstamo de socio).
                    Se registrará como <b>desembolso</b> (entrada de capital) y no contará como ingreso operativo en el P&L.
                  </p>
                  {operacionesFinanciacion.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                      No hay operaciones de financiación activas. Créalas desde la tab <b>Deudas</b>.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {operacionesFinanciacion.map(op => {
                        const tipoLabel = {
                          'click_pay': 'Click & Pay',
                          'prestamo': 'Préstamo',
                          'linea_credito': 'Línea de crédito',
                          'prestamo_socio': 'Préstamo de socio',
                        }[op.tipo] || op.tipo;
                        const tipoColor = {
                          'click_pay': 'border-amber-300 bg-amber-50',
                          'prestamo': 'border-red-300 bg-red-50',
                          'linea_credito': 'border-blue-300 bg-blue-50',
                          'prestamo_socio': 'border-emerald-300 bg-emerald-50',
                        }[op.tipo] || 'border-slate-300 bg-slate-50';
                        const isSelected = operacionFinanciacionId === op.id;
                        return (
                          <button
                            key={op.id}
                            type="button"
                            onClick={() => setOperacionFinanciacionId(op.id)}
                            className={`w-full text-left p-3 border-2 rounded-lg transition-all ${
                              isSelected ? `${tipoColor} ring-2 ring-offset-1 ring-blue-400` : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="text-[10px]">{tipoLabel}</Badge>
                                  <span className="font-semibold text-sm truncate">{op.nombre}</span>
                                  {op.estado === 'cerrada' && (
                                    <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">cerrada</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-slate-600 mt-1">
                                  {op.tipo === 'prestamo_socio' && op.socio_nombre && <span>Socio: {op.socio_nombre}</span>}
                                  {op.tipo === 'linea_credito' && op.limite && <span>Límite {formatNumber(op.limite)} · Dispuesto {formatNumber(op.dispuesto || 0)}</span>}
                                  {(op.tipo === 'prestamo' || op.tipo === 'click_pay') && op.importe_cuota && (
                                    <span>Cuota {formatNumber(op.importe_cuota)} · {op.cuotas_pagadas || 0}/{op.num_cuotas || '?'}</span>
                                  )}
                                </p>
                              </div>
                              {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0 mt-1" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* === Sección: Cobro de Cliente === */}
              {tipoVinculacionAbono === 'factura' && (
              <>
              {/* Barra de resumen de asignación */}
              {documentosSeleccionados.length > 0 && (() => {
                const totalAsignado = documentosSeleccionados.reduce((s, d) => s + d.importe_asignado, 0);
                const totalPendienteFacturas = documentosSeleccionados.reduce((s, d) => s + (d.pendiente || 0), 0);
                const importeAbono = abonoSeleccionado.importe;
                const diff = importeAbono - totalAsignado;
                const pct = importeAbono > 0 ? Math.min(100, (totalAsignado / importeAbono) * 100) : 0;
                const esCuadrado = Math.abs(diff) < 0.5;
                const sobraIngreso = diff >= 0.5;
                const faltaIngreso = diff < -0.5;
                // Check if facturas still have pending amounts after this payment
                const pendienteDespues = totalPendienteFacturas - totalAsignado;
                const hayPendienteFactura = pendienteDespues > 0.5;
                return (
                  <div className={`border-2 rounded-lg p-3 ${esCuadrado && !hayPendienteFactura ? 'bg-green-50 border-green-300' : esCuadrado && hayPendienteFactura ? 'bg-amber-50 border-amber-300' : sobraIngreso ? 'bg-amber-50 border-amber-400' : 'bg-red-50 border-red-400'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-sm font-medium ${esCuadrado ? (hayPendienteFactura ? 'text-amber-700' : 'text-green-700') : sobraIngreso ? 'text-amber-700' : 'text-red-700'}`}>{documentosSeleccionados.length} documento{documentosSeleccionados.length > 1 ? 's' : ''} seleccionado{documentosSeleccionados.length > 1 ? 's' : ''}</span>
                      <span data-testid="payment-status-label" className={`text-base font-bold px-3 py-0.5 rounded-full ${esCuadrado && !hayPendienteFactura ? 'bg-green-100 text-green-700' : esCuadrado && hayPendienteFactura ? 'bg-amber-100 text-amber-700' : sobraIngreso ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        {esCuadrado && !hayPendienteFactura ? 'Cuadrado' : esCuadrado && hayPendienteFactura ? `Parcial: faltan ${formatNumber(pendienteDespues)} en factura` : sobraIngreso ? `EXCESO: sobran ${formatNumber(diff)}` : `FALTA: ${formatNumber(-diff)}`}
                      </span>
                    </div>
                    <div className={`h-2.5 rounded-full overflow-hidden ${esCuadrado && !hayPendienteFactura ? 'bg-green-200' : esCuadrado && hayPendienteFactura ? 'bg-amber-200' : sobraIngreso ? 'bg-amber-200' : 'bg-red-200'}`}>
                      <div className={`h-full rounded-full transition-all ${esCuadrado && !hayPendienteFactura ? 'bg-green-500' : esCuadrado && hayPendienteFactura ? 'bg-amber-500' : sobraIngreso ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="flex justify-between text-xs mt-1.5">
                      <span className={esCuadrado && !hayPendienteFactura ? 'text-green-600' : 'text-amber-600'}>Facturas seleccionadas: {formatNumber(totalAsignado)}</span>
                      <span className="text-gray-500">Ingreso: {formatNumber(importeAbono)}</span>
                    </div>
                    {/* Detalle por factura seleccionada (solo lectura) */}
                    <div className="mt-2 space-y-1.5">
                      {documentosSeleccionados.map(doc => (
                        <div key={doc.key} className="flex items-center gap-2 bg-white rounded-lg px-2.5 py-1.5 border border-gray-200">
                          <button
                            className="text-red-400 hover:text-red-600 flex-shrink-0"
                            onClick={() => setDocumentosSeleccionados(prev => prev.filter(d => d.key !== doc.key))}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium">#{doc.numero} {doc.factura_numero ? `(${doc.factura_numero})` : ''}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-700">{formatNumber(doc.importe_asignado)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Sugerencias inteligentes */}
              {(cargandoSugerenciasV3 || sugerenciasV3.length > 0) && (
                <div className="border border-amber-200 rounded-lg overflow-hidden">
                  <div className="p-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">Sugerencias inteligentes</span>
                    <span className="text-xs text-amber-600">(selecciona una o varias)</span>
                  </div>
                  {cargandoSugerenciasV3 ? (
                    <div className="p-4 text-center">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto text-amber-600" />
                      <p className="text-xs text-gray-500 mt-1">Analizando coincidencias...</p>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto">
                      {sugerenciasV3.map((sug, idx) => {
                        const isSelected = documentosSeleccionados.some(d => d.key === sug.id);
                        const confStyle = sug.confianza === 'alta' ? 'bg-green-100 text-green-800' : sug.confianza === 'media' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600';
                        const confText = sug.confianza === 'alta' ? 'Alta' : sug.confianza === 'media' ? 'Media' : 'Baja';
                        return (
                          <div
                            key={sug.id || idx}
                            className={`p-3 border-b last:border-0 cursor-pointer transition-colors ${
                              isSelected ? 'bg-green-50 border-l-4 border-l-green-500' : 'hover:bg-amber-50'
                            }`}
                            onClick={() => togglePresupuestoSeleccion({
                              key: sug.id,
                              presupuesto_id: sug.id,
                              factura_id: sug.factura_id || null,
                              documento_tipo: 'presupuesto',
                              es_por_jornada: false,
                              numero: sug.numero,
                              cliente: sug.cliente,
                              factura_numero: sug.factura_numero,
                              pendiente: sug.pendiente,
                              total: sug.total,
                            })}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex items-start gap-2">
                                <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                                  {isSelected && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${confStyle}`}>{confText}</span>
                                    {sug.factura_numero && <span className="font-medium text-blue-600 text-sm">Fact. {sug.factura_numero}</span>}
                                    <span className="text-gray-500 text-sm">Presup. #{sug.numero}</span>
                                  </div>
                                  <p className="text-sm text-gray-600 truncate">{sug.cliente}</p>
                                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                    {(sug.razones || []).map((r, ri) => (
                                      <span key={ri} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{r}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0 ml-3">
                                <p className="text-sm font-medium">{formatNumber(sug.total)}</p>
                                {sug.pendiente > 0 && <p className="text-xs text-red-600">Pend: {formatNumber(sug.pendiente)}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Búsqueda manual de documentos */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {sugerenciasV3.length > 0 ? 'O buscar manualmente:' : 'Buscar factura o presupuesto'}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Escribe numero de factura (ej: 1050) o nombre de cliente..."
                    value={busquedaV3}
                    onChange={(e) => {
                      setBusquedaV3(e.target.value);
                      buscarDocumentosV3(e.target.value);
                    }}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Tip: Escribe solo los ultimos 4 digitos para encontrar facturas con prefijo DC2025/DC2026
                </p>
                <label className="flex items-center gap-1.5 cursor-pointer mt-2" data-testid="mostrar-pagadas-v3-check">
                  <input
                    type="checkbox"
                    checked={mostrarPagadas}
                    onChange={(e) => {
                      const newVal = e.target.checked;
                      setMostrarPagadas(newVal);
                      if (busquedaV3) {
                        fetch(`${API_URL}/api/cobros-v3/buscar?q=${encodeURIComponent(busquedaV3)}&limit=20&incluir_pagadas=${newVal}`, { headers })
                          .then(r => r.ok ? r.json() : null).then(d => { if (d) setResultadosV3(d.items || []); });
                      }
                    }}
                    className="w-3.5 h-3.5 rounded text-blue-600"
                  />
                  <span className="text-xs text-gray-500">Mostrar ya pagadas</span>
                </label>
              </div>

              {/* Resultados de búsqueda */}
              {buscandoV3 ? (
                <div className="text-center py-4">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto text-green-600" />
                  <p className="text-sm text-gray-500 mt-1">Buscando...</p>
                </div>
              ) : resultadosV3.length > 0 ? (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {resultadosV3.map((doc, idx) => {
                    // Multievento por jornada: cada factura es un documento único
                    // (key = factura). El resto se agrupa por presupuesto.
                    const esPorJornada = !!doc.es_por_jornada;
                    const selKey = esPorJornada ? doc.documento_id : (doc.presupuesto_id || doc.documento_id);
                    const isSelected = documentosSeleccionados.some(d => d.key === selKey);
                    const docNorm = {
                      key: selKey,
                      presupuesto_id: doc.presupuesto_id,
                      factura_id: doc.documento_tipo === 'factura' ? doc.documento_id : null,
                      documento_tipo: doc.documento_tipo,
                      es_por_jornada: esPorJornada,
                      numero: doc.numero_presupuesto,
                      cliente: doc.cliente_nombre,
                      factura_numero: doc.numero_factura,
                      pendiente: doc.pendiente || 0,
                      total: doc.total || 0,
                    };
                    return (
                    <div
                      key={doc.documento_id || idx}
                      className={`p-3 border-b last:border-0 cursor-pointer transition-colors ${
                        isSelected ? 'bg-green-50 border-l-4 border-l-green-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        if (doc.estado === 'pagado') {
                          if (!window.confirm(`Esta factura/presupuesto ya está marcada como pagada (${formatNumber(doc.total)}). ¿Vincular solo para trazabilidad sin afectar importes?`)) return;
                        }
                        togglePresupuestoSeleccion(docNorm);
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-2">
                          <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              {doc.numero_factura && (
                                <span className="font-medium text-blue-600">#{doc.numero_factura}</span>
                              )}
                              {doc.numero_presupuesto && (
                                <span className="text-gray-500 text-sm">Presup. #{doc.numero_presupuesto}</span>
                              )}
                              {esPorJornada && doc.jornada_fecha && (
                                <span className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">Jornada {formatFecha(doc.jornada_fecha)}</span>
                              )}
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                doc.estado === 'pagado' ? 'bg-green-100 text-green-700' :
                                doc.estado === 'parcial' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {doc.estado === 'pagado' ? 'Pagado' : doc.estado === 'parcial' ? 'Parcial' : 'Pendiente'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 truncate">{doc.cliente_nombre}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{formatNumber(doc.total)}</p>
                          {doc.pendiente > 0 && (
                            <p className="text-xs text-red-600">Pend: {formatNumber(doc.pendiente)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : busquedaV3.length > 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No se encontraron documentos</p>
                </div>
              ) : null}
              </>
              )}

              {/* === Sección: Devolución de Proveedor === */}
              {tipoVinculacionAbono === 'devolucion' && (
                <div className="border rounded-lg p-4" data-testid="seccion-devolucion">
                  <h4 className="font-medium mb-3 text-blue-700">Devolución de Proveedor</h4>
                  
                  {!mostrarFormDevolucion && (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Busca la factura del proveedor a la que corresponde esta devolución.
                      </p>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Buscar por proveedor, nº factura o concepto..."
                          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                          data-testid="buscar-factura-soportada"
                          value={busquedaFacturaSoportada}
                          onChange={(e) => {
                            setBusquedaFacturaSoportada(e.target.value);
                            buscarFacturasSoportadas(e.target.value);
                          }}
                        />
                      </div>
                      {buscandoFacturas && (
                        <div className="text-center py-3"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                      )}
                      {resultadosBusquedaFactura.length > 0 && (
                        <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                          {resultadosBusquedaFactura.map(f => (
                            <div key={f.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                              onClick={() => vincularAbonoFacturaSoportada(abonoSeleccionado.id, f.id, 'devolucion')}>
                              <div>
                                <p className="font-medium">{f.proveedor_nombre}</p>
                                <p className="text-sm text-gray-600">{f.numero_factura} · {formatFecha(f.fecha_factura)}</p>
                              </div>
                              <p className="font-bold">{formatNumber(f.total)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {busquedaFacturaSoportada && !buscandoFacturas && resultadosBusquedaFactura.length === 0 && (
                        <p className="text-gray-500 text-sm py-3 text-center">No se encontraron facturas</p>
                      )}
                      <div className="border-t mt-4 pt-4">
                        <p className="text-sm text-gray-500 mb-2">¿No tienes la factura registrada?</p>
                        <button onClick={() => setMostrarFormDevolucion(true)}
                          className="w-full px-4 py-2 border-2 border-dashed border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                          data-testid="registrar-devolucion-manual">
                          Registrar devolución manualmente
                        </button>
                      </div>
                    </>
                  )}
                  {mostrarFormDevolucion && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        Registra los datos del proveedor para tener trazabilidad.
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                        <input type="text" value={datosDevolucion.proveedor}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, proveedor: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: Adobe, Amazon, etc." data-testid="devolucion-proveedor" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nº Factura Original (opcional)</label>
                        <input type="text" value={datosDevolucion.numero_factura_original}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, numero_factura_original: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Nº de la factura que te devuelven" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                        <textarea value={datosDevolucion.notas}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, notas: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          rows={2} placeholder="Motivo de la devolución, referencia, etc." />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button onClick={() => { setMostrarFormDevolucion(false); setDatosDevolucion({ proveedor: '', notas: '', numero_factura_original: '' }); }}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm">Cancelar</button>
                        <button onClick={guardarDevolucionManual}
                          disabled={guardandoDevolucion || !datosDevolucion.proveedor.trim()}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                          data-testid="guardar-devolucion">
                          {guardandoDevolucion ? <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</> : 'Guardar Devolución'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* === Sección: Fianza Recuperada === */}
              {tipoVinculacionAbono === 'fianza' && (
                <div className="border rounded-lg p-4" data-testid="seccion-fianza">
                  <h4 className="font-medium mb-3 text-purple-700">Vincular a Factura con Fianza</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Busca la factura del proveedor que incluía la fianza que te han devuelto.
                  </p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Buscar por proveedor, nº factura o concepto..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" data-testid="buscar-factura-fianza"
                      value={busquedaFacturaSoportada}
                      onChange={(e) => { setBusquedaFacturaSoportada(e.target.value); buscarFacturasSoportadas(e.target.value); }} />
                  </div>
                  {buscandoFacturas && (
                    <div className="text-center py-3"><RefreshCw className="w-4 h-4 animate-spin mx-auto" /></div>
                  )}
                  {resultadosBusquedaFactura.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                      {resultadosBusquedaFactura.map(f => (
                        <div key={f.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
                          onClick={() => vincularAbonoFacturaSoportada(abonoSeleccionado.id, f.id, 'fianza_recuperada')}>
                          <div>
                            <p className="font-medium">{f.proveedor_nombre}</p>
                            <p className="text-sm text-gray-600">{f.numero_factura} · {formatFecha(f.fecha_factura)}</p>
                          </div>
                          <p className="font-bold">{formatNumber(f.total)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {busquedaFacturaSoportada && !buscandoFacturas && resultadosBusquedaFactura.length === 0 && (
                    <p className="text-gray-500 text-sm py-3 text-center">No se encontraron facturas</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVincularV3(false)}>
              Cancelar
            </Button>
            {tipoVinculacionAbono === 'factura' && (
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={vincularConV3}
              disabled={documentosSeleccionados.length === 0 || vinculandoV3}
            >
              {vinculandoV3 ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Vinculando...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Vincular{documentosSeleccionados.length > 1 ? ` (${documentosSeleccionados.length})` : ''}
                </>
              )}
            </Button>
            )}
            {tipoVinculacionAbono === 'financiacion' && (
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={vincularAbonoAFinanciacion}
                disabled={!operacionFinanciacionId || vinculandoV3}
              >
                {vinculandoV3 ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Vinculando...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Vincular a Financiación
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Vincular Abono */}
      <Dialog open={showVincularAbono} onOpenChange={(open) => {
        setShowVincularAbono(open);
        if (!open) {
          setBusquedaFacturaSoportada('');
          setResultadosBusquedaFactura([]);
          setBusquedaCobro('');
          setResultadosBusquedaCobro({ facturas_emitidas: [], presupuestos: [] });
          setItemsSeleccionadosCobro([]); // Limpiar selección múltiple
        }
      }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-green-600" />
              Vincular Abono
            </DialogTitle>
          </DialogHeader>
          
          {abonoSeleccionado && (
            <div className="space-y-4">
              {/* Info del abono */}
              <div className="bg-green-50 p-3 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{abonoSeleccionado.concepto}</p>
                    <p className="text-sm text-gray-600">{formatFecha(abonoSeleccionado.fecha)}</p>
                  </div>
                  <p className="text-xl font-bold text-green-600">+{formatNumber(abonoSeleccionado.importe)}</p>
                </div>
              </div>

              {/* Selector de tipo de vinculación */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={tipoVinculacionAbono === 'factura' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('factura')}
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Cobro de Cliente
                </Button>
                <Button 
                  variant={tipoVinculacionAbono === 'devolucion' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('devolucion')}
                >
                  <TrendingDown className="w-4 h-4 mr-1" />
                  Devolución Proveedor
                </Button>
                <Button 
                  variant={tipoVinculacionAbono === 'fianza' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTipoVinculacionAbono('fianza')}
                >
                  <Receipt className="w-4 h-4 mr-1" />
                  Fianza Recuperada
                </Button>
              </div>

              {/* Sección para Cobro de Cliente (Factura Emitida o Presupuesto) */}
              {tipoVinculacionAbono === 'factura' && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3 text-green-700">Cobro de Cliente</h4>
                  
                  {/* Botón para cargar pendientes */}
                  <button
                    onClick={cargarPendientesCobro}
                    className="w-full mb-3 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-sm flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Ver facturas y presupuestos pendientes de cobro
                  </button>
                  
                  {/* Sugerencias automáticas si se detectó nº en el concepto */}
                  {cargandoSugerenciasAbono ? (
                    <div className="text-center py-4">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Buscando nº de factura o presupuesto en el concepto...</p>
                    </div>
                  ) : sugerenciasAbono?.facturas_emitidas?.length > 0 ? (
                    <div className="mb-4">
                      <p className="text-sm text-green-600 mb-2 font-medium">
                        Sugerencia automática: 
                        {sugerenciasAbono.presupuesto_detectado && ` Presupuesto ${sugerenciasAbono.presupuesto_detectado}`}
                        {sugerenciasAbono.factura_detectada && ` Factura ${sugerenciasAbono.factura_detectada}`}
                      </p>
                      <div className="space-y-2">
                        {sugerenciasAbono.facturas_emitidas.map(f => (
                          <div 
                            key={f.id}
                            className="flex justify-between items-center p-3 border-2 border-green-300 rounded-lg hover:bg-green-50 cursor-pointer transition-colors bg-green-50"
                            onClick={() => vincularAbonoFactura(abonoSeleccionado.id, f.id)}
                          >
                            <div>
                              <p className="font-medium">{f.numero_factura}</p>
                              <p className="text-sm text-gray-600">{f.cliente_nombre}</p>
                              <p className="text-xs text-green-600">{f.match_reason}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{formatNumber(f.total)}</p>
                              {f.pendiente_cobro > 0 && (
                                <p className="text-xs text-orange-600">Pendiente: {formatNumber(f.pendiente_cobro)}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t mt-4 pt-3">
                        <p className="text-sm text-gray-500 mb-2">¿No es la correcta? Busca manualmente:</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm py-2 px-3 mb-3 bg-gray-50 rounded">
                      No se detectó nº de factura/presupuesto en el concepto. Busca manualmente:
                    </p>
                  )}
                  
                  {/* Buscador manual */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nº factura, presupuesto o cliente..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                      value={busquedaCobro}
                      onChange={(e) => {
                        setBusquedaCobro(e.target.value);
                        if (e.target.value.length >= 1) {
                          buscarParaCobro(e.target.value);
                        } else {
                          setResultadosBusquedaCobro({ items: [], facturas_emitidas: [], presupuestos: [] });
                        }
                      }}
                    />
                  </div>
                  
                  {/* Botón Ver Todos */}
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">
                        {resultadosBusquedaCobro.total_items > 0 && 
                          `${resultadosBusquedaCobro.total_items} resultado${resultadosBusquedaCobro.total_items !== 1 ? 's' : ''}`
                        }
                      </span>
                      <label className="flex items-center gap-1.5 cursor-pointer" data-testid="mostrar-pagadas-check">
                        <input
                          type="checkbox"
                          checked={mostrarPagadas}
                          onChange={(e) => {
                            const newVal = e.target.checked;
                            setMostrarPagadas(newVal);
                            // Re-trigger search with explicit new value (avoid closure issue)
                            const importeRef = abonoSeleccionado?.importe || 0;
                            const q = busquedaCobro || '';
                            const url = `${API_URL}/api/finanzas/cobros/buscar?q=${encodeURIComponent(q)}&limit=50&solo_pendientes=true&incluir_pagadas=${newVal}${importeRef > 0 ? `&importe_ref=${importeRef}` : ''}`;
                            fetch(url, { headers }).then(r => r.ok ? r.json() : null).then(d => { if (d) setResultadosBusquedaCobro(d); });
                          }}
                          className="w-3.5 h-3.5 rounded text-blue-600"
                        />
                        <span className="text-xs text-gray-500">Mostrar ya pagadas</span>
                      </label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => buscarParaCobro('', true)}
                      disabled={buscandoCobros}
                      className="text-xs"
                    >
                      {buscandoCobros ? (
                        <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                      ) : (
                        <List className="w-3 h-3 mr-1" />
                      )}
                      Ver todos pendientes
                    </Button>
                  </div>
                  
                  {buscandoCobros && (
                    <div className="text-center py-3">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                    </div>
                  )}
                  
                  {/* Barra de selección múltiple */}
                  {itemsSeleccionadosCobro.length > 0 && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-green-800">
                            {itemsSeleccionadosCobro.length} item{itemsSeleccionadosCobro.length > 1 ? 's' : ''} seleccionado{itemsSeleccionadosCobro.length > 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-green-600">
                            Total pendiente: {formatNumber(totalSeleccionadoCobro)}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setItemsSeleccionadosCobro([])}
                          >
                            Limpiar
                          </Button>
                          <Button
                            size="sm"
                            onClick={vincularMultiplesItems}
                            disabled={vinculandoMultiple}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {vinculandoMultiple ? (
                              <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                              <Check className="w-4 h-4 mr-1" />
                            )}
                            Vincular Seleccionados
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Resultados: Lista Unificada */}
                  {resultadosBusquedaCobro.items?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        FACTURAS Y PRESUPUESTOS PENDIENTES (clic para seleccionar)
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {resultadosBusquedaCobro.items.map(item => (
                          <div 
                            key={`${item.tipo}-${item.id}`}
                            className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                              isItemSeleccionadoCobro(item, item.tipo) 
                                ? item.tipo === 'factura' ? 'bg-green-100 border-green-400' : 'bg-blue-100 border-blue-400'
                                : item.tipo === 'factura' ? 'hover:bg-green-50' : 'hover:bg-blue-50'
                            }`}
                            onClick={() => toggleItemSeleccionadoCobro(item, item.tipo)}
                          >
                            <input
                              type="checkbox"
                              checked={isItemSeleccionadoCobro(item, item.tipo)}
                              onChange={() => {}}
                              className={`w-4 h-4 rounded ${item.tipo === 'factura' ? 'text-green-600' : 'text-blue-600'}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                  item.tipo === 'factura' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {item.tipo === 'factura' ? 'FAC' : 'PRES'}
                                </span>
                                <span className="font-medium text-sm">
                                  {item.numero_factura || `#${item.numero_presupuesto}`}
                                </span>
                                {item.tipo === 'factura' && item.numero_presupuesto && (
                                  <span className="text-xs text-gray-400">
                                    (Pres. #{item.numero_presupuesto})
                                  </span>
                                )}
                                {item.tipo === 'presupuesto' && item.numero_factura && (
                                  <span className="text-xs text-green-600">
                                    ({item.numero_factura})
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 truncate">{item.cliente}</p>
                              {item.titulo && <p className="text-xs text-gray-400 truncate">{item.titulo}</p>}
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="font-bold text-sm">{formatNumber(item.total)}</p>
                              {item.estado_cobro === 'pagada' ? (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">Pagado</span>
                              ) : (
                                <p className="text-xs text-orange-600">Pdte: {formatNumber(item.pendiente)}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Compatibilidad: Si no hay items pero sí facturas_emitidas o presupuestos del formato antiguo */}
                  {!resultadosBusquedaCobro.items?.length && resultadosBusquedaCobro.facturas_emitidas?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">FACTURAS EMITIDAS (clic para seleccionar)</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {resultadosBusquedaCobro.facturas_emitidas.map(f => (
                          <div 
                            key={f.id}
                            className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                              isItemSeleccionadoCobro(f, 'factura') 
                                ? 'bg-green-100 border-green-400' 
                                : 'hover:bg-green-50'
                            }`}
                            onClick={() => toggleItemSeleccionadoCobro(f, 'factura')}
                          >
                            <input
                              type="checkbox"
                              checked={isItemSeleccionadoCobro(f, 'factura')}
                              onChange={() => {}}
                              className="w-4 h-4 text-green-600 rounded"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{f.numero_factura}</p>
                              <p className="text-xs text-gray-600">{f.cliente_nombre || f.cliente_empresa}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">{formatNumber(f.total)}</p>
                              <p className="text-xs text-orange-600">Pdte: {formatNumber(f.pendiente_cobro)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!resultadosBusquedaCobro.items?.length && resultadosBusquedaCobro.presupuestos?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">PRESUPUESTOS (clic para seleccionar)</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {resultadosBusquedaCobro.presupuestos.map(p => (
                          <div 
                            key={p.id}
                            className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors ${
                              isItemSeleccionadoCobro(p, 'presupuesto') 
                                ? 'bg-blue-100 border-blue-400' 
                                : 'hover:bg-blue-50'
                            }`}
                            onClick={() => toggleItemSeleccionadoCobro(p, 'presupuesto')}
                          >
                            <input
                              type="checkbox"
                              checked={isItemSeleccionadoCobro(p, 'presupuesto')}
                              onChange={() => {}}
                              className="w-4 h-4 text-blue-600 rounded"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">#{p.numero}</p>
                              <p className="text-xs text-gray-600">{p.cliente_nombre || p.cliente_empresa}</p>
                              {p.titulo && <p className="text-xs text-gray-500">{p.titulo}</p>}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-sm">{formatNumber(p.total_con_iva || p.importe_total || p.precio_total)}</p>
                              <p className="text-xs text-orange-600">Pdte: {formatNumber(p.pendiente_cobro)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(busquedaCobro || resultadosBusquedaCobro.ver_todos) && !buscandoCobros && 
                   !resultadosBusquedaCobro.items?.length &&
                   !resultadosBusquedaCobro.facturas_emitidas?.length && 
                   !resultadosBusquedaCobro.presupuestos?.length && (
                    <p className="text-gray-500 text-sm py-3 text-center">No se encontraron resultados pendientes de cobro</p>
                  )}
                </div>
              )}

              {/* Sección para Devolución de Proveedor */}
              {tipoVinculacionAbono === 'devolucion' && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3 text-blue-700">Devolución de Proveedor</h4>
                  
                  {/* Opción 1: Vincular a factura soportada existente */}
                  {!mostrarFormDevolucion && (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Busca la factura del proveedor a la que corresponde esta devolución.
                      </p>
                      
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Buscar por proveedor, nº factura o concepto..."
                          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                          value={busquedaFacturaSoportada}
                          onChange={(e) => {
                            setBusquedaFacturaSoportada(e.target.value);
                            buscarFacturasSoportadas(e.target.value);
                          }}
                        />
                      </div>
                      
                      {buscandoFacturas && (
                        <div className="text-center py-3">
                          <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                        </div>
                      )}
                      
                      {resultadosBusquedaFactura.length > 0 && (
                        <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                          {resultadosBusquedaFactura.map(f => (
                            <div 
                              key={f.id}
                              className="flex justify-between items-center p-3 border rounded-lg hover:bg-blue-50 cursor-pointer transition-colors"
                              onClick={() => vincularAbonoFacturaSoportada(abonoSeleccionado.id, f.id, 'devolucion')}
                            >
                              <div>
                                <p className="font-medium">{f.proveedor_nombre}</p>
                                <p className="text-sm text-gray-600">{f.numero_factura} · {formatFecha(f.fecha_factura)}</p>
                              </div>
                              <p className="font-bold">{formatNumber(f.total)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {busquedaFacturaSoportada && !buscandoFacturas && resultadosBusquedaFactura.length === 0 && (
                        <p className="text-gray-500 text-sm py-3 text-center">No se encontraron facturas</p>
                      )}
                      
                      {/* Separador y opción manual */}
                      <div className="border-t mt-4 pt-4">
                        <p className="text-sm text-gray-500 mb-2">¿No tienes la factura registrada en el sistema?</p>
                        <button
                          onClick={() => setMostrarFormDevolucion(true)}
                          className="w-full px-4 py-2 border-2 border-dashed border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors text-sm"
                        >
                          Registrar devolución manualmente
                        </button>
                      </div>
                    </>
                  )}
                  
                  {/* Opción 2: Formulario manual para devolución */}
                  {mostrarFormDevolucion && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                        Registra los datos del proveedor para tener trazabilidad de esta devolución.
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                        <input
                          type="text"
                          value={datosDevolucion.proveedor}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, proveedor: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: Adobe, Amazon, etc."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nº Factura Original (opcional)</label>
                        <input
                          type="text"
                          value={datosDevolucion.numero_factura_original}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, numero_factura_original: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          placeholder="Nº de la factura que te devuelven"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notas (opcional)</label>
                        <textarea
                          value={datosDevolucion.notas}
                          onChange={(e) => setDatosDevolucion(prev => ({...prev, notas: e.target.value}))}
                          className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Motivo de la devolución, referencia, etc."
                        />
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => {
                            setMostrarFormDevolucion(false);
                            setDatosDevolucion({ proveedor: '', notas: '', numero_factura_original: '' });
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={guardarDevolucionManual}
                          disabled={guardandoDevolucion || !datosDevolucion.proveedor.trim()}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                        >
                          {guardandoDevolucion ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Guardando...
                            </>
                          ) : (
                            'Guardar Devolución'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Sección para Fianza Recuperada */}
              {tipoVinculacionAbono === 'fianza' && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3 text-purple-700">Vincular a Factura con Fianza</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Busca la factura del proveedor que incluía la fianza que te han devuelto.
                  </p>
                  
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por proveedor, nº factura o concepto..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                      value={busquedaFacturaSoportada}
                      onChange={(e) => {
                        setBusquedaFacturaSoportada(e.target.value);
                        buscarFacturasSoportadas(e.target.value);
                      }}
                    />
                  </div>
                  
                  {buscandoFacturas && (
                    <div className="text-center py-3">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto" />
                    </div>
                  )}
                  
                  {resultadosBusquedaFactura.length > 0 && (
                    <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                      {resultadosBusquedaFactura.map(f => (
                        <div 
                          key={f.id}
                          className="flex justify-between items-center p-3 border rounded-lg hover:bg-purple-50 cursor-pointer transition-colors"
                          onClick={() => vincularAbonoFacturaSoportada(abonoSeleccionado.id, f.id, 'fianza_recuperada')}
                        >
                          <div>
                            <p className="font-medium">{f.proveedor_nombre}</p>
                            <p className="text-sm text-gray-600">{f.numero_factura} · {formatFecha(f.fecha_factura)}</p>
                          </div>
                          <p className="font-bold">{formatNumber(f.total)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {busquedaFacturaSoportada && !buscandoFacturas && resultadosBusquedaFactura.length === 0 && (
                    <p className="text-gray-500 text-sm py-3 text-center">No se encontraron facturas</p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVincularAbono(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Mover Archivo */}
      <Dialog open={showMoverArchivo} onOpenChange={setShowMoverArchivo}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderInput className="w-5 h-5 text-blue-600" />
              Mover Archivo
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {archivoParaMover && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-sm truncate">{archivoParaMover.name}</p>
                {archivoParaMover.proveedor && (
                  <p className="text-xs text-gray-500">{archivoParaMover.proveedor}</p>
                )}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-2">Carpeta destino</label>
              <Select value={carpetaDestino} onValueChange={setCarpetaDestino}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona una carpeta..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {carpetasDisponibles.map((carpeta) => (
                    <SelectItem key={carpeta.valor} value={carpeta.valor}>
                      {carpeta.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {carpetaDestino === 'papelera' && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ El archivo se moverá a la Papelera. Podrás recuperarlo desde Google Drive si lo necesitas.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowMoverArchivo(false);
                setArchivoParaMover(null);
                setCarpetaDestino('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              disabled={!carpetaDestino || moviendoArchivo}
              onClick={async () => {
                if (!archivoParaMover?.id || !carpetaDestino) return;
                
                setMoviendoArchivo(true);
                try {
                  const res = await fetch(`${API_URL}/api/finanzas/drive/mover-archivo?file_id=${archivoParaMover.id}&destino=${encodeURIComponent(carpetaDestino)}`, {
                    method: 'POST',
                    headers
                  });
                  const data = await res.json();
                  
                  if (data.success) {
                    alert(`✅ ${data.mensaje}`);
                    setShowMoverArchivo(false);
                    setArchivoParaMover(null);
                    setCarpetaDestino('');
                    // Recargar lista de archivos
                    cargarDatosPestaña();
                  } else {
                    alert('Error: ' + (data.detail || 'No se pudo mover el archivo'));
                  }
                } catch (e) {
                  console.error('Error moviendo archivo:', e);
                  alert('Error: ' + e.message);
                } finally {
                  setMoviendoArchivo(false);
                }
              }}
            >
              {moviendoArchivo ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Moviendo...
                </>
              ) : (
                <>
                  <FolderInput className="w-4 h-4 mr-2" />
                  Mover
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal gestión de exceso de pago */}
      {excesoData && (
        <ExcesoModalFinanzas
          exceso={excesoData}
          onClose={() => setExcesoData(null)}
          onDone={() => {
            setExcesoData(null);
            cargarAbonos();
          }}
        />
      )}

      {/* Modal de revisión de duplicados */}
      <Dialog open={showDuplicadosModal} onOpenChange={setShowDuplicadosModal}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Facturas con posible duplicado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Estas facturas coinciden con otra ya vinculada (mismo CIF de proveedor y nº de factura).
              No cuentan en el IVA hasta que decidas.
            </p>

            {duplicadosPendientes.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                No hay duplicados pendientes.
              </div>
            ) : (
              duplicadosPendientes.map((par) => {
                const s = par.sospechosa;
                const o = par.original;
                const resolviendo = resolviendoDuplicado === s.id;
                return (
                  <div key={s.id} className="border border-slate-200 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-amber-50 border border-amber-200 rounded p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-2">
                          <FileWarning className="w-3.5 h-3.5" /> Sospechosa (no cuenta)
                        </div>
                        <p className="text-sm font-medium text-slate-900">{s.proveedor_nombre || '—'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">CIF: {s.proveedor_cif || '—'}</p>
                        <p className="text-xs text-slate-500">Nº: {s.numero_factura || '—'}</p>
                        <p className="text-xs text-slate-500">Fecha: {s.fecha_factura || '—'}</p>
                        <p className="text-sm font-semibold text-slate-900 mt-2">
                          Total: {formatNumber(s.importe_total || 0)} · IVA: {formatNumber(s.importe_iva || 0)}
                        </p>
                        <p className="text-xs text-slate-400 mt-1 truncate" title={s.drive_file_name}>
                          Archivo: {s.drive_file_name || s.drive_file_id}
                        </p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded p-3">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-2">
                          <CheckCircle className="w-3.5 h-3.5" /> Original (ya contabilizada)
                        </div>
                        {o ? (
                          <>
                            <p className="text-sm font-medium text-slate-900">{o.proveedor_nombre || '—'}</p>
                            <p className="text-xs text-slate-500 mt-0.5">CIF: {o.proveedor_cif || '—'}</p>
                            <p className="text-xs text-slate-500">Nº: {o.numero_factura || '—'}</p>
                            <p className="text-xs text-slate-500">Fecha: {o.fecha_factura || '—'}</p>
                            <p className="text-sm font-semibold text-slate-900 mt-2">
                              Total: {formatNumber(o.importe_total || 0)} · IVA: {formatNumber(o.importe_iva || 0)}
                            </p>
                            <p className="text-xs text-slate-400 mt-1 truncate" title={o.drive_file_name}>
                              Archivo: {o.drive_file_name || o.drive_file_id}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-slate-400">Original no encontrada.</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 justify-end pt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={resolviendo}
                        onClick={() => resolverDuplicado(s.id, 'confirmar_distinta')}
                      >
                        {resolviendo ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                        Son distintas (sumar al IVA)
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={resolviendo}
                        onClick={() => {
                          if (window.confirm('¿Eliminar esta factura? Se desvinculará del cargo y se borrará del sistema.')) {
                            resolverDuplicado(s.id, 'eliminar');
                          }
                        }}
                      >
                        {resolviendo ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                        Eliminar (es duplicado)
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicadosModal(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Desglose de tarjeta consolidada */}
      {desgloseTarjeta && (() => {
        const d = desgloseTarjeta;
        const c = d.cargo;
        // Suma/diferencia efectivas (usa las del API si existen, si no las calcula)
        const sumaCalc = (d.subcargos || []).reduce((s, x) => s + (Number(x.importe) || 0), 0);
        const totalSubcargos = (d.subcargos || []).length;
        const conFactura = (d.subcargos || []).filter((x) => x.factura_drive_id).length;
        const sumaEfectiva = d.suma ?? sumaCalc;
        const difEfectiva = d.diferencia ?? ((c.importe || 0) - sumaCalc);
        const cargoImporte = c.importe || 0;

        const headersJson = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
        const cerrar = () => {
          setDesgloseTarjeta(null);
          setFacturasSugeridas(null);
          setNuevoSubcargo({ fecha_compra: '', concepto: '', proveedor: '', categoria: '', importe: '', notas: '' });
          extractoFileRef.current = null;
        };

        const marcarTarjeta = async () => {
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/marcar-tarjeta`, {
              method: 'PATCH',
              headers: headersJson,
              body: JSON.stringify({ es_tarjeta: true })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setDesgloseTarjeta(prev => prev ? { ...prev, cargo: { ...prev.cargo, es_consolidado_tarjeta: true, tarjeta: data.tarjeta ?? prev.cargo.tarjeta } } : prev);
              cargarCargosBancarios();
            } else {
              alert('Error al marcar la tarjeta: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al marcar la tarjeta: ' + e.message);
          }
        };

        const guardarCampoSubcargo = async (subId, campo, valor) => {
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/${subId}`, {
              method: 'PUT',
              headers: headersJson,
              body: JSON.stringify({ [campo]: campo === 'importe' ? (parseFloat(valor) || 0) : valor })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              aplicarShapeSubcargos(data);
            } else {
              alert('Error al guardar la compra: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al guardar la compra: ' + e.message);
          }
        };

        const editarSubcargoLocal = (subId, campo, valor) => {
          setDesgloseTarjeta(prev => prev ? {
            ...prev,
            subcargos: (prev.subcargos || []).map(s => s.id === subId ? { ...s, [campo]: valor } : s)
          } : prev);
        };

        const eliminarSubcargo = async (subId) => {
          if (!window.confirm('¿Eliminar esta compra?')) return;
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/${subId}`, {
              method: 'DELETE',
              headers: headersJson
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              aplicarShapeSubcargos(data);
            } else {
              alert('Error al eliminar la compra: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al eliminar la compra: ' + e.message);
          }
        };

        // ===== Vinculación de factura de Drive a un subcargo =====
        const abrirSelectorFacturas = async (subId) => {
          setFacturasSugeridas({ subId, cargando: true, lista: [] });
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/${subId}/facturas-sugeridas`, { headers: headersJson });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
              setFacturasSugeridas({ subId, cargando: false, lista: data.sugeridas || [] });
            } else {
              setFacturasSugeridas({ subId, cargando: false, lista: [] });
            }
          } catch (e) {
            setFacturasSugeridas({ subId, cargando: false, lista: [] });
          }
        };

        const vincularFactura = async (subId, facturaDriveId) => {
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/${subId}/vincular-factura`, {
              method: 'POST',
              headers: headersJson,
              body: JSON.stringify({ factura_drive_id: facturaDriveId })
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
              actualizarSubcargoEnModal(data.subcargo);
              setFacturasSugeridas(null);
            } else {
              alert('Error al enlazar la factura: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al enlazar la factura: ' + e.message);
          }
        };

        const desvincularFactura = async (subId) => {
          try {
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/${subId}/factura`, {
              method: 'DELETE',
              headers: headersJson
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok && data.success) {
              actualizarSubcargoEnModal(data.subcargo);
            } else {
              alert('Error al desenlazar la factura: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al desenlazar la factura: ' + e.message);
          }
        };

        const añadirSubcargo = async () => {
          if (!nuevoSubcargo.concepto.trim() || nuevoSubcargo.importe === '') {
            alert('El concepto y el importe son obligatorios');
            return;
          }
          try {
            const body = {
              fecha_compra: nuevoSubcargo.fecha_compra || undefined,
              concepto: nuevoSubcargo.concepto.trim(),
              proveedor: nuevoSubcargo.proveedor || undefined,
              categoria: nuevoSubcargo.categoria || undefined,
              importe: parseFloat(nuevoSubcargo.importe) || 0,
              notas: nuevoSubcargo.notas || undefined,
            };
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos`, {
              method: 'POST',
              headers: headersJson,
              body: JSON.stringify(body)
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              aplicarShapeSubcargos(data);
              setNuevoSubcargo({ fecha_compra: '', concepto: '', proveedor: '', categoria: '', importe: '', notas: '' });
            } else {
              alert('Error al añadir la compra: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al añadir la compra: ' + e.message);
          }
        };

        const seleccionarExtracto = async (file) => {
          if (!file) return;
          extractoFileRef.current = file;
          try {
            const fd = new FormData();
            fd.append('archivo', file);
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/importar-extracto?dry_run=true&reemplazar=true`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setDesgloseTarjeta(prev => prev ? { ...prev, preview: data } : prev);
            } else {
              alert('Error al leer el extracto: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al leer el extracto: ' + e.message);
          }
        };

        const confirmarImportacion = async () => {
          const file = extractoFileRef.current;
          if (!file) return;
          try {
            const fd = new FormData();
            fd.append('archivo', file);
            const res = await fetch(`${API_URL}/api/finanzas/cargos-bancarios/${c.id}/subcargos/importar-extracto?dry_run=false&reemplazar=true`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setDesgloseTarjeta(prev => prev ? { ...prev, preview: null } : prev);
              extractoFileRef.current = null;
              aplicarShapeSubcargos(data);
            } else {
              alert('Error al importar el extracto: ' + (data.detail || 'Error desconocido'));
            }
          } catch (e) {
            alert('Error al importar el extracto: ' + e.message);
          }
        };

        return (
          <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4" onClick={cerrar}>
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cabecera */}
              <div className="flex items-start justify-between p-4 border-b">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-slate-600" /> Desglose de tarjeta
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {limpiarConcepto(c.concepto).proveedor || c.concepto} · {formatFecha(c.fecha)} · {formatNumber(c.importe)}
                  </p>
                </div>
                <button onClick={cerrar} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Cerrar">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Cuerpo */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {!c.es_consolidado_tarjeta ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800 mb-3">
                      Este cargo aún no está marcado como liquidación de tarjeta. Márcalo para desglosar las compras del mes.
                    </p>
                    <Button className="bg-slate-700 hover:bg-slate-800 text-white" onClick={marcarTarjeta}>
                      <CreditCard className="w-4 h-4 mr-1" /> Marcar como liquidación de tarjeta
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Barra de cuadre (sticky) */}
                    <div className={`sticky top-0 z-10 rounded-lg p-3 text-sm border ${d.cuadrado ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span>Cargo del banco: <strong>{formatNumber(cargoImporte)}</strong></span>
                        <span>·</span>
                        <span>Desglosado: <strong>{formatNumber(sumaEfectiva)}</strong></span>
                        <span>·</span>
                        <span>Diferencia: <strong className={Math.abs(difEfectiva) < 0.01 ? '' : 'text-red-600'}>{formatNumber(difEfectiva)}</strong></span>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1 text-gray-600">
                          <FileText className="w-3.5 h-3.5" />
                          <strong>{conFactura}</strong> de <strong>{totalSubcargos}</strong> compras con factura
                        </span>
                      </div>
                      {d.cuadrado && (
                        <div className="flex items-center gap-1 mt-1 font-medium">
                          <CheckCircle className="w-4 h-4" /> Cuadrado con el cargo del banco
                        </div>
                      )}
                    </div>

                    {/* Tabla de subcargos */}
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-semibold">Fecha</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Concepto</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Proveedor</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Categoría</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Importe</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Factura</th>
                            <th className="px-2 py-1.5 text-left font-semibold w-24">Notas</th>
                            <th className="px-2 py-1.5 w-8"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(d.subcargos || []).map((s) => (
                            <tr key={s.id} className="hover:bg-gray-50">
                              <td className="px-2 py-1">
                                <input
                                  type="date"
                                  className="text-xs border rounded px-1 py-0.5 w-full"
                                  value={(s.fecha_compra || '').slice(0, 10)}
                                  onChange={(e) => editarSubcargoLocal(s.id, 'fecha_compra', e.target.value)}
                                  onBlur={(e) => { if (e.target.value !== (s.fecha_compra || '').slice(0, 10)) guardarCampoSubcargo(s.id, 'fecha_compra', e.target.value); }}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  className="text-xs border rounded px-1 py-0.5 w-full"
                                  value={s.concepto || ''}
                                  onChange={(e) => editarSubcargoLocal(s.id, 'concepto', e.target.value)}
                                  onBlur={(e) => { if (e.target.value !== (s.concepto || '')) guardarCampoSubcargo(s.id, 'concepto', e.target.value); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  className="text-xs border rounded px-1 py-0.5 w-full"
                                  value={s.proveedor || ''}
                                  onChange={(e) => editarSubcargoLocal(s.id, 'proveedor', e.target.value)}
                                  onBlur={(e) => { if (e.target.value !== (s.proveedor || '')) guardarCampoSubcargo(s.id, 'proveedor', e.target.value); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <select
                                  className="text-xs border rounded px-1 py-0.5 w-full"
                                  value={s.categoria || ''}
                                  onChange={(e) => { editarSubcargoLocal(s.id, 'categoria', e.target.value); guardarCampoSubcargo(s.id, 'categoria', e.target.value); }}
                                >
                                  <option value="">—</option>
                                  {CATEGORIAS_GASTO.map((cat) => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  type="number"
                                  step="0.01"
                                  className="text-xs border rounded px-1 py-0.5 w-20 text-right"
                                  value={s.importe ?? ''}
                                  onChange={(e) => editarSubcargoLocal(s.id, 'importe', e.target.value)}
                                  onBlur={(e) => { if (parseFloat(e.target.value || 0) !== (Number(s.importe) || 0)) guardarCampoSubcargo(s.id, 'importe', e.target.value); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                              </td>
                              <td className="px-2 py-1 align-top">
                                {s.factura_drive_id ? (
                                  <div className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-1 text-green-700">
                                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                                      <span className="truncate max-w-[160px]" title={`${s.factura_proveedor || ''} · ${s.factura_numero || ''}`}>
                                        {(s.factura_proveedor || 'Factura')} · {s.factura_numero || ''}
                                      </span>
                                      <button
                                        onClick={() => desvincularFactura(s.id)}
                                        className="text-gray-400 hover:text-red-600 flex-shrink-0"
                                        title="Desenlazar factura"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    {s.importe_iva != null && (
                                      <span className="text-[10px] text-gray-500">IVA {formatNumber(s.importe_iva)}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="relative">
                                    <button
                                      onClick={() => abrirSelectorFacturas(s.id)}
                                      className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900 border rounded px-1.5 py-0.5 whitespace-nowrap"
                                      title="Enlazar factura de Drive"
                                    >
                                      <Link2 className="w-3 h-3" /> Enlazar factura
                                    </button>
                                    {facturasSugeridas && facturasSugeridas.subId === s.id && (
                                      <div className="absolute left-0 top-full mt-1 z-20 w-72 bg-white border rounded-lg shadow-lg p-2">
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-[11px] font-semibold text-gray-600">Facturas de Drive</span>
                                          <button
                                            onClick={() => setFacturasSugeridas(null)}
                                            className="text-gray-400 hover:text-gray-600"
                                            title="Cerrar"
                                          >
                                            <X className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                        {facturasSugeridas.cargando ? (
                                          <p className="text-[11px] text-gray-400 py-2 text-center">Buscando facturas…</p>
                                        ) : (facturasSugeridas.lista || []).length === 0 ? (
                                          <p className="text-[11px] text-gray-400 py-2">
                                            No hay facturas de Drive sin enlazar que cuadren con este importe
                                          </p>
                                        ) : (
                                          <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                                            {facturasSugeridas.lista.map((f) => (
                                              <button
                                                key={f.id}
                                                onClick={() => vincularFactura(s.id, f.id)}
                                                className="w-full text-left px-1.5 py-1 hover:bg-gray-50 flex items-start justify-between gap-2"
                                              >
                                                <span className="text-[11px] leading-tight">
                                                  {(f.proveedor_nombre || 'Sin proveedor')} · {f.numero_factura || 's/n'} · {formatNumber(f.importe_total)} · {formatFecha(f.fecha_factura)}
                                                </span>
                                                {f.cuadre_exacto ? (
                                                  <span className="text-[9px] font-semibold text-green-700 bg-green-100 rounded px-1 py-0.5 flex-shrink-0">cuadra</span>
                                                ) : (
                                                  <span className="text-[9px] font-semibold text-gray-500 bg-gray-100 rounded px-1 py-0.5 flex-shrink-0">{f.dias}d</span>
                                                )}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-2 py-1">
                                <input
                                  className="text-xs border rounded px-1 py-0.5 w-full"
                                  value={s.notas || ''}
                                  onChange={(e) => editarSubcargoLocal(s.id, 'notas', e.target.value)}
                                  onBlur={(e) => { if (e.target.value !== (s.notas || '')) guardarCampoSubcargo(s.id, 'notas', e.target.value); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                              </td>
                              <td className="px-2 py-1 text-center">
                                <button onClick={() => eliminarSubcargo(s.id)} className="text-red-500 hover:text-red-700" title="Eliminar compra">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {/* Fila añadir */}
                          <tr className="bg-gray-50">
                            <td className="px-2 py-1">
                              <input
                                type="date"
                                className="text-xs border rounded px-1 py-0.5 w-full"
                                value={nuevoSubcargo.fecha_compra}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, fecha_compra: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                className="text-xs border rounded px-1 py-0.5 w-full"
                                placeholder="Concepto"
                                value={nuevoSubcargo.concepto}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, concepto: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                className="text-xs border rounded px-1 py-0.5 w-full"
                                placeholder="Proveedor"
                                value={nuevoSubcargo.proveedor}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, proveedor: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="text-xs border rounded px-1 py-0.5 w-full"
                                value={nuevoSubcargo.categoria}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, categoria: e.target.value })}
                              >
                                <option value="">—</option>
                                {CATEGORIAS_GASTO.map((cat) => (
                                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                step="0.01"
                                className="text-xs border rounded px-1 py-0.5 w-20 text-right"
                                placeholder="0,00"
                                value={nuevoSubcargo.importe}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, importe: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1">
                              <input
                                className="text-xs border rounded px-1 py-0.5 w-full"
                                placeholder="Notas"
                                value={nuevoSubcargo.notas}
                                onChange={(e) => setNuevoSubcargo({ ...nuevoSubcargo, notas: e.target.value })}
                              />
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button onClick={añadirSubcargo} className="text-green-600 hover:text-green-800 text-xs font-medium whitespace-nowrap" title="Añadir compra">
                                + Añadir
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Importar extracto */}
                    <div className="border-t pt-4">
                      <input
                        type="file"
                        accept=".pdf,.csv,.txt"
                        ref={extractoInputRef}
                        hidden
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; seleccionarExtracto(f); }}
                      />
                      <Button variant="outline" size="sm" onClick={() => extractoInputRef.current?.click()}>
                        <Upload className="w-4 h-4 mr-1" /> Importar extracto (PDF o CSV)
                      </Button>

                      {d.preview && (
                        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                          <p className="text-sm text-slate-800">
                            {d.preview.total_filas} movimientos · suman <strong>{formatNumber(d.preview.suma)}</strong> · diferencia con el cargo: <strong className={Math.abs(d.preview.diferencia_vs_cargo || 0) < 0.01 ? '' : 'text-red-600'}>{formatNumber(d.preview.diferencia_vs_cargo)}</strong>
                          </p>
                          {Array.isArray(d.preview.avisos) && d.preview.avisos.length > 0 && (
                            <ul className="text-xs text-amber-700 list-disc pl-4">
                              {d.preview.avisos.map((a, i) => <li key={i}>{a}</li>)}
                            </ul>
                          )}
                          {(d.preview.reemplazara > 0) && (
                            <p className="text-xs text-amber-700">
                              Se reemplazarán {d.preview.reemplazara} compras importadas anteriormente (las añadidas a mano se conservan).
                            </p>
                          )}
                          <div className="max-h-48 overflow-y-auto border rounded bg-white">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1 text-left font-semibold">Fecha</th>
                                  <th className="px-2 py-1 text-left font-semibold">Concepto</th>
                                  <th className="px-2 py-1 text-right font-semibold">Importe</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {(d.preview.filas || []).map((f, i) => (
                                  <tr key={i}>
                                    <td className="px-2 py-1 whitespace-nowrap">{formatFecha(f.fecha_compra || f.fecha)}</td>
                                    <td className="px-2 py-1">{f.concepto}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(f.importe)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={confirmarImportacion}>
                              <Check className="w-4 h-4 mr-1" /> Confirmar importación
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setDesgloseTarjeta(prev => prev ? { ...prev, preview: null } : prev); extractoFileRef.current = null; }}>
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Pie */}
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-xs text-gray-400">
                  Al cuadrar el desglose con el importe del cargo, el cargo deja de estar pendiente.
                </p>
                <Button variant="outline" onClick={cerrar}>Cerrar</Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Modal de exceso de pago para Finanzas
function ExcesoModalFinanzas({ exceso, onClose, onDone }) {
  const [mode, setMode] = useState(null);
  const [buscarCargo, setBuscarCargo] = useState('');
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [notasDeposito, setNotasDeposito] = useState('');
  const tkn = getAuthToken();
  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

  const formatCur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

  const fetchCargos = async (q) => {
    setLoadingCargos(true);
    try {
      const params = q ? `?buscar=${encodeURIComponent(q)}` : '';
      const res = await fetch(`${apiUrl}/api/cobros-unificado/cargos-disponibles${params}`, {
        headers: { 'Authorization': `Bearer ${tkn}` }
      });
      if (res.ok) setCargos(await res.json());
    } catch (e) { console.error(e); }
    setLoadingCargos(false);
  };

  useEffect(() => {
    if (mode === 'devolucion') fetchCargos('');
  }, [mode]);

  const handleDevolucion = async (cargoId, importeCargo) => {
    setProcessing(true);
    try {
      const importeDev = Math.min(exceso.importe, importeCargo);
      const res = await fetch(`${apiUrl}/api/cobros-unificado/vincular-devolucion-exceso`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tkn}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ presupuesto_id: exceso.presupuesto_id, cargo_id: cargoId, importe: importeDev })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      onDone();
    } catch (e) { alert(e.message); }
    setProcessing(false);
  };

  const handleDeposito = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${apiUrl}/api/cobros-unificado/marcar-deposito`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${tkn}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ presupuesto_id: exceso.presupuesto_id, importe: exceso.importe, notas: notasDeposito || undefined })
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error');
      onDone();
    } catch (e) { alert(e.message); }
    setProcessing(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="exceso-modal-finanzas">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-base">Exceso de Pago Detectado</h3>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="exceso-finanzas-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-slate-700">
              Presupuesto <span className="font-bold">#{exceso.presupuesto_numero}</span>
              {exceso.cliente_nombre && <span> ({exceso.cliente_nombre})</span>}: exceso de{' '}
              <span className="font-bold text-amber-700">{formatCur(exceso.importe)}</span>
            </p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!mode && (
            <div className="space-y-3" data-testid="exceso-finanzas-options">
              <p className="text-sm text-slate-600 mb-4">¿Qué deseas hacer con el exceso?</p>
              <button data-testid="exceso-fin-devolucion" onClick={() => setMode('devolucion')}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors text-left">
                <div className="p-2.5 bg-blue-100 rounded-lg"><RotateCcw className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Registrar Devolución</p>
                  <p className="text-xs text-slate-500 mt-0.5">Vincular a un cargo bancario de devolución</p>
                </div>
              </button>
              <button data-testid="exceso-fin-deposito" onClick={() => setMode('deposito')}
                className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-left">
                <div className="p-2.5 bg-emerald-100 rounded-lg"><CreditCard className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">Guardar como Depósito</p>
                  <p className="text-xs text-slate-500 mt-0.5">A cuenta del cliente para futuros presupuestos</p>
                </div>
              </button>
              <button data-testid="exceso-fin-ignorar" onClick={onClose}
                className="w-full text-center text-xs text-slate-400 hover:text-slate-600 py-2">
                Gestionar más tarde
              </button>
            </div>
          )}
          {mode === 'devolucion' && (
            <div data-testid="exceso-fin-devolucion-view">
              <div className="flex items-center justify-between mb-3">
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600 text-sm flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Volver
                </button>
                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">{formatCur(exceso.importe)}</span>
              </div>
              <div className="relative mb-3">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <input type="text" placeholder="Buscar cargo bancario..." value={buscarCargo}
                  onChange={(e) => setBuscarCargo(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchCargos(buscarCargo)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="exceso-fin-buscar-cargo" />
              </div>
              <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                {loadingCargos ? <p className="text-center text-sm text-slate-400 py-4">Buscando...</p> :
                 cargos.length === 0 ? <p className="text-center text-sm text-slate-400 py-4">No hay cargos disponibles</p> :
                 cargos.map(c => (
                  <div key={c.id} className="flex items-center justify-between bg-slate-50 hover:bg-blue-50 rounded-lg px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-700 truncate">{c.concepto}</p>
                      <p className="text-xs text-slate-400">{c.fecha}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <span className="text-sm font-bold text-red-600">{formatCur(c.importe)}</span>
                      <button onClick={() => handleDevolucion(c.id, c.importe)} disabled={processing}
                        className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium disabled:opacity-50">
                        {processing ? '...' : 'Vincular'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {mode === 'deposito' && (
            <div data-testid="exceso-fin-deposito-view">
              <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600 text-sm flex items-center gap-1 mb-4">
                <RotateCcw className="w-3 h-3" /> Volver
              </button>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-700">
                  Depósito de <span className="font-bold text-emerald-700">{formatCur(exceso.importe)}</span> a cuenta de{' '}
                  <span className="font-bold">{exceso.cliente_nombre}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">Se podrá aplicar a futuros presupuestos.</p>
              </div>
              <textarea data-testid="exceso-fin-deposito-notas" value={notasDeposito} onChange={(e) => setNotasDeposito(e.target.value)}
                placeholder="Notas (opcional)..." className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none h-20 mb-4" />
              <button data-testid="exceso-fin-confirmar-deposito" onClick={handleDeposito} disabled={processing}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
                {processing ? 'Registrando...' : `Registrar depósito de ${formatCur(exceso.importe)}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Finanzas;
